import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { SIGNED_OUT_EVENT } from '../stores/signedOutEvent';
import { trackProductLoad, trackProductRequest } from './productActionRequest';
import { trackRequests } from './requestActivity';
import type {
  ProbationDecisionInput,
  ProbationDecisionResult,
  ProbationListResponse,
  ProbationReviewBundle,
  ProbationView,
} from '../types/probation';
import type { PlanBaseCityChange, PlanReportPeriod, PlanRules } from '../types/preJourneyPlan';

// API Base URL - reads from root .env file via vite.config.ts
// The value is set via API_BASE_URL in the root .env file (mapped to VITE_API_URL)
// For local development, uses relative /api path which is proxied to backend

const RAW_API_URL = (import.meta as any).env?.VITE_API_URL as string | undefined;

const normalizeBaseUrl = (url: string) => url.replace(/\/+$/, '');

const isLocalhostOrigin = (origin: string) =>
  origin.includes('localhost') || origin.includes('127.0.0.1');

const getApiUrl = () => {
  if (typeof window === 'undefined') return '/api';

  const origin = normalizeBaseUrl(window.location.origin);

  // In development (localhost), ALWAYS use relative /api path (proxied by vite)
  // The Vite proxy will forward requests to the target specified in vite.config.ts
  if (isLocalhostOrigin(origin)) {
    return '/api';
  }

  // In production, use VITE_API_URL from env or default to origin/api
  if (RAW_API_URL) {
    const normalizedApiUrl = normalizeBaseUrl(RAW_API_URL);
    if (normalizedApiUrl.startsWith('http')) {
      return `${normalizedApiUrl}/api`;
    }
    return normalizedApiUrl;
  }

  return `${origin}/api`;
};

const API_URL = getApiUrl();

// Debug: Log the API URL being used
console.log('[API Config] RAW_API_URL:', RAW_API_URL);
console.log('[API Config] API_URL:', API_URL);

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Resolve a leave supporting-document storage key (e.g. "leave/123_..._report.pdf")
// into a viewable absolute URL via the backend's /oneoffice/profile-photo proxy — a
// generic sendAWSFileLink streamer that attaches X-Client-Token server-side and is
// NOT bearer-gated, so it works as a plain <a href>/new tab. Mirrors the mobile
// helper: split the key at the LAST '/' into path (incl. trailing slash) + filename.
export const buildLeaveDocumentUrl = (documentUrl?: string | null): string | undefined => {
  if (!documentUrl) return undefined;
  const key = String(documentUrl).trim();
  if (!key) return undefined;
  if (/^https?:\/\//i.test(key)) return key; // already a full URL
  const slash = key.lastIndexOf('/');
  const path = slash >= 0 ? key.slice(0, slash + 1) : '';
  const filename = slash >= 0 ? key.slice(slash + 1) : key;
  if (!filename) return undefined;
  const base = API_URL.replace(/\/+$/, '');
  return `${base}/oneoffice/profile-photo?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(filename)}`;
};

// Request interceptor to add auth token and user info
api.interceptors.request.use(
  (config) => {
    const { token, user } = useAuthStore.getState();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Send userId and userCode in headers for backend to use
    if (user?.userId) {
      config.headers['X-User-Id'] = user.userId.toString();
    }
    if (user?.userCode) {
      config.headers['X-User-Code'] = user.userCode.toString();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      // Don't trigger session expired for auth endpoints (login, forgot password, etc.)
      const isAuthEndpoint = requestUrl.includes('/auth/');
      // Nor for an ENTITLEMENT PROBE — a request whose whole purpose is to read a
      // refusal. Opt-in per request, never a default: the caller sets it only when
      // it treats every non-2xx as a plain "no". Without it, a probe of a route the
      // viewer was never granted can end their session over a question they only
      // asked on the page's behalf.
      const isProbe = Boolean((error.config as any)?.skipSessionExpiry);

      if (!isAuthEndpoint && !isProbe) {
        const { isMockAuth, token } = useAuthStore.getState();
        // Only show session expired if user was previously logged in
        if (!isMockAuth && token) {
          useAuthStore.getState().setSessionExpired(true);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Signing out anywhere on the website (the menu, the Session Expired dialog, an
// access-denied page) goes through clearAuth; ask the server to drop the sign-in
// pass cookie too, which page scripts cannot reach themselves.
if (typeof window !== 'undefined') {
  window.addEventListener(SIGNED_OUT_EVENT, () => {
    api.post('/auth/logout').catch(() => { /* the pass still runs out on its own */ });
  });
}

// Counts the requests in flight, for the line under the top bar. It only watches: every
// config, response and error goes on exactly as it came (see requestActivity.ts).
trackRequests(api);

export default api;

export const authAPI = {
  // Login using backend proxy to external authentication API
  // webPass: also hand this browser the website's own sign-in pass, an HttpOnly
  // cookie the server checks instead of trusting X-User-Id / X-User-Code.
  login: (email: string, password: string) => {
    return api.post('/auth/external-login', { username: email, password, webPass: true });
  },
  
  // Forgot password - send OTP to email (via backend proxy)
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  // Verify OTP (via backend proxy)
  verifyOTP: (email: string, otp: string) =>
    api.post('/auth/verify-otp', { email, otp }),
  
  // Update password after OTP verification (via backend proxy). The OTP goes with
  // it: the server checks it again here and refuses a new password sent without it.
  updatePassword: (email: string, otp: string, newPassword: string) =>
    api.post('/auth/update-password', { email, otp, password: newPassword }),
  
  // Legacy local auth methods
  register: (email: string, password: string, confirmPassword: string) =>
    api.post('/auth/register', { email, password, confirmPassword }),
  logout: () => api.post('/auth/logout'),
};


// Employees API
export const employeesAPI = {
  getAll: (active?: boolean) => api.get('/employees', { params: { active } }),
  getDeployed: (active?: boolean) => api.get('/employees/deployed', { params: { active } }),
  getUndeployed: (active?: boolean) => api.get('/employees/undeployed', { params: { active } }),
  getAllFromMaster: (active?: boolean, projectId?: number) => api.get('/employees/master', { params: { active, projectId } }),
  getForRoleAssignment: (active?: boolean, projectId?: number) => api.get('/employees/for-role-assignment', { params: { active, projectId } }),
  getDeployedCodes: () => api.get('/employees/deployed-codes'),
  getProjectEmployees: (active?: boolean, projectId?: number, includeInactive?: boolean) => api.get('/employees/project-employees', { params: { active, projectId, ...(includeInactive ? { includeInactive: 'true' } : {}) } }),
  // Employees 1Office has marked inactive whose exit date falls in the last `months`
  // months (server default 3) — the Employees page's Inactive tab.
  getRecentlyInactiveEmployees: (projectId: number, months?: number) =>
    api.get('/employees/project-employees/inactive', { params: { projectId, ...(months ? { months } : {}) } }),
  getSeedScores: (empCodes: string, projectId?: number | null) => api.get(`/employees/seed-scores`, { params: { empCodes, ...(projectId ? { projectId } : {}) } }),
  updateSeedScore: (empCode: string, seedScore: number | null, projectId?: number | null) =>
    api.put(`/employees/${empCode}/seed-score`, { seedScore, ...(projectId ? { projectId } : {}) }),
  sync: (employees: any[]) => api.post('/employees/sync', { employees }),
  getReportees: (empCode: string, projectId: number) =>
    api.get(`/employees/${empCode}/reportees`, { params: { projectId } }),
  getManager: (empCode: string, projectId: number) =>
    api.get(`/employees/${empCode}/manager`, { params: { projectId } }),
  updateReportees: (empCode: string, data: { reporteeCodes: string[]; projectId: number }) =>
    api.put(`/employees/${empCode}/reportees`, data),
  updateManager: (empCode: string, data: { managerCodes: string[]; projectId: number }) =>
    api.put(`/employees/${empCode}/manager`, data),
  getFullDetails: (userId: number | string) =>
    api.get(`/employees/${userId}/full-details`),
  documentViewUrl: (path: string, filename: string) =>
    `${API_URL}/employees/documents/view?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(filename)}`,
};

// Stores API
export const storesAPI = {
  getAll: (projectId: number) => api.get('/stores', { params: { projectId } }),
  getById: (id: string) => api.get(`/stores/${id}`),
  getInsights: (id: string) => api.get(`/stores/${id}/insights`),
  getTimeline: (id: string) => api.get(`/stores/${id}/timeline`),
  create: (data: any) => api.post('/stores', data),
  update: (id: string, data: any) => api.put(`/stores/${id}`, data),
  delete: (id: string) => api.delete(`/stores/${id}`),
  deploy: (data: any) => api.post('/stores/deploy', data),
  // The employee code is required here on purpose: the endpoint reads a request without
  // one as "everyone at this store".
  undeploy: (storeId: string, employeeCode: string) => api.post('/stores/undeploy', { storeId, employeeCode }),
  getEmployeesByStore: (storeId: string) => api.get(`/stores/${storeId}/employees`),
  getByEmployee: (empCode: string) => api.get(`/stores/employee/${empCode}`),
  // Multi-store deployment (roaming promoters): every store an employee is on + whether
  // their role allows several, and ADD a store while keeping the existing ones. Removing
  // one store is `undeploy(storeId, employeeCode)` above.
  getAssignments: (empCode: string, projectId?: number) =>
    api.get(`/stores/employee/${encodeURIComponent(empCode)}/assignments`, { params: { projectId } }),
  addEmployeeStore: (
    empCode: string,
    data: { storeId: string; employee_name?: string; designation?: string; date_of_joining?: string; salary?: number }
  ) => api.post(`/stores/employee/${encodeURIComponent(empCode)}/stores`, data),
  // Swap modal APIs
  getDeployedEmployees: (projectId: number) => api.get('/stores/deployed-employees', { params: { projectId } }),
  // `includeMultiStore` additionally returns roaming promoters who are already deployed
  // elsewhere, so a second store's picker can offer them. Omitted by the dashboard's
  // Undeployed Employees card, which must keep treating them as deployed.
  getUndeployedEmployees: (projectId: number, includeMultiStore?: boolean) =>
    api.get('/stores/undeployed-employees', {
      params: { projectId, ...(includeMultiStore ? { includeMultiStore: true } : {}) },
    }),
  getAvailableStores: (params?: { excludeStoreCode?: string; trade?: string; category?: string; projectId?: number }) => 
    api.get('/stores/available-stores', { params }),
  swap: (data: {
    currentStoreId: string;
    currentEmployeeCode?: string;
    newEmployeeCode: string;
    newEmployeeName?: string;
    newEmployeeDateOfJoining?: string;
    newEmployeeSalary?: number;
    newEmployeeDesignation?: string;
    targetStoreId?: string;
  }) => api.post('/stores/swap', data),
  // Bulk upload APIs
  // projectId is required: the template appends a column per active custom field,
  // and those are defined per project.
  downloadBulkUploadTemplate: (projectId: number) =>
    api.get('/stores/bulk-upload/template', { params: { projectId }, responseType: 'blob' }),
  bulkUpload: (data: string, projectId: number) => api.post('/stores/bulk-upload', { data, project_id: projectId }),
};

/** Field type strings a store custom field may use — mirrors STORE_FIELD_TYPES on the API. */
export type StoreCustomFieldType =
  | 'text' | 'number' | 'date' | 'boolean' | 'dropdown' | 'multiple_choice';

export interface StoreCustomField {
  id: string;
  projectId: number;
  label: string;
  fieldType: StoreCustomFieldType;
  options: string[] | null;
  isRequired: boolean;
  displayOrder: number;
  active: boolean;
}

// Master Data → Stores → Custom Fields. Project-scoped user-defined store
// attributes; the definitions drive the store form, the bulk template and the CSV.
export const storeCustomFieldsAPI = {
  list: (projectId: number, includeInactive = false) =>
    api.get(`/store-custom-fields/${projectId}/fields`, { params: { includeInactive } }),
  create: (projectId: number, data: Partial<StoreCustomField>) =>
    api.post(`/store-custom-fields/${projectId}/fields`, data),
  update: (projectId: number, fieldId: string, data: Partial<StoreCustomField> & { confirmOverwrite?: boolean }) =>
    api.put(`/store-custom-fields/${projectId}/fields/${fieldId}`, data),
  setActive: (projectId: number, fieldId: string, active: boolean) =>
    api.put(`/store-custom-fields/${projectId}/fields/${fieldId}/active`, { active }),
  reorder: (projectId: number, fieldIds: string[]) =>
    api.post(`/store-custom-fields/${projectId}/fields/reorder`, { fieldIds }),
};

/** Field type strings an employee custom field may use — mirrors EMPLOYEE_FIELD_TYPES on the API. */
export type EmployeeCustomFieldType =
  | 'text' | 'number' | 'date' | 'boolean' | 'dropdown' | 'multiple_choice';

export interface EmployeeCustomField {
  id: string;
  projectId: number;
  label: string;
  fieldType: EmployeeCustomFieldType;
  options: string[] | null;
  isRequired: boolean;
  showOnMobileHome: boolean;
  displayOrder: number;
  active: boolean;
}

// Master Data → Employees → Custom Fields. Project-scoped user-defined employee
// attributes; the definitions drive the employee edit modal, the bulk template and
// (when flagged) the mobile home screen. Values are keyed by employee code.
export const employeeCustomFieldsAPI = {
  list: (projectId: number, includeInactive = false) =>
    api.get(`/employee-custom-fields/${projectId}/fields`, { params: { includeInactive } }),
  create: (projectId: number, data: Partial<EmployeeCustomField>) =>
    api.post(`/employee-custom-fields/${projectId}/fields`, data),
  update: (projectId: number, fieldId: string, data: Partial<EmployeeCustomField> & { confirmOverwrite?: boolean }) =>
    api.put(`/employee-custom-fields/${projectId}/fields/${fieldId}`, data),
  setActive: (projectId: number, fieldId: string, active: boolean) =>
    api.put(`/employee-custom-fields/${projectId}/fields/${fieldId}/active`, { active }),
  reorder: (projectId: number, fieldIds: string[]) =>
    api.post(`/employee-custom-fields/${projectId}/fields/reorder`, { fieldIds }),
  // Per-employee values for the edit modal.
  getValues: (projectId: number, employeeCode: string) =>
    api.get(`/employee-custom-fields/${projectId}/values/${encodeURIComponent(employeeCode)}`),
  saveValues: (projectId: number, employeeCode: string, values: Record<string, unknown>) =>
    api.put(`/employee-custom-fields/${projectId}/values/${encodeURIComponent(employeeCode)}`, { values }),
  // Bulk upload of values (base64 XLSX in a JSON body, parsed server-side).
  downloadBulkTemplate: (projectId: number) =>
    api.get(`/employee-custom-fields/${projectId}/bulk-upload/template`, { responseType: 'blob' }),
  bulkUpload: (projectId: number, data: string) =>
    api.post(`/employee-custom-fields/${projectId}/bulk-upload`, { data }),
};

export interface StoreTrade {
  id: string;
  projectId: number;
  name: string;
  displayOrder: number;
  active: boolean;
  /** Stores currently carrying this trade — present on list only. */
  storeCount?: number;
}

// Master Data → Stores → Trades. Governs the trade dropdown, which was previously
// derived from whatever trades happened to already exist in the store data.
export const storeTradesAPI = {
  list: (projectId: number, includeInactive = false) =>
    api.get(`/store-trades/${projectId}/trades`, { params: { includeInactive } }),
  create: (projectId: number, name: string) =>
    api.post(`/store-trades/${projectId}/trades`, { name }),
  update: (projectId: number, tradeId: string, name: string) =>
    api.put(`/store-trades/${projectId}/trades/${tradeId}`, { name }),
  setActive: (projectId: number, tradeId: string, active: boolean) =>
    api.put(`/store-trades/${projectId}/trades/${tradeId}/active`, { active }),
  reorder: (projectId: number, tradeIds: string[]) =>
    api.post(`/store-trades/${projectId}/trades/reorder`, { tradeIds }),
};

// ─── Manager Store Access ────────────────────────────────────────────────────
// Rules that give a manager stores beyond their reporting hierarchy, on Master Data →
// Stores → Manager Access. Every call is gated server-side by the per-role
// `web.manager_store_access` capability (a 403 means the role lacks it).

/** A rule's conditions: every condition present must match; any value inside one may. */
export interface StoreAccessCriteria {
  tradeIds?: string[];
  categoryIds?: number[];
  regions?: string[];
  tiers?: string[];
  states?: string[];
  cities?: string[];
  customFields?: { fieldId: string; values: string[] }[];
  /** Exact stores — a rule with these takes no other condition. */
  storeIds?: string[];
}

export interface ManagerStoreAccessRule {
  id: string;
  projectId: number;
  employeeCode: string;
  employeeName: string | null;
  roleName: string | null;
  label: string | null;
  criteria: StoreAccessCriteria;
  active: boolean;
  /** Stores the rule adds right now (Active, with someone deployed); null if unknown. */
  storeCount: number | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StoreAccessOptions {
  managers: { code: string; name: string | null; roleName: string; category: string }[];
  trades: { id: string; name: string; active: boolean }[];
  categories: { id: number; name: string }[];
  regions: string[];
  tiers: string[];
  states: string[];
  cities: string[];
  customFields: { id: string; label: string; options: string[] }[];
}

export interface StoreAccessStoreOption {
  id: string;
  name: string;
  code: string | null;
  city: string | null;
  state: string | null;
  status: string | null;
}

export interface StoreAccessPreview {
  /** Every store the conditions match, whatever its status or staffing. */
  matchedStores: number;
  /** The matched stores that will show in the manager's store lists. */
  visibleStores: number;
  stores: (StoreAccessStoreOption & { willShow: boolean })[];
}

export interface ManagerStoreAccessRuleInput {
  employeeCode: string;
  label: string;
  criteria: StoreAccessCriteria;
}

export const managerStoreAccessAPI = {
  /**
   * Succeeds only for someone allowed to manage rules — the ENTITLEMENT PROBE the Stores
   * page runs on every visit. `skipSessionExpiry` for the same reason as
   * storeRequestsAPI.summary: a refusal only means "no tab" and must never end the session.
   */
  access: (projectId: number) =>
    api.get(`/manager-store-access/${projectId}/access`, { skipSessionExpiry: true } as any),
  listRules: (projectId: number) => api.get(`/manager-store-access/${projectId}/rules`),
  options: (projectId: number) => api.get(`/manager-store-access/${projectId}/options`),
  storeOptions: (projectId: number) => api.get(`/manager-store-access/${projectId}/store-options`),
  preview: (projectId: number, criteria: StoreAccessCriteria) =>
    api.post(`/manager-store-access/${projectId}/preview`, { criteria }),
  create: (projectId: number, data: ManagerStoreAccessRuleInput) =>
    api.post(`/manager-store-access/${projectId}/rules`, data),
  update: (projectId: number, ruleId: string, data: ManagerStoreAccessRuleInput) =>
    api.put(`/manager-store-access/${projectId}/rules/${ruleId}`, data),
  setActive: (projectId: number, ruleId: string, active: boolean) =>
    api.put(`/manager-store-access/${projectId}/rules/${ruleId}/active`, { active }),
};

// ─── Store Requests ──────────────────────────────────────────────────────────
// Stores submitted from the field on the mobile app, reviewed by the program team on
// Master Data → Stores → Store Requests. Every call is gated server-side by the
// per-role `web.store_requests` capability (a 403 means the role lacks it).

export type StoreRequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';
/** What approval did (or will do) to the requester's own store mapping. */
export type StoreRequestMapping = 'added' | 'moved' | 'skipped';

export interface StoreRequestDuplicate {
  kind: 'store' | 'request';
  id: string;
  name: string;
  code: string | null;
  /** Metres from the request; null when only the store code matched. */
  distanceM: number | null;
  sameCode: boolean;
  detail: string | null;
}

export interface StoreRequestReviewContext {
  duplicates: StoreRequestDuplicate[];
  /** Stores the requester is mapped to right now, in any project. */
  requesterStores: { id: string; name: string; code: string | null; otherProject: boolean }[];
  requesterMultiStore: boolean;
  predictedMapping: StoreRequestMapping;
}

export interface StoreRequest {
  id: number;
  projectId: number;
  status: StoreRequestStatus;
  requesterCode: string;
  requesterName: string | null;
  requesterDesignation: string | null;
  identityVerified: boolean;
  storeName: string;
  storeCode: string | null;
  trade: string | null;
  storeCategory: number | null;
  storeCategoryName: string | null;
  address: string;
  state: string;
  city: string;
  pinCode: string;
  latitude: number;
  longitude: number;
  gpsAccuracy: number | null;
  locationMocked: boolean | null;
  /** 'HH:MM' */
  shiftStartTime: string | null;
  /** 0 = Sunday … 6 = Saturday */
  storeWeekOff: number | null;
  customFields: Record<string, unknown>;
  remark: string | null;
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdStoreId: string | null;
  createdStoreName: string | null;
  requesterMapping: StoreRequestMapping | null;
  createdAt: string;
  updatedAt: string;
  /** Present on pending requests only. */
  review?: StoreRequestReviewContext;
}

/** The store exactly as the approver confirms it. */
export interface StoreRequestApproval {
  storeName: string;
  storeCode: string;
  trade: string | null;
  storeCategory: number | null;
  address: string;
  state: string;
  city: string;
  pinCode: string;
  latitude: number;
  longitude: number;
  shiftStartTime: string | null;
  storeWeekOff: number | null;
  customFields: Record<string, unknown>;
  storeStatus: string;
  attendanceRadius: number;
  compliancePolicy: string | null;
  mapRequester: boolean;
  note: string | null;
}

/**
 * What an approval answers: the request, the store it created, and that store's Status and
 * Staffing Status as saved (mapping the requester can make it Deployed). Both are null when
 * the server could not read them back.
 */
export interface StoreRequestApproved {
  request: StoreRequest;
  storeId: string;
  mapping: 'added' | 'moved' | 'skipped';
  storeStatus: string | null;
  staffingStatus: string | null;
}

export const storeRequestsAPI = {
  /**
   * Pending count — and the ENTITLEMENT PROBE the Stores page runs on every visit to
   * decide whether to show the tab. `skipSessionExpiry`: every refusal here is a plain
   * "no tab", so a 401 from this call must never trip the interceptor's session-expired
   * flow and end the session of someone who only opened the Stores page.
   */
  summary: (projectId: number) =>
    api.get(`/store-requests/review/${projectId}/summary`, { skipSessionExpiry: true } as any),
  list: (projectId: number, status: StoreRequestStatus | 'all' = 'pending') =>
    api.get(`/store-requests/review/${projectId}`, { params: { status } }),
  approve: (projectId: number, requestId: number, body: StoreRequestApproval) =>
    trackProductRequest('approval_decide', { approval_type: 'store', decision: 'approve', entry_point: 'approval_queue' },
      () => api.post(`/store-requests/review/${projectId}/${requestId}/approve`, body)),
  reject: (projectId: number, requestId: number, reason: string) =>
    trackProductRequest('approval_decide', { approval_type: 'store', decision: 'reject', entry_point: 'approval_queue' },
      () => api.post(`/store-requests/review/${projectId}/${requestId}/reject`, { reason })),
};

// ─── Product Master ──────────────────────────────────────────────────────────
// Per-project product catalogue that task questions read from, instead of each
// task re-declaring the whole product list in its own option matrix.
//
// Four levels, and deliberately NOT one tree: `groups` is a filing hierarchy
// (Brand / Category / Sub-Category, any depth), while variation lives on the
// product as independent `attributes` (Size, Colour, Storage) that are
// multiplied out into `skus`. A pure tree would force 8 x 4 x 2 = 64 hand-made
// rows for one Oura ring, and 16 more per new colour.

export interface ProductGroup {
  id: string;
  projectId: number;
  parentId: string | null;
  name: string;
  depth: number;
  path: string;
  displayOrder: number;
  active: boolean;
  productCount?: number;
}

export interface Product {
  id: string;
  projectId: number;
  groupId: string | null;
  groupPath?: string | null;
  name: string;
  code: string | null;
  displayOrder: number;
  active: boolean;
  attributeCount?: number;
  /** Comma-joined characteristic names, e.g. "Chip, RAM, Storage". */
  attributeNames?: string;
  skuCount?: number;
}

export interface ProductAttribute {
  id: string;
  productId: string;
  name: string;
  values: string[];
  displayOrder: number;
}

export interface ProductSku {
  id: string;
  projectId: number;
  productId: string;
  productName?: string;
  skuCode: string | null;
  attributes: Record<string, string>;
  label?: string;
  mrp: number | null;
  uom: string | null;
  barcode: string | null;
  active: boolean;
}

/**
 * Flattened active catalogue. Mirrors the API's ProductCatalog and the mobile
 * resolver's copy — all three must agree, since the builder configures against
 * this shape and the mobile app resolves against it.
 */
export interface ProductCatalog {
  /** Whether this project has opted into running-stock tracking. Gates the
   *  stock control in the task builder — off means it isn't shown at all. */
  stockTrackingEnabled?: boolean;
  /** What this project calls the thing it counts — 'Stock', 'Samples', 'SIM
   *  cards'. Never rendered raw: pass it to `balancePhrases()`, which owns the
   *  grammar for every screen. Absent means 'Stock'. */
  balanceLabel?: string;
  groups: { id: string; parentId: string | null; name: string; path: string }[];
  products: { id: string; groupId: string | null; name: string }[];
  attributes: { productId: string; name: string; values: string[] }[];
  skus: { id: string; productId: string; code: string | null; label: string; attributes: Record<string, string> }[];
}

export const productMasterAPI = {
  listGroups: (projectId: number, includeInactive = false) =>
    api.get(`/product-master/${projectId}/groups`, { params: { includeInactive } }),
  createGroup: (projectId: number, body: { name: string; parentId?: string | null }) =>
    api.post(`/product-master/${projectId}/groups`, body),
  updateGroup: (projectId: number, groupId: string, body: { name: string; parentId?: string | null }) =>
    api.put(`/product-master/${projectId}/groups/${groupId}`, body),
  setGroupActive: (projectId: number, groupId: string, active: boolean) =>
    api.put(`/product-master/${projectId}/groups/${groupId}/active`, { active }),
  // Hard delete. 409s when products, sub-folders or live grid questions depend
  // on it; the message names what is in the way.
  deleteGroup: (projectId: number, groupId: string, force = false) =>
    api.delete(`/product-master/${projectId}/groups/${groupId}`, { params: { force } }),

  listProducts: (projectId: number, opts: { groupId?: string | null; includeInactive?: boolean } = {}) =>
    api.get(`/product-master/${projectId}/products`, {
      params: { groupId: opts.groupId ?? undefined, includeInactive: opts.includeInactive ?? false },
    }),
  createProduct: (projectId: number, body: { name: string; groupId?: string | null; code?: string | null }) =>
    api.post(`/product-master/${projectId}/products`, body),
  updateProduct: (
    projectId: number,
    productId: string,
    body: { name: string; groupId?: string | null; code?: string | null },
  ) => api.put(`/product-master/${projectId}/products/${productId}`, body),
  setProductActive: (projectId: number, productId: string, active: boolean) =>
    api.put(`/product-master/${projectId}/products/${productId}/active`, { active }),
  deleteProduct: (projectId: number, productId: string, force = false) =>
    api.delete(`/product-master/${projectId}/products/${productId}`, { params: { force } }),

  listAttributes: (projectId: number, productId: string) =>
    api.get(`/product-master/${projectId}/products/${productId}/attributes`),
  createAttribute: (projectId: number, productId: string, body: { name: string; values: string[] }) =>
    api.post(`/product-master/${projectId}/products/${productId}/attributes`, body),
  updateAttribute: (projectId: number, attributeId: string, body: { name: string; values: string[] }) =>
    api.put(`/product-master/${projectId}/attributes/${attributeId}`, body),
  deleteAttribute: (projectId: number, attributeId: string) =>
    api.delete(`/product-master/${projectId}/attributes/${attributeId}`),

  // Re-runnable: only inserts combinations that don't exist yet, so adding a
  // colour and regenerating leaves every existing SKU (and its retired state)
  // untouched. Returns { created, total }.
  generateSkus: (projectId: number, productId: string) =>
    api.post(`/product-master/${projectId}/products/${productId}/generate-skus`),
  listSkus: (projectId: number, opts: { productId?: string | null; includeInactive?: boolean } = {}) =>
    api.get(`/product-master/${projectId}/skus`, {
      params: { productId: opts.productId ?? undefined, includeInactive: opts.includeInactive ?? false },
    }),
  updateSku: (
    projectId: number,
    skuId: string,
    body: { skuCode?: string | null; mrp?: number | string | null; uom?: string | null; barcode?: string | null },
  ) => api.put(`/product-master/${projectId}/skus/${skuId}`, body),
  setSkuActive: (projectId: number, skuId: string, active: boolean) =>
    api.put(`/product-master/${projectId}/skus/${skuId}/active`, { active }),
  deleteSku: (projectId: number, skuId: string, force = false) =>
    api.delete(`/product-master/${projectId}/skus/${skuId}`, { params: { force } }),

  // The whole active catalogue in one call. Feeds the task builder's level /
  // characteristic / scope pickers; the same shape is embedded in the mobile
  // task payload so both sides resolve options identically.
  getCatalog: (projectId: number) => api.get(`/product-master/${projectId}/catalog`),

  // Catalogue import (base64 XLSX in a JSON body, parsed server-side — same
  // convention as every other bulk upload here). One row per SKU; any column
  // outside the fixed set is read as a characteristic.
  downloadBulkTemplate: (projectId: number) =>
    api.get(`/product-master/${projectId}/bulk-upload/template`, { responseType: 'blob' }),
  bulkUpload: (projectId: number, data: string) =>
    api.post(`/product-master/${projectId}/bulk-upload`, { data }),

  // Running stock at one store: what each version opened at, what moved it, and
  // which task question recorded each figure. The arithmetic — last count wins,
  // then deltas — is the one thing no task response report can do, because it
  // spans tasks by design.
  // Omit storeId for every store — the default the Running Count tab opens with.
  getStockLedger: (projectId: number, storeId?: string) =>
    api.get(`/product-master/${projectId}/stock/ledger`, {
      params: storeId ? { storeId } : {},
    }),

  // The master switch. Off means promoters see no balance; it does NOT stop the
  // figures being recorded, so switching it on later reveals history rather than
  // starting from zero.
  setStockTrackingEnabled: (projectId: number, enabled: boolean) =>
    api.put(`/product-master/${projectId}/stock/enabled`, { enabled }),

  // What this project calls the thing it counts. Read it through
  // useBalanceLabel(), which caches — this is the raw call behind it.
  getBalanceLabel: (projectId: number) =>
    api.get(`/product-master/${projectId}/stock/label`),

  // An empty string is meaningful here: it clears the override and restores the
  // 'Stock' default.
  setBalanceLabel: (projectId: number, label: string) =>
    api.put(`/product-master/${projectId}/stock/label`, { label }),
};

/** One product version's running stock at a store, with its provenance. */
export interface StockSummaryRow {
  key: string;
  isVersion: boolean;
  /** Stock is per shelf: the same version at two stores is two separate rows. */
  storeId: string;
  storeName: string;
  label: string;
  opening: number | null;
  openingAt: string | null;
  openingSource: string | null;
  added: number;
  /** Sales only — see `removed` for everything else that left the shelf. */
  reduced: number;
  /** Transferred out, returned to the client, written off. Never a sale. */
  removed: number;
  available: number;
  sources: { effect: string; taskTitle: string; questionText: string; count: number }[];
  lastMovementAt: string | null;
  /** The product a version belongs to, and its characteristic values in order. */
  productName?: string | null;
  attributes?: { name: string; value: string }[];
  /** Who recorded the latest figure. */
  lastByCode?: string | null;
  lastByName?: string | null;
}

/** Somebody deployed at a store. */
export interface StockStoreStaff {
  storeId: string;
  employeeCode: string;
  name: string | null;
  designation: string | null;
  isPrimary: boolean;
}

export interface StockActivityPart {
  units: number;
  products: number;
  top: { label: string; qty: number }[];
}

/** What one person recorded at one store, for one task, on one day. */
export interface StockActivity {
  storeId: string;
  day: string;
  at: string;
  employeeCode: string | null;
  employeeName?: string | null;
  taskTitle: string;
  counted: StockActivityPart;
  added: StockActivityPart;
  sold: StockActivityPart;
  removed: StockActivityPart;
}

// Projects API
export const projectsAPI = {
  getActiveProjects: () => api.get('/projects'),
  getActiveBudgets: (projectId: number) => api.get(`/projects/${projectId}/budgets`),
  getProjectOwner: (projectId: number) => api.get(`/projects/${projectId}/owner`),
};

// File Upload API
export const uploadAPI = {
  uploadFile: (file: File, folder: string = 'uploads') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    return api.post('/upload/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteFile: (blobPath: string) => api.delete('/upload/file', { data: { blobPath } }),
  getBlobUrl: (blobPath: string) => api.get(`/upload/url/${blobPath}`),
};

// Project Configuration API (Super Admin - project settings + budget feature mapping)
export const projectConfigAPI = {
  getAll: () => api.get('/project-config'),
  getByProject: (projectId: number) => api.get(`/project-config/${projectId}`),
  getPublicConfig: (projectId: number) => api.get(`/project-config/public/${projectId}`),
  upsert: (data: {
    project_id: number;
    project_name?: string;
    display_title?: string;
    theme_primary_color?: string;
    theme_secondary_color?: string;
    theme_tertiary_color?: string;
    theme_on_primary_color?: string;
    theme_surface_accent_color?: string;
    logo_url?: string;
    favicon_url?: string;
    notification_icon_url?: string;
    description?: string;
    is_active?: boolean;
  }) => api.post('/project-config', data),
  delete: (projectId: number) => api.delete(`/project-config/${projectId}`),
  // Feature access per project + budget
  getFeatures: (projectId: number, budgetId?: number) =>
    api.get(`/project-config/${projectId}/features`, { params: budgetId ? { budgetId } : {} }),
  upsertFeature: (data: {
    application: string;
    project_id: number;
    budget_id?: number | null;
    feature_key: string;
    is_enabled: boolean;
  }) => api.post('/project-config/features', data),
  bulkSaveFeatures: (data: {
    project_id: number;
    budget_id?: number | null;
    features: { application: string; feature_key: string; is_enabled: boolean }[];
  }) => api.post('/project-config/features/bulk', data),
  deleteFeature: (id: number) => api.delete(`/project-config/features/${id}`),
  // Budget application access (web/mobile/both/none per budget)
  getBudgetAccess: (projectId: number) =>
    api.get(`/project-config/${projectId}/budget-access`),
  saveBudgetAccess: (data: {
    project_id: number;
    budgets: { budget_id: number; budget_name: string; application_access: string }[];
  }) => api.post('/project-config/budget-access', data),
  backfillAdminRoles: () => api.post('/project-config/backfill-admin-roles'),
  // Per-project Gemini grooming prompt (Master Management → Grooming Prompt)
  getGroomingPrompt: (projectId: number) =>
    api.get(`/project-config/${projectId}/grooming-prompt`),
  saveGroomingPrompt: (projectId: number, grooming_prompt: string) =>
    api.put(`/project-config/${projectId}/grooming-prompt`, { grooming_prompt }),
  resetGroomingPrompt: (projectId: number) =>
    api.put(`/project-config/${projectId}/grooming-prompt`, { grooming_prompt: '' }),
};

// Store Categories API
export const categoriesAPI = {
  getAll: (projectId: number) => api.get('/store-categories', { params: { projectId } }),
  getById: (id: string) => api.get(`/store-categories/${id}`),
  create: (data: any) => api.post('/store-categories', data),
  update: (id: string, data: any) => api.put(`/store-categories/${id}`, data),
  delete: (id: string) => api.delete(`/store-categories/${id}`),
};

// Locations API
export const locationsAPI = {
  // Get all states
  getStates: () => api.get('/locations/states'),
  // Get cities by stateId
  getCitiesByStateId: (stateId: number) => api.get(`/locations/cities/byStateId/${stateId}`),
};

export const googleMapsAPI = {
  autocomplete: (
    params: { input: string; city?: string; state?: string; sessionToken: string },
    signal?: AbortSignal,
  ) => api.get('/google-maps/autocomplete', { params, signal }),
  placeDetails: (placeId: string, sessionToken: string, signal?: AbortSignal) =>
    api.get('/google-maps/place-details', { params: { placeId, sessionToken }, signal }),
};

// Approvals API
export const approvalsAPI = {
  getAllApprovalRequests: (userId: string | number, params?: { status?: string; type?: string; projectId?: number; employeeCode?: string }) =>
    trackProductLoad('approval_queue', () => api.get(`/approvals/all/${userId}`, { params })),
  updateLeaveRequest: (requestId: string | number, data: { 
    status: string; 
    comments?: string; 
    approverId: string | number;
  }) => trackProductRequest('approval_decide', {
    approval_type: 'leave', entry_point: 'approval_queue',
    decision: data.status === 'approved' ? 'approve' : data.status === 'rejected' ? 'reject' : undefined,
  }, () => api.put(`/approvals/leave/${requestId}`, data)),
  updateRegularizationRequest: (requestId: string | number, data: {
    status: string;
    comments?: string;
    approverId: string | number;
  }) => trackProductRequest('approval_decide', {
    approval_type: 'regularization', entry_point: 'approval_queue',
    decision: data.status === 'approved' ? 'approve' : data.status === 'rejected' ? 'reject' : undefined,
  }, () => api.put(`/approvals/regularization/${requestId}`, data)),
  // requestId is the 1Office ATTENDANCE ROW id, not a leave application id.
  // Approving makes 1Office credit the employee's Comp Off leave balance.
  updateCompOffRequest: (requestId: string | number, data: {
    status: string;
    comments?: string;
    approverId: string | number;
  }) => trackProductRequest('approval_decide', {
    approval_type: 'compoff', entry_point: 'approval_queue',
    decision: data.status === 'approved' ? 'approve' : data.status === 'rejected' ? 'reject' : undefined,
  }, () => api.put(`/approvals/compoff/${requestId}`, data)),
};

// ── Probation Confirmation (My Approvals → Probation Confirmation) ───────────
// The 1Office employee-confirmation flow, the same one the Employee Portal
// runs. The approver is ALWAYS the signed-in user, resolved server-side — no
// call here sends an approver id, and the API would ignore one.
//
// `view` is 'pending' (waiting for this user) or 'all' (their history as well).
// submit is a real 1Office write and is never retried automatically; while the
// server's switch is off it answers 403 SUBMIT_DISABLED before touching 1Office.
// Errors carry `{ success: false, code, message, errors? }` — callers branch on
// `code` (NOT_YOURS / NOT_YOUR_STEP / STALE / VALIDATION / UPSTREAM_FAILED / …).
//
// As with every other *API here the raw AxiosResponse comes back, so callers
// read `res.data.data`.
export const probationAPI = {
  list: (view: ProbationView) =>
    api.get<{ success: boolean; data: ProbationListResponse }>('/approvals/probation', { params: { view } }),
  detail: (confirmationId: number, view: ProbationView) =>
    api.get<{ success: boolean; data: ProbationReviewBundle }>(`/approvals/probation/${confirmationId}`, {
      params: { view },
    }),
  // 1Office's own last working day for a termination (notice periods applied),
  // used to prefill the field when Termination is picked.
  lastWorkingDate: (confirmationId: number) =>
    api.get<{ success: boolean; data: { lastWorkingDate: string | null } }>(
      `/approvals/probation/${confirmationId}/last-working-date`,
    ),
  submit: (confirmationId: number, input: ProbationDecisionInput) =>
    api.post<{ success: boolean; data: ProbationDecisionResult }>(
      `/approvals/probation/${confirmationId}/decision`,
      input,
    ),
};

// Tasks API (Market Working Activity)
/** The people a task is assigned to. `resolved: false` = it could not be worked out just now. */
export interface TaskAssignees {
  people: { code: string; name: string; roles: string[] }[];
  resolved: boolean;
  /** The hand-picked people asked about, by name, whether or not they fit (the form's preview only). */
  picked?: { code: string; name: string }[];
}

export const tasksAPI = {
  // Task CRUD
  getAll: (params?: { status?: string; task_type?: string; projectId?: number; roleId?: string }) =>
    trackProductLoad('task_list', () => api.get('/tasks', { params })),
  getById: (id: string) => api.get(`/tasks/${id}`),
  // Who the roles and conditions in the task form would reach (not yet saved): the list to hand-pick from.
  // The hand-picked codes are sent only to be named in the answer; they do not narrow it.
  previewAudience: (body: {
    projectId: number;
    target_role_ids: string[];
    target_store_category_ids: number[];
    target_store_trade_ids: string[];
    target_am_codes: string[];
    target_th_codes: string[];
    target_audience_match: string;
    target_employee_codes?: string[];
  }) => api.post<{ success: boolean; data: TaskAssignees }>('/tasks/audience-preview', body),
  // Who a saved task is assigned to right now.
  getAssignees: (taskId: string) => api.get<{ success: boolean; data: TaskAssignees }>(`/tasks/${taskId}/assignees`),
  // The project's Assistant Managers and Trade Heads, for the task form's "team of" pickers.
  getAudienceManagers: (projectId: number) =>
    api.get<{ success: boolean; data: { ams: { code: string; name: string }[]; ths: { code: string; name: string }[] } }>(
      '/tasks/audience-managers',
      { params: { projectId } },
    ),
  create: (data: { 
    title: string; 
    description?: string; 
    task_type?: string; 
    status?: string; 
    start_date?: string; 
    end_date?: string; 
    created_by?: number;
    recurrence_pattern?: string;
    recurrence_interval?: number;
    recurrence_days_of_week?: number[];
    recurrence_day_of_month?: number;
    recurrence_week_of_month?: number;
    recurrence_month_of_year?: number;
    recurrence_end_type?: string;
    recurrence_end_date?: string;
    recurrence_occurrences?: number;
    project_id?: number;
    target_role_ids?: string[];
    submission_mode?: 'overwrite' | 'incremental';
    geo_based_submission?: boolean;
  }) => trackProductRequest('task_create', { entry_point: 'task_editor' },
    () => api.post('/tasks', data)),
  update: (id: string, data: {
    title?: string;
    description?: string;
    task_type?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    recurrence_pattern?: string;
    recurrence_interval?: number;
    recurrence_days_of_week?: number[];
    recurrence_day_of_month?: number;
    recurrence_week_of_month?: number;
    recurrence_month_of_year?: number;
    recurrence_end_type?: string;
    recurrence_end_date?: string;
    recurrence_occurrences?: number;
    target_role_ids?: string[];
    submission_mode?: 'overwrite' | 'incremental';
    geo_based_submission?: boolean;
  }) => trackProductRequest('task_update', { entry_point: 'task_editor' },
    () => api.put(`/tasks/${id}`, data)),
  delete: (id: string) => api.delete(`/tasks/${id}`),

  // Request individual raw submissions (pre-aggregation) — used for the
  // "drill into submissions" view in the response explorer. Default answers
  // endpoint returns aggregated rows per the task's submission_mode.
  getRawAnswers: (taskId: string, userId?: number) =>
    api.get(`/tasks/${taskId}/answers`, { params: { user_id: userId, raw: true } }),
  
  // Questions
  getQuestions: (taskId: string) => api.get(`/tasks/${taskId}/questions`),
  addQuestion: (taskId: string, data: { question_text: string; question_type?: string; options?: string[]; is_required?: boolean; display_order?: number; rules?: any | null }) =>
    api.post(`/tasks/${taskId}/questions`, data),
  updateQuestion: (questionId: string, data: { question_text?: string; question_type?: string; options?: string[]; is_required?: boolean; display_order?: number; rules?: any | null; confirm_overwrite?: boolean }) =>
    api.put(`/tasks/questions/${questionId}`, data),
  deleteQuestion: (questionId: string) => api.delete(`/tasks/questions/${questionId}`),
  reorderQuestions: (taskId: string, questionIds: string[]) =>
    api.put(`/tasks/${taskId}/questions/reorder`, { questionIds }),
  // Admin override for OCR-extracted answer data. Used by the Apple Scorecard's
  // Seed Scores tab to correct misreads. Re-upserts employee_seed_scores when
  // the parent question is is_seed_score_source.
  overrideAnswerExtractedData: (answerId: string, value: number) =>
    api.patch(`/tasks/answers/${answerId}/extracted-data`, { value }),
  
  // Answers
  getAnswers: (taskId: string, userId?: number) => api.get(`/tasks/${taskId}/answers`, { params: { user_id: userId } }),
  submitAnswers: (taskId: string, data: { user_id: number; employee_code?: string; answers: Array<{ question_id: string; answer_text?: string; answer_value?: any; photo_url?: string }>; latitude?: string; longitude?: string }) =>
    api.post(`/tasks/${taskId}/answers`, data),
  getResponsesSummary: (taskId: string) => api.get(`/tasks/${taskId}/responses`),
};

// Store visit questionnaires. Authoring goes through tasksAPI (a questionnaire IS a task with
// task_type='store_visit'); only the answers report is its own endpoint.
export const storeVisitTasksAPI = {
  report: (projectId: number, startDate: string, endDate: string) =>
    api.get('/store-visit-tasks/report', { params: { projectId, startDate, endDate } }),
};

// Store Visits Configuration: the per-role visit targets (does this role visit stores, how
// many days a week, how many stores a day), and the weekly read of who met them.
export const storeVisitTargetsAPI = {
  get: (projectId: number) => api.get(`/store-visit-targets/${projectId}`),
  /** validFrom is the day the targets apply from (YYYY-MM-DD); left out, the server uses this week's Monday.
   *  carried marks a role sent unchanged only to cover the days before its first target. */
  save: (
    projectId: number,
    roles: Array<{ roleId: string; visitsExpected: boolean; daysPerWeek: number | null; storesPerDay: number | null; carried?: boolean }>,
    validFrom?: string,
  ) => api.put(`/store-visit-targets/${projectId}`, validFrom ? { roles, validFrom } : { roles }),
  /** weekStart is the Monday of the week (YYYY-MM-DD); left out, the server uses this week. */
  compliance: (projectId: number, weekStart?: string) =>
    api.get(`/store-visit-targets/${projectId}/compliance`, { params: weekStart ? { weekStart } : {} }),
  /** One person's visits behind their compliance row: one day, or up to seven within one week (YYYY-MM-DD). */
  complianceVisits: (projectId: number, employeeCode: string, from: string, to: string) =>
    api.get(`/store-visit-targets/${projectId}/compliance/visits`, { params: { employeeCode, from, to } }),
};

// Pre-Journey Plan: each project's monthly visit plan for the roles it ticks (settings on
// Master Data › Store Visits Configuration, the report on Reports › Store Visit Compliance).
// Saving the settings or a base city rebuilds the current month's plan on the server.
export const preJourneyPlanAPI = {
  getSettings: (projectId: number) => api.get(`/pre-journey-plan/${projectId}/settings`),
  /** Every rule is sent (all eight); roleIds [] switches the plan off. */
  saveSettings: (projectId: number, body: { roleIds: string[]; rules: PlanRules }) =>
    api.put(`/pre-journey-plan/${projectId}/settings`, body),
  getBaseCities: (projectId: number) => api.get(`/pre-journey-plan/${projectId}/base-cities`),
  /** Only the rows that changed; city null removes the override. */
  saveBaseCities: (projectId: number, changes: PlanBaseCityChange[]) =>
    api.put(`/pre-journey-plan/${projectId}/base-cities`, { changes }),
  /** One month (YYYY-MM) or one quarter (YYYY-Qn); left out, the current month. */
  getReport: (projectId: number, period?: PlanReportPeriod) =>
    api.get(`/pre-journey-plan/${projectId}/report`, { params: period ?? {} }),
};

// The managers who cover a store while nobody is deployed there (Dashboard > Deployment >
// Undeployed Stores). A store's manager is otherwise worked out from the people deployed
// at it, so a vacant store belongs to nobody until some are assigned here.
export const storeVacancyManagersAPI = {
  /** Every assignment of the project (one per store and person), and whether this person may change them. */
  get: (projectId: number) => api.get(`/store-vacancy-managers/${projectId}`),
  /** The managers who can be picked, as Trade Heads and AMs. Only for someone who may assign. */
  managers: (projectId: number) => api.get(`/store-vacancy-managers/${projectId}/managers`),
  /** Give these stores exactly these people (Trade Heads and AMs), or clear them with []. */
  assign: (projectId: number, storeIds: string[], managerCodes: string[]) =>
    api.put(`/store-vacancy-managers/${projectId}`, { storeIds, managerCodes }),
};

// Roles API

/** roles.uniform_check_engine: who checks uniform & background for a role with
 *  Face Verification & Uniform Compliance on. Jev falls back to Gemini per photo. */
export type UniformCheckEngine = 'gemini' | 'jev';

/** GET /roles/uniform-check-options: whether Jev can be chosen for a project.
 *  rubric 'project' = Jev checks against the project's own grooming prompt; null = Jev
 *  can't take the project's grooming rules now (too long, or not readable just now; Jev
 *  can't be picked); connected false = the Sophy key is not set (Gemini checks until it
 *  is). reason is null when Jev is usable, otherwise plain English. */
export interface UniformCheckOptions {
  connected: boolean;
  rubric: 'ipro' | 'default' | 'project' | null;
  reason: string | null;
}

export const rolesAPI = {
  getAll: () => api.get('/roles'),
  getByProject: (projectId: number) => api.get(`/roles/project/${projectId}`),
  getById: (id: string) => api.get(`/roles/${id}`),
  create: (data: { name: string; description?: string; permissions?: string[]; project_id?: number; category?: string; geo_based_attendance?: boolean; aws_face_verification?: boolean; uniform_check_engine?: UniformCheckEngine; multi_store_deployment?: boolean; data_scope?: 'all_project' | 'hierarchy' }) =>
    api.post('/roles', data),
  update: (id: string, data: { name?: string; description?: string; permissions?: string[]; category?: string; geo_based_attendance?: boolean; aws_face_verification?: boolean; uniform_check_engine?: UniformCheckEngine; multi_store_deployment?: boolean; data_scope?: 'all_project' | 'hierarchy' }) =>
    api.put(`/roles/${id}`, data),
  delete: (id: string) => api.delete(`/roles/${id}`),
  /** Whether Jev can check uniform & background for this project's roles (UniformCheckOptions). */
  getUniformCheckOptions: (projectId: number) =>
    api.get('/roles/uniform-check-options', { params: { projectId } }),
  
  // User-Role assignments
  getAllAssignments: (projectId?: number) => api.get('/roles/assignments/all', { params: projectId ? { projectId } : {} }),
  getRoleUsers: (roleId: string) => api.get(`/roles/${roleId}/users`),
  getUserRoles: (userId: string) => api.get(`/roles/user/${userId}`),
  assignRole: (userId: string, roleId: string) =>
    api.post('/roles/assign', { user_name: userId, role_id: roleId }),
  removeRole: (userId: string, roleId: string) =>
    api.delete(`/roles/assign/${userId}/${roleId}`),

  // Search all employees (super_admin only)
  searchAllEmployees: (query: string, employeeCode: string) =>
    api.get('/roles/search-all-employees', { params: { q: query, employeeCode } }),

  // Role-Feature mappings (Mobile Access)
  getAllFeatureMappings: () => api.get('/roles/feature-mappings/all'),
  updateRoleFeatures: (roleId: string, featureKeys: string[]) =>
    api.put(`/roles/${roleId}/features`, { feature_keys: featureKeys }),
};

// External Users API
export const externalUsersAPI = {
  getByProject: (projectId: number) => api.get('/external-users', { params: { projectId } }),
  // Detect identity reuse — used by the Add External User form to switch into
  // "linking to existing user" mode before the admin clicks Submit.
  lookup: (params: { email?: string; mobile?: string; projectId?: number }) =>
    api.get('/external-users/lookup', { params }),
  // Whether this person may add external users to this project, and at what level.
  access: (projectId: number) => api.get('/external-users/access', { params: { projectId } }),
  create: (data: {
    name: string;
    mobile: string;
    projectId: number;
    createdBy?: string;
    email?: string | null;
    password?: string | null;
    roleId?: string;
  }) => api.post('/external-users', data),
  update: (
    id: number | string,
    data: {
      name?: string;
      mobile?: string;
      isActive?: boolean;
      email?: string | null;
      password?: string | null;
      projectId?: number;
    }
  ) => api.put(`/external-users/${id}`, data),
  delete: (id: number) => api.delete(`/external-users/${id}`),
};

// Menus API
export const menusAPI = {
  getAll: () => api.get('/menus'),
  getById: (id: string) => api.get(`/menus/${id}`),
  getUserMenus: (userId: string) => api.get(`/menus/user/${userId}`),
  create: (data: { name: string; code: string; description?: string; icon?: string; path?: string; parent_id?: string | null; display_order?: number; is_active?: boolean }) =>
    api.post('/menus', data),
  update: (id: string, data: { name?: string; code?: string; description?: string; icon?: string; path?: string; parent_id?: string | null; display_order?: number; is_active?: boolean }) =>
    api.put(`/menus/${id}`, data),
  delete: (id: string) => api.delete(`/menus/${id}`),
  
  // Role-Menu mappings
  getRoleMenus: (roleId: string) => api.get(`/menus/role/${roleId}/menus`),
  updateRoleMenus: (roleId: string, menuIds: string[]) =>
    api.put(`/menus/role/${roleId}/menus`, { menu_ids: menuIds }),
  getAllMappings: () => api.get('/menus/mappings/all'),
  bulkUpdateMappings: (mappings: { role_id: string; menu_id: string }[]) =>
    api.post('/menus/mappings/bulk', { mappings }),
  // What each page's tick also allows (the server's one map, services/page-rights.ts):
  // [{ key, label, also, pages: [{ name, code, paths }] }]. Drawn as the grid's "also:" lines.
  getPageRights: () => api.get('/menus/page-rights'),
};

// Attendance API
export const attendanceAPI = {
  // Get daily attendance data with date range support
  getDailyAttendanceLogs: (params?: {
    date?: string;
    fromDate?: string;
    toDate?: string;
    isActive?: boolean;
    projectId?: number;
    /** One person's employee code: the server answers for that person only, and only
     *  when they are in the viewer's own scope (a code outside it gives no rows). Not
     *  `employeeCode`, which on this route names the VIEWER (the app sends it). */
    onlyEmployee?: string;
  }) => api.get('/attendance/daily-attendance-data', { params }),
  // Get random attendance logs for reports
  getRandomAttendanceLogs: (params?: {
    start_date?: string;
    end_date?: string;
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
    employeeCode?: string; // For hierarchy filtering - excludes logged-in user
    projectId?: number;
  }) => api.get('/attendance/random-attendance-logs', { params }),

  // ── Dashboard → Attendance tab ──
  // Date-wise status distribution (stacked-bar trend) for a month.
  getDashboardTrend: (params?: { month?: string; projectId?: number; roleId?: string } & DashRichFilterParams) =>
    api.get('/attendance/dashboard/trend', { params }),
  // Continuously-absent + absent-today lists (today-anchored).
  getDashboardAbsentees: (params?: { projectId?: number; lookbackDays?: number; roleId?: string } & DashRichFilterParams) =>
    api.get('/attendance/dashboard/absentees', { params }),
  // Drill-down: employees in one status segment of one day.
  getDashboardTrendDay: (params?: { date: string; status: string; projectId?: number; roleId?: string } & DashRichFilterParams) =>
    api.get('/attendance/dashboard/trend-day', { params }),
  // Employee Attendance Map: per-employee present/absent day counts for a month.
  getDashboardAttendanceMap: (params?: { month?: string; projectId?: number; roleId?: string } & DashRichFilterParams) =>
    api.get('/attendance/dashboard/attendance-map', { params }),
};

// Rich dashboard filters shared by the four server-side tabs (Attendance / Red
// Flag / Mood / Appeals). Multi-selects are serialized as CSV strings, text
// filters as-is. Every field is optional and only sent when active (axios drops
// undefined params) — an all-undefined object leaves the endpoint behaviour
// unchanged. Mirrors the backend DashFilters (dashboard-shared.ts). No "Employees
// Deployed" — that store-headcount filter stays on the Deployment view only.
export interface DashRichFilterParams {
  storeName?: string;
  storeCode?: string;
  trades?: string;      // CSV of store trades
  categories?: string;  // CSV of store-category names
  cities?: string;      // CSV of cities
  states?: string;      // CSV of states (State filter) — matched on the normalized key
  regionStates?: string; // CSV of the states a selected Region expands to (server has no region map)
  empName?: string;
  empCode?: string;
  standings?: string;   // CSV of 6-mo quadrants (Stars/Achievers/Strivers/At Risk)
  amCodes?: string;     // CSV of Area Manager codes
  thCodes?: string;     // CSV of Trade Head codes
  budgetIds?: string;   // CSV of 1Office budget ids
}

// Dashboard → Mood tab. Date-wise 5-point mood mix, averages + distribution, a
// low-mood watchlist + not-submitted-today, and a per-day drill-down. Scoped
// like the Daily Attendance Report (admin → project, manager → hierarchy).
export const moodDashboardAPI = {
  getTrend: (params?: { month?: string; projectId?: number; employeeCode?: string; roleId?: string } & DashRichFilterParams) =>
    api.get('/emoji/dashboard/trend', { params }),
  getWatchlist: (params?: { month?: string; projectId?: number; employeeCode?: string; roleId?: string } & DashRichFilterParams) =>
    api.get('/emoji/dashboard/watchlist', { params }),
  getTrendDay: (params?: { date: string; mood: string; projectId?: number; employeeCode?: string; roleId?: string } & DashRichFilterParams) =>
    api.get('/emoji/dashboard/trend-day', { params }),
};

// Dashboard → Red Flag tab. Daily trend stacked by flag type, yearly leaderboard,
// today's flags, and a per-day drill-down. Scoped like the Daily Attendance Report.
// Dashboard → Yellow Flag tab. Mounted on the yellow-flags router (NOT manager-focus)
// and deliberately authenticate-only, so a manager holding the dashboard_yellow_flag
// menu is scoped to their own subtree rather than 403'd like the /admin/* routes.
export const yellowFlagDashboardAPI = {
  getTrend: (params?: { month?: string; projectId?: number; roleId?: string; reason?: string } & DashRichFilterParams) =>
    api.get('/yellow-flags/dashboard/trend', { params }),
  // "Flags raised": every flag the trend's month total counts, one row each. Ask it with
  // the same params as getTrend so the list holds exactly that number.
  getMonthFlags: (params?: { month?: string; projectId?: number; roleId?: string; reason?: string } & DashRichFilterParams) =>
    api.get('/yellow-flags/dashboard/month-flags', { params }),
  getLeaderboard: (params?: { year?: string; projectId?: number; roleId?: string; reason?: string } & DashRichFilterParams) =>
    api.get('/yellow-flags/dashboard/leaderboard', { params }),
  getRaisers: (params?: { month?: string; projectId?: number; roleId?: string; reason?: string } & DashRichFilterParams) =>
    api.get('/yellow-flags/dashboard/raisers', { params }),
  getToday: (params?: { projectId?: number; roleId?: string; reason?: string } & DashRichFilterParams) =>
    api.get('/yellow-flags/dashboard/today', { params }),
  getTrendDay: (params?: { date: string; projectId?: number; roleId?: string; reason?: string } & DashRichFilterParams) =>
    api.get('/yellow-flags/dashboard/trend-day', { params }),
  getReasonOptions: (params?: { projectId?: number }) =>
    api.get('/yellow-flags/dashboard/reasons', { params }),
};

// Dashboard → Approvals tab. ONE endpoint returns every view (KPIs, by-manager,
// by-type, top requesters, plus the raw rows the drill-downs filter client-side)
// because the cost here is the upstream leave + regularization fetch, not the
// aggregation — splitting it per card would multiply that fetch.
export const approvalsDashboardAPI = {
  getDashboard: (params?: { month?: string; projectId?: number; roleId?: string } & DashRichFilterParams) =>
    api.get('/approvals/dashboard', { params }),
};

// Dashboard → Training tab. ONE endpoint returns every view (KPIs, by-session,
// by-employee, by-manager, by-type, plus the raw invitation rows the drill-downs
// filter client-side): they are all cuts of the SAME `trainings ⨝ training_attendees`
// join, so splitting per card would re-run the CRS name/manager resolution each time.
export const trainingDashboardAPI = {
  getDashboard: (params?: { month?: string; projectId?: number; roleId?: string } & DashRichFilterParams) =>
    api.get('/apple-scorecard/trainings/dashboard', { params }),
};

// Dashboard → Score Movement tab. ONE endpoint returns every view (the six-month
// series, the Assistant Manager / Trade Head roll-ups and the people behind them):
// they are all cuts of the same snapshot rows, and the drill-downs filter the people
// client-side, so splitting it per card would repeat the name / manager resolution.
export const scoreMovementDashboardAPI = {
  getDashboard: (projectId: number, params?: { month?: string; roleId?: string } & DashRichFilterParams) =>
    api.get(`/scorecard/${projectId}/dashboard/score-movement`, { params }),
};

// Dashboard → EWS (Early Warning System). ONE endpoint: every employee in view with six
// figures for each of three finished months. `month` is the last of the three; the
// server never ends on a month still running. Leave and hours are read from 1Office for
// everyone in view, so the first answer for a set of months can take a minute or two.
export const ewsDashboardAPI = {
  getDashboard: (projectId: number, params?: { month?: string; roleId?: string } & DashRichFilterParams) =>
    api.get(`/scorecard/${projectId}/dashboard/ews`, { params, timeout: 300_000 }),
  // A ticket count, opened: the tickets of one employee raised in one month, then one
  // ticket's conversation (read on from the last message id with `after`). Answered only
  // to a viewer who holds EWS, about people EWS lists for them; anyone else is a 404.
  getTickets: (projectId: number, params: { empCode: string; month: string }) =>
    api.get(`/scorecard/${projectId}/dashboard/ews/tickets`, { params }),
  getConversation: (projectId: number, ticketNumber: string, after?: number) =>
    api.get(`/scorecard/${projectId}/dashboard/ews/tickets/${encodeURIComponent(ticketNumber)}`, { params: after ? { after } : undefined }),
  // What was said in a conversation with an employee: one person's, or everyone in the
  // viewer's scope for the Excel. The same guard as the tickets. Only ever added to.
  getRemarks: (projectId: number, params?: { empCode: string }) =>
    api.get(`/scorecard/${projectId}/dashboard/ews/remarks`, { params }),
  addRemark: (projectId: number, body: { empCode: string; remark: string }) =>
    api.post(`/scorecard/${projectId}/dashboard/ews/remarks`, body),
  // Which parameters the project monitors: every one named in `off` is switched off, every
  // other on, for the whole project. The project's admins only.
  setColumns: (projectId: number, body: { off: string[] }) =>
    api.put(`/scorecard/${projectId}/dashboard/ews/columns`, body),
};

// Dashboard → Data Hygiene tab. Two endpoints, one for each side of the tab, so a slow
// read of the people never holds up the store lists. Always live: neither takes a month
// or a filter. Envelope: { success, data }. Someone who does not see the whole project
// is answered 403, which the tab draws as its own panel.
//
// "Mark as OK" and its undo name one row of one check ({ side, check, subject }: the
// store id or the employee code) and answer with that side as it now stands, in the
// same shape as getStores / getEmployees. 409 = the row is not on its list any more.
export const dataHygieneDashboardAPI = {
  getStores: (projectId: number) =>
    api.get(`/data-hygiene/${projectId}/stores`),
  getEmployees: (projectId: number) =>
    api.get(`/data-hygiene/${projectId}/employees`),
  markOk: (projectId: number, row: { side: 'stores' | 'employees'; check: string; subject: string }) =>
    api.post(`/data-hygiene/${projectId}/marks`, row),
  undoMark: (projectId: number, row: { side: 'stores' | 'employees'; check: string; subject: string }) =>
    api.post(`/data-hygiene/${projectId}/marks/undo`, row),
};

// Optional subject-level scope for the Red Flag tab (store-level employees vs people
// managers). Sent only when chosen; an older API ignores the unknown param.
export type RedFlagDashLevel = 'frontline' | 'manager';
export const redFlagDashboardAPI = {
  getTrend: (params?: { month?: string; projectId?: number; employeeCode?: string; roleId?: string; flagType?: string; level?: RedFlagDashLevel } & DashRichFilterParams) =>
    api.get('/manager-focus/dashboard/trend', { params }),
  getActionStats: (params?: { month?: string; projectId?: number; employeeCode?: string; roleId?: string; flagType?: string; level?: RedFlagDashLevel } & DashRichFilterParams) =>
    api.get('/manager-focus/dashboard/action-stats', { params }),
  getLeaderboard: (params?: { year?: string; projectId?: number; employeeCode?: string; roleId?: string; flagType?: string; level?: RedFlagDashLevel } & DashRichFilterParams) =>
    api.get('/manager-focus/dashboard/leaderboard', { params }),
  getToday: (params?: { projectId?: number; employeeCode?: string; roleId?: string; flagType?: string; level?: RedFlagDashLevel } & DashRichFilterParams) =>
    api.get('/manager-focus/dashboard/today', { params }),
  getTrendDay: (params?: { date: string; flagType?: string; level?: RedFlagDashLevel; projectId?: number; employeeCode?: string; roleId?: string } & DashRichFilterParams) =>
    api.get('/manager-focus/dashboard/trend-day', { params }),
  getStoreBreakdown: (params?: { month?: string; projectId?: number; employeeCode?: string; roleId?: string; flagType?: string; level?: RedFlagDashLevel } & DashRichFilterParams) =>
    api.get('/manager-focus/dashboard/store-breakdown', { params }),
};

// Generic per-employee remarks (call notes) — one append-only thread per (project,
// employee). Shared by the Mood Report + Dashboard Low-mood watchlist.
export const employeeRemarksAPI = {
  list: (params: { employeeCode: string; projectId?: number }) =>
    api.get('/employee-remarks', { params }),
  add: (payload: { employeeCode: string; projectId?: number; body: string; authorName?: string }) =>
    api.post('/employee-remarks', payload),
  counts: (params: { employeeCodes: string; projectId?: number }) =>
    api.get('/employee-remarks/counts', { params }),
  // Batch remark TEXT for an export (POST — a report can cover thousands of codes).
  bulk: (payload: { employeeCodes: string[]; projectId?: number }) =>
    api.post('/employee-remarks/bulk', payload),
};

// Dashboard → Appeals tab. Overturn %, resolution TAT, appeals by flag type + by
// manager, and the open-appeals queue. Scoped like the Red Flag dashboard.
export const appealsDashboardAPI = {
  getDashboard: (params?: { month?: string; projectId?: number; roleId?: string } & DashRichFilterParams) =>
    api.get('/manager-focus/dashboard/appeals', { params }),
};

// Attrition dashboard tab. `month` anchors a 6-month trend window ENDING at that
// month (unlike the source PM dashboard, whose chart ignores its month picker).
export const attritionDashboardAPI = {
  // roleId + the look-back-safe rich filters narrow the roster server-side: amCodes /
  // thCodes / budgetIds, and states / regionStates / cities (resolved from each
  // person's last store on record). The other store / standing keys `filters` carries
  // from shared state are ignored for attrition. `tenures` is a CSV of tenure-band
  // keys; it narrows which EXITS are counted, not the headcount. Undefined fields are
  // dropped by axios, so the unfiltered call sends only `month`.
  getDashboard: (
    projectId: number,
    month?: string,
    roleId?: string,
    filters?: DashRichFilterParams,
    tenures?: string,
  ) =>
    api.get(`/scorecard/${projectId}/dashboard/attrition`, { params: { month, roleId, ...filters, tenures } }),
  /** The month's exits grouped by manager / store trade / category / city / state. */
  getBreakdown: (projectId: number, month?: string, roleId?: string, filters?: DashRichFilterParams, tenures?: string) =>
    api.get(`/scorecard/${projectId}/dashboard/attrition/breakdown`, { params: { month, roleId, ...filters, tenures } }),
};


// Task Logs V2 API (Task-Centric)
interface TaskLogsV2Params {
  projectId: number;
  start_date?: string;
  end_date?: string;
  employeeCode?: string;
}
export const taskLogsV2API = {
  getTasks: (params: TaskLogsV2Params) =>
    api.get('/tasks/logs/v2/tasks', { params }),
  getTaskResponses: (taskId: string, params: TaskLogsV2Params) =>
    api.get(`/tasks/logs/v2/tasks/${taskId}/responses`, { params }),
  downloadTaskPivot: (taskId: string, params: TaskLogsV2Params) =>
    api.get(`/tasks/logs/v2/tasks/${taskId}/download`, { params, responseType: 'blob' }),
};

// Where a sub-metric's values come from. 'sales_targets' = the month's Target or
// Sold figure from Master Data → Sales & Targets; nobody uploads it.
export type SubMetricSourceType = 'manual' | 'task_question' | 'sales_targets';
/** Which Sales & Targets figure an input carries. On screen 'sales' reads "Sold". */
export type SalesFeedFigure = 'target' | 'sales';
/** Where one parameter's Sales & Targets figures stand for one month. */
export interface SalesFeedState {
  /** 'none' = nothing to store (no scorecard carries the parameter that month). */
  status: 'none' | 'running' | 'waiting' | 'stored' | 'failed';
  month_over: boolean;
  /** The newest month that is over — the only one that follows later corrections by itself. */
  latest_closed: boolean;
  /** Waiting for an admin to press "Use these sales" (uploaded sales with a Sold input). */
  needs_release: boolean;
  /** ISO timestamps. */
  stored_at: string | null;
  people: number;
  sold_no_target: number;
  /** Newest sales write for the month; null = none found, or not applicable. */
  last_sales_at: string | null;
  /** Stored, and that month's targets or sales changed since. */
  changed: boolean;
  detail: string | null;
}

// Target Metrics API (per-project metric dictionary used by the Targets page)
export const targetMetricsAPI = {
  getAll: (projectId: number) => api.get('/target-metrics', { params: { projectId } }),
  create: (data: any) => api.post('/target-metrics', data),
  update: (id: number, data: any) => api.put(`/target-metrics/${id}`, data),
  // `cascade: true` wipes any targets/achievements that reference the metric
  // along with the metric itself. When omitted, the server refuses to delete
  // a referenced metric (returns 409 + structured payload).
  delete: (id: number, opts: { cascade?: boolean } = {}) =>
    api.delete(`/target-metrics/${id}`, { params: opts.cascade ? { cascade: 'true' } : {} }),
  // Eligible task-question source list for hybrid metrics (admin sets target,
  // achievement auto-derived from these questions' answers).
  getEligibleSourceQuestions: (projectId: number) =>
    api.get('/target-metrics/eligible-source-questions', { params: { projectId } }),
  // Sub-metric CRUD: each custom metric is a container of N sub-metrics
  // (Target, Achievement, Baseline, …), each with its own data source.
  // The parent metric carries a `formula` that combines them into the
  // achievement % consumed by the slab engine.
  // `effectiveMonth` (YYYY-MM-01) picks which version of the metric's calculation
  // to return — sub-metrics, formula and cohort can differ by month. Omitted =
  // the version in effect this month. The response also carries `version`.
  listSubMetrics: (metricId: number, effectiveMonth?: string) =>
    api.get(`/target-metrics/${metricId}/sub-metrics`, {
      params: effectiveMonth ? { effective_month: effectiveMonth } : {},
    }),
  createSubMetric: (metricId: number, data: {
    name: string;
    code?: string;
    source_type: SubMetricSourceType;
    source_question_id?: string | null;
    source_aggregation?: 'sum' | 'avg' | 'latest' | null;
    source_target_picker_question_id?: string | null;
    /** 'sales_targets' only: the figure, and the product keys (`<productId>::`; null = all products). */
    source_sales_figure?: SalesFeedFigure | null;
    source_sales_products?: string[] | null;
    display_order?: number;
    /** Valid range — values outside it are ignored when scoring. null = no limit. */
    valid_min?: number | null;
    valid_max?: number | null;
    /** Month whose calculation version the leg joins (default: this month). */
    effective_month?: string;
  }) => api.post(`/target-metrics/${metricId}/sub-metrics`, data),
  updateSubMetric: (metricId: number, subId: string, data: Partial<{
    name: string;
    code: string;
    source_type: SubMetricSourceType;
    source_question_id: string | null;
    source_aggregation: 'sum' | 'avg' | 'latest' | null;
    source_target_picker_question_id: string | null;
    source_sales_figure: SalesFeedFigure | null;
    source_sales_products: string[] | null;
    display_order: number;
    valid_min: number | null;
    valid_max: number | null;
  }>) => api.patch(`/target-metrics/${metricId}/sub-metrics/${subId}`, data),
  deleteSubMetric: (metricId: number, subId: string) =>
    api.delete(`/target-metrics/${metricId}/sub-metrics/${subId}`),
  updateFormula: (metricId: number, formula: string | null, effectiveMonth?: string) =>
    api.patch(`/target-metrics/${metricId}/formula`, effectiveMonth ? { formula, effective_month: effectiveMonth } : { formula }),
  // Save a metric's whole CALCULATION — sub-metrics (+ sources), formula, cohort —
  // as ONE version applying from `effective_from`, in a single transaction. When
  // no version starts at that month yet the server creates one, so earlier months
  // keep their calculation. `based_on` = the version the editor was showing (a
  // stale editor gets a 409).
  // A save that adds, removes or changes a Sales & Targets input can also answer
  // 409 with a `code`: SALES_FEED_HISTORY (figures already exist from that month;
  // the message names the month to pick) or SALES_FEED_CONFIRM (`months` +
  // `message`; resend with `confirm_replace: true` to replace those figures).
  saveMetricDefinition: (metricId: number, payload: {
    effective_from: string;
    based_on: string;
    formula: string | null;
    cohort_grouping: unknown;
    sub_metrics: Array<{
      id: string | null;
      name: string;
      code: string;
      source_type: SubMetricSourceType;
      source_question_id?: string | null;
      source_aggregation?: 'sum' | 'avg' | 'latest' | null;
      source_target_picker_question_id?: string | null;
      source_sales_figure?: SalesFeedFigure | null;
      source_sales_products?: string[] | null;
      /** Valid range — values outside it are ignored when scoring. null = no limit. */
      valid_min?: number | null;
      valid_max?: number | null;
    }>;
    confirm_replace?: boolean;
  }) => api.put(`/target-metrics/${metricId}/definition`, payload),
  // Sales & Targets figures of one parameter for one month (YYYY-MM-01). Nothing
  // is stored by a save: a month is stored by the nightly run, or by `release`
  // when the project's sales are uploaded (the admin's "sales are in"). A refusal
  // comes back as its status with { code, message }: MONTH_RUNNING, NO_TARGETS,
  // NO_SALES (resend with `allow_empty`), CONFIRM_OLDER (resend with
  // `confirm_older`), READ_FAILED.
  releaseSalesFeed: (metricId: number, periodMonth: string, opts?: { allow_empty?: boolean; confirm_older?: boolean }) =>
    api.post(`/target-metrics/${metricId}/sales-feed/release`, { period_month: periodMonth, ...opts }),
  getSalesFeedState: (metricId: number, periodMonth: string) =>
    api.get(`/target-metrics/${metricId}/sales-feed/state`, { params: { period_month: periodMonth } }),
  // Per-sub-metric value endpoints (manual upload path only — task-question
  // legs are populated by the extraction pipeline, Sales & Targets legs by the
  // month's stored figures; saving, the template and the upload answer 409
  // SUB_METRIC_AUTOMATED for either).
  // `roleId` lets a task-derived leg show the values its role's "No value = last
  // available month" config carries forward (such rows carry `carried_from_month`).
  // An answer outside the leg's valid range comes back as `ignored_value`.
  // For a Sales & Targets leg the answer also carries `sales_feed` (a
  // SalesFeedState), `auto_derived: true` and `preview` (true = the month is not
  // stored yet and the rows are the live figures, never used for scores); its
  // rows are the people on `roleId` whom the caller may see on the score list.
  listSubMetricValues: (subId: string, periodMonth: string, roleId?: string | null) =>
    api.get(`/target-metrics/sub-metrics/${subId}/values`, {
      params: { period_month: periodMonth, ...(roleId ? { role_id: roleId } : {}) },
    }),
  upsertSubMetricValues: (subId: string, payload: {
    period_month: string;
    scope_type: 'store' | 'employee';
    rows: Array<{ store_id?: string; employee_code?: string; value: number | null }>;
  }) => api.put(`/target-metrics/sub-metrics/${subId}/values`, payload),
  // Per-metric formula evaluation — returns the final per-employee value
  // (formula applied over sub-metrics) for the period. Feeds the "Result"
  // tab on the per-metric Manage surface.
  // `roleId` → the result also reflects that role's "No value = last available
  // month" carry-forward (carried rows come back with `carried_from_month`).
  getFormulaValues: (metricId: number, periodMonth: string, roleId?: string | null) =>
    api.get(`/target-metrics/${metricId}/formula-values`, {
      params: { period_month: periodMonth, ...(roleId ? { role_id: roleId } : {}) },
    }),
  // Who the Manage tables list for a month, for the role: the template's and the upload's list.
  getMonthPeople: (metricId: number, periodMonth: string, roleId?: string | null) =>
    api.get(`/target-metrics/${metricId}/month-people`, {
      params: { period_month: periodMonth, ...(roleId ? { role_id: roleId } : {}) },
    }),
  // Manual sub-metric bulk upload — template download (xlsx) + import.
  // roleId (optional) scopes the template's subjects to that role's employees
  // (or, for store scope, stores with at least one employee in the role).
  downloadSubMetricTemplate: (subId: string, periodMonth: string, scope: 'store' | 'employee', roleId?: string | null) =>
    api.get(`/target-metrics/sub-metrics/${subId}/template`, {
      params: { period_month: periodMonth, scope, ...(roleId ? { role_id: roleId } : {}) },
      responseType: 'blob',
    }),
  bulkUploadSubMetric: (subId: string, payload: {
    period_month: string;
    scope: 'store' | 'employee';
    /** base64-encoded xlsx file body */
    data: string;
    /** Optional role scope — rejects uploaded rows whose subject is not in the role. */
    role_id?: string | null;
  }) => api.post(`/target-metrics/sub-metrics/${subId}/bulk-upload`, payload),
};

// Targets API (set targets, upload achievements, view history)
export const targetsAPI = {
  list: (params: {
    projectId: number;
    periodMonth: string;
    metricId?: number;
    scope?: 'store' | 'employee';
    includeSubjects?: boolean;
  }) => api.get('/targets', { params }),
  upsertOne: (data: any) => api.post('/targets', data),
  bulkByFilter: (data: any) => api.post('/targets/bulk-by-filter', data),
  downloadTemplate: (params: { projectId: number; scope: 'store' | 'employee' }) =>
    api.get('/targets/bulk-upload/template', { params, responseType: 'blob' }),
  bulkUpload: (base64Data: string, projectId: number, scope: 'store' | 'employee') =>
    api.post('/targets/bulk-upload', { data: base64Data, project_id: projectId, scope }),
  delete: (id: string) => api.delete(`/targets/${id}`),
};

// Generic, project-scoped Employee Scorecard API (Phase 1+2).
// Project-agnostic. iPro (170) is seeded; other projects get an empty-state
// "Configure your scorecard" until an admin sets things up.
export const scorecardAPI = {
  // Computed scorecard rows for the given (project, month). Each row has
  // employee meta, the configured metrics with raw value + slab points +
  // weighted contribution, the total, and a quadrant label.
  getScore: (projectId: number, periodMonth: string) =>
    api.get(`/scorecard/${projectId}/${periodMonth}`),
  // People who have since left, with the score that was stored for them that month.
  // Nothing is recalculated and nobody is re-ranked — see the Inactive employees tab.
  getInactiveScores: (projectId: number, periodMonth: string) =>
    api.get(`/scorecard/${projectId}/${periodMonth}/inactive`),
  // Where the page should LAND, per role: the newest month <= N-1 that actually has
  // something to show for that role. Cheap probe — no scoring engine involved. Takes no
  // month, deliberately: the answer must not depend on what is currently displayed.
  getDefaultMonths: (projectId: number) =>
    api.get(`/scorecard/${projectId}/default-months`),
  // 6-month average view: last 6 monthly totals per employee (read from the
  // snapshot table, ending at periodMonth) + a mean-of-present average. Returns
  // { data: { months: string[], employees: [{ employee_code, totals, avg, months_count }] } }.
  getSixMonthScore: (projectId: number, periodMonth: string) =>
    api.get(`/scorecard/${projectId}/${periodMonth}/six-month`),
  // Catalog + current config for the project, scoped to a role. Passing null
  // (or omitting roleId) loads the project-default config (role_id IS NULL).
  // The response also includes the list of project roles for the dropdown.
  // effectiveFrom (YYYY-MM or YYYY-MM-01) picks which month's config VERSION
  // to load — configs are effective-dated, so different months can carry
  // different metric sets/weights. Omit to load the current month's version.
  getConfig: (projectId: number, roleId?: string | null, effectiveFrom?: string) => {
    const params: Record<string, string> = {};
    if (roleId) params.roleId = roleId;
    if (effectiveFrom) params.effectiveFrom = effectiveFrom;
    return api.get(`/scorecard/${projectId}/config`, { params });
  },
  // Atomically replace the (project, role) config. role_id null = project
  // default. attribution_scope is per-metric and defaults to 'employee'.
  saveConfig: (
    projectId: number,
    payload: {
      role_id?: string | null;
      // Which month this config version applies FROM (YYYY-MM or YYYY-MM-01).
      // The save replaces only this month's version. Omit → current month.
      effective_from?: string;
      metrics: Array<{
        metric_def_id: number;
        weight_pct: number;
        display_order?: number;
        attribution_scope?: 'employee' | 'store';
        // Target_metric only — 'raw' (default) scores the achievement
        // directly; 'achievement_pct' divides achievement by target ×100.
        score_basis?: 'raw' | 'achievement_pct';
        // Optional per-metric tuning knobs — used by seed_score for cohort
        // math params (honeymoon window, tier points). null = engine
        // defaults. Other metrics ignore this.
        config_params?: Record<string, any> | null;
        slabs: Array<{
          lower_bound: number | null;
          upper_bound: number | null;
          points: number;
          display_label?: string | null;
          display_order?: number;
        }>;
      }>;
    },
  ) => api.put(`/scorecard/${projectId}/config`, payload),
  // Bottom-boxing status keyed by employee_code — 6-month scorecard average
  // vs same-AM peer group (bottom 25%). Dashboard overlays this on the store
  // list. Employees absent from the map have no snapshot history yet.
  getBottomBoxing: (projectId: number) =>
    api.get(`/scorecard/${projectId}/bottom-boxing`),
  // Nearby stores around a store (nearest-first within radiusKm), each with
  // its deployed employee's bottom-boxing status — the redeployment drill-down.
  // The MisDeployed tab as a spreadsheet, with a swap verdict per misfit employee.
  // POSTs the rows the tab is CURRENTLY showing — misdeployment is derived in the
  // browser from the filtered store set, so sending the ids is what keeps the sheet and
  // the on-screen count in agreement. Returns an xlsx blob.
  // `aonGraceMonths`: the tab's AON grace period (0 / omitted = none).
  exportMisdeployedReshuffle: (
    projectId: number,
    body: { radiusKm: number; aonGraceMonths?: number; items: { storeId: string; kind: 'under' | 'over'; employeeCodes: string[] }[] },
  ) =>
    api.post(`/scorecard/${projectId}/misdeployed-reshuffle-export`, body, { responseType: 'blob' }),
  getRedeployCandidates: (projectId: number, storeId: string, radiusKm: number, periodMonth?: string, employeeCode?: string, gate?: 'scorecard' | 'eligibility') =>
    api.get(`/scorecard/${projectId}/redeploy-candidates`, { params: { storeId, radiusKm, periodMonth, employeeCode, gate } }),
  // Decision context for one employee: master (mobile, reporting manager, DOJ,
  // AON), team size under that manager, and score (selected month + 6-mo avg +
  // quadrant). Drives the redeployment flow's employee cards + month dropdown.
  getEmployeeContext: (projectId: number, employeeCode: string, periodMonth?: string) =>
    api.get(`/scorecard/${projectId}/employee-context/${encodeURIComponent(employeeCode)}`, { params: periodMonth ? { periodMonth } : {} }),
};

// Store Intelligence API — read-only aggregations powering the three-lens
// Dashboard (Financial / Store Performance / People). Lens availability is
// auto-detected server-side from the project's data.
export const storeIntelligenceAPI = {
  // { lenses: {financial,performance,people,scorecard}, periodMonth, stores: { [storeId]: {performance?, people?} } }
  // opts.monthEnd (Dashboard → Deployment only): for a PAST month, stores + people as they
  // stood on its last day instead of today's roster. Every other caller omits it.
  getSummary: (projectId: number, periodMonth?: string, opts?: { monthEnd?: boolean }) =>
    api.get(`/store-intelligence/${projectId}/summary`, {
      params: { ...(periodMonth ? { periodMonth } : {}), ...(opts?.monthEnd ? { asOf: 'month-end' } : {}) },
    }),
  // Per-store drill-down: per-employee signals + 3-month score trend.
  getStoreDetail: (projectId: number, storeId: string, periodMonth?: string) =>
    api.get(`/store-intelligence/${projectId}/store/${storeId}`, { params: periodMonth ? { periodMonth } : {} }),
  // Bottom 3% employees (per-manager 6-mo-avg ranking) + their stores — Dashboard
  // "Bottom Box" KPI + table. { periodMonth, total, storeCount, employees: [...] }
  getBottomBox: (projectId: number, periodMonth?: string, opts?: { monthEnd?: boolean }) =>
    api.get(`/store-intelligence/${projectId}/bottom-box`, {
      params: { ...(periodMonth ? { periodMonth } : {}), ...(opts?.monthEnd ? { asOf: 'month-end' } : {}) },
    }),
  // Exit initiation — records the "termination letter initiated" decision AND
  // undeploys the employee from the store atomically.
  createExitInitiation: (projectId: number, payload: {
    employeeCode: string;
    employeeName?: string | null;
    storeId?: string | null;
    storeName?: string | null;
    reason?: string | null;
    avg6m?: number | null;
    quadrant?: string | null;
    status?: string | null;
  }) => api.post(`/store-intelligence/${projectId}/exit-initiations`, payload),
  getExitInitiations: (projectId: number) =>
    api.get(`/store-intelligence/${projectId}/exit-initiations`),
};

// KPI API — role-based, QUARTER-scoped KPIs (parallel to the Employee Scorecard,
// not part of it). The catalog carries explainer text + config-knob declarations
// + availability reasons, so this page renders them straight from the API rather
// than duplicating any of that client-side.
export const kpiAPI = {
  // Catalog (available + unavailable metrics with reasons), project roles, and
  // the default/current quarter ids. Everything the Configure tab needs to draw
  // the metric picker.
  getCatalog: (projectId: number) => api.get(`/kpi/${projectId}/catalog`),
  // The config VERSION in force for `quarter` (as-of resolution), scoped to a
  // role (omit roleId for the project default). Also returns the list of
  // effective_from versions for the version picker.
  getConfig: (projectId: number, quarter: string, roleId?: string | null) =>
    api.get(`/kpi/${projectId}/config`, { params: { quarter, ...(roleId ? { roleId } : {}) } }),
  // Atomically replace the (project, role, effective_from) config version.
  // effective_from must be an explicit quarter id ('2026-Q4') or exact quarter
  // start — the write path deliberately rejects a mid-quarter date so an empty
  // metrics array can't silently wipe the wrong version.
  saveConfig: (
    projectId: number,
    payload: {
      role_id?: string | null;
      effective_from: string;
      // true makes this version the COMPLETE set from effective_from on: the API
      // retires any metric in force but not listed (reported back as excluded_count).
      // Omitted/false keeps the previous merge behaviour.
      full_set?: boolean;
      metrics: Array<{
        metric_def_id: number;
        weight_pct: number;
        display_order?: number;
        status?: 'active' | 'inactive';
        config_params?: Record<string, any> | null;
        slabs: Array<{
          lower_bound: number | null;
          upper_bound: number | null;
          points: number;
          display_label?: string | null;
          display_order?: number;
        }>;
      }>;
    },
  ) => api.put(`/kpi/${projectId}/config`, payload),
  // Computed scores for the quarter, scoped to the viewer's hierarchy. `quarter`
  // is a quarter id ('2026-Q3') or any date inside it. The viewer is derived
  // server-side from the session token — deliberately NOT a parameter here, so a
  // caller cannot request someone else's scope.
  getScores: (projectId: number, quarter: string) =>
    api.get(`/kpi/${projectId}/${quarter}`),
  // The raw records behind one metric's value, for admin verification. Currently
  // infant_attrition_pct only: every exit in the quarter with DOJ/LWD/tenure and
  // the manager it was charged to — INCLUDING exits the score dropped, each with
  // the reason. roleId matters: the engine scores one role's employees at a time,
  // so it decides whether an exit counts.
  getMetricEvidence: (
    projectId: number,
    quarter: string,
    metricCode: string,
    viewerCode: string,
    roleId?: string | null,
  ) =>
    api.get(`/kpi/${projectId}/${quarter}/evidence/${metricCode}`, {
      params: { viewerCode, ...(roleId ? { roleId } : {}) },
    }),
  // The same rows as an XLSX (Blob) — each metric's Value/Detail/Points/Weight/
  // Contribution flattened into columns. Same session-derived hierarchy scoping
  // as getScores.
  downloadScores: (projectId: number, quarter: string, roleId?: string | null) =>
    api.get(`/kpi/${projectId}/${quarter}/download`, {
      params: { ...(roleId ? { roleId } : {}) },
      responseType: 'blob',
    }),
  // One employee's KPI + per-metric breakdown. Prefers the nightly snapshot;
  // pass live=1 to force a recompute after a config change.
  getEmployee: (projectId: number, quarter: string, employeeCode: string, live = false) =>
    api.get(`/kpi/${projectId}/${quarter}/employee/${encodeURIComponent(employeeCode)}`, {
      params: { ...(live ? { live: 1 } : {}) },
    }),
  // Create / edit a project-scoped manual metric (the "any name, uploaded value"
  // case, e.g. Store Front Feedback).
  createManualMetric: (
    projectId: number,
    payload:
      | { name: string; unit?: string | null; higher_is_better?: boolean; upload_grain?: 'quarter' | 'month'; aggregation?: 'avg' | 'sum' | 'latest' }
      // A metric measured from a custom field: the share of a manager's team (or stores)
      // whose value moved up in the quarter. ladder = a dropdown's levels, lowest first.
      | { kind: 'custom_field'; name: string; scope: 'employee' | 'store'; field_id: string; measure: 'moved_up'; ladder: string[] | null },
  ) => api.post(`/kpi/${projectId}/metric-defs`, payload),
  updateManualMetric: (
    projectId: number,
    metricDefId: number,
    payload: Partial<{ name: string; unit: string | null; higher_is_better: boolean; upload_grain: 'quarter' | 'month'; aggregation: 'avg' | 'sum' | 'latest'; status: 'active' | 'inactive' }>,
  ) => api.put(`/kpi/${projectId}/metric-defs/${metricDefId}`, payload),
  // Manual-value grid for a metric+quarter: one row per eligible employee per
  // upload period (blank where nothing entered yet).
  listManualValues: (projectId: number, metricDefId: number, quarter: string) =>
    api.get(`/kpi/${projectId}/manual-values`, { params: { metricDefId, quarter } }),
  // Inline-edit save. A null value CLEARS the row back to "not uploaded".
  saveManualValues: (
    projectId: number,
    metricDefId: number,
    quarter: string,
    values: Array<{ employee_code: string; period_start: string; value: number | null; note?: string | null }>,
  ) => api.put(`/kpi/${projectId}/manual-values`, { metricDefId, quarter, values }),
  // XLSX template pre-filled with the eligible roster (Blob).
  downloadManualTemplate: (projectId: number, metricDefId: number, quarter: string) =>
    api.get(`/kpi/${projectId}/manual-values/template`, { params: { metricDefId, quarter }, responseType: 'blob' }),
  // base64-in-JSON bulk upload (matches every other spreadsheet upload here).
  // Returns { inserted, updated, skipped_blank, rejected_count, rejected: [...] }.
  bulkUploadManualValues: (projectId: number, metricDefId: number, quarter: string, base64: string) =>
    api.post(`/kpi/${projectId}/manual-values/bulk-upload`, { metricDefId, quarter, data: base64 }),
};

// Apple Employee Scorecard API — gated to project_id=170 on the backend.
export const appleScorecardAPI = {
  getScorecard: (projectId: number, periodMonth: string) =>
    api.get('/apple-scorecard', { params: { projectId, period_month: periodMonth } }),
  getSlabs: (projectId: number) =>
    api.get('/apple-scorecard/slabs', { params: { projectId } }),
  // Seed score review surface — lists every employee in the project with their
  // photo submission + OCR-extracted value for the selected month.
  getSeedScoreSubmissions: (projectId: number, periodMonth: string) =>
    api.get('/apple-scorecard/seed-score-submissions', { params: { projectId, period_month: periodMonth } }),
  // 2×2 quadrant classification (Stars / Achievers / Strivers / At Risk) per
  // employee, computed against AON peer-cohort medians for B + TA scores.
  getEmployeeQuadrants: (projectId: number, periodMonth: string) =>
    api.get('/apple-scorecard/employee-quadrants', { params: { projectId, period_month: periodMonth } }),
  listWarnings: (projectId: number, periodMonth: string) =>
    api.get('/apple-scorecard/warnings', { params: { projectId, period_month: periodMonth } }),
  upsertWarning: (projectId: number, data: { employee_code: string; period_month: string; warning_count: number; notes?: string }) =>
    api.post('/apple-scorecard/warnings', { project_id: projectId, ...data }),
  bulkUploadWarnings: (projectId: number, base64Data: string) =>
    api.post('/apple-scorecard/warnings/bulk-upload', { project_id: projectId, data: base64Data }),
  downloadWarningsTemplate: (projectId: number) =>
    api.get('/apple-scorecard/warnings/bulk-upload/template', { params: { projectId }, responseType: 'blob' }),
  downloadScorecard: (projectId: number, periodMonth: string) =>
    api.get('/apple-scorecard/download', { params: { projectId, period_month: periodMonth }, responseType: 'blob' }),

  // Target Achievement Score — Apple-only score surfaced on the Targets page.
  getTargetAchievementScore: (projectId: number, periodMonth: string) =>
    api.get('/apple-scorecard/target-achievement', { params: { projectId, period_month: periodMonth } }),

  // Trainings — drives the Training Participation parameter on the scorecard.
  // `filter` accepts either a legacy period month string (YYYY-MM) or a filter
  // object: { periodMonth?, startDate?, endDate?, employee? } (dates YYYY-MM-DD).
  listTrainings: (
    projectId: number,
    filter?: string | { periodMonth?: string; startDate?: string; endDate?: string; employee?: string },
  ) => {
    const f = typeof filter === 'string' ? { periodMonth: filter } : (filter || {});
    return api.get('/apple-scorecard/trainings', {
      params: {
        projectId,
        ...(f.periodMonth ? { period_month: f.periodMonth } : {}),
        ...(f.startDate ? { start_date: f.startDate } : {}),
        ...(f.endDate ? { end_date: f.endDate } : {}),
        ...(f.employee ? { employee: f.employee } : {}),
      },
    });
  },
  // Assignable employees enriched with city / area manager / store category / store trade.
  listTrainingCandidates: (projectId: number) =>
    api.get('/apple-scorecard/training-candidates', { params: { projectId } }),
  createTraining: (
    projectId: number,
    data: { name: string; training_datetime: string; training_type: 'in_person' | 'web_based'; description?: string; registration_url?: string; duration_minutes?: number | null; employee_codes?: string[] }
  ) => api.post('/apple-scorecard/trainings', { project_id: projectId, ...data }),
  updateTraining: (
    projectId: number,
    id: string,
    data: { name?: string; training_datetime?: string; training_type?: 'in_person' | 'web_based'; description?: string; registration_url?: string; duration_minutes?: number | null; status?: 'active' | 'cancelled'; employee_codes?: string[] }
  ) => api.put(`/apple-scorecard/trainings/${id}`, { project_id: projectId, ...data }),
  deleteTraining: (projectId: number, id: string, hard?: boolean) =>
    api.delete(`/apple-scorecard/trainings/${id}`, { params: { projectId, ...(hard ? { hard: 'true' } : {}) } }),
  // Sign-out check: On = people not marked Registered in time cannot end their day; Off = this
  // training blocks nobody. Needs the Offline Trainings tick (checked on the server).
  setTrainingSignoutCheck: (projectId: number, id: string, on: boolean) =>
    api.put(`/apple-scorecard/trainings/${id}/signout-check`, { on }, { params: { projectId } }),
  listAttendees: (projectId: number, id: string) =>
    api.get(`/apple-scorecard/trainings/${id}/attendees`, { params: { projectId } }),
  // Download all trainings + their assignees as one XLSX. Mirrors listTrainings'
  // filters (date range, employee) plus an optional name/URL match, so the file
  // matches the visible Trainings tab.
  exportTrainings: (
    projectId: number,
    filter?: { startDate?: string; endDate?: string; employee?: string; name?: string },
  ) => {
    const f = filter || {};
    return api.get('/apple-scorecard/trainings/export', {
      params: {
        projectId,
        ...(f.startDate ? { start_date: f.startDate } : {}),
        ...(f.endDate ? { end_date: f.endDate } : {}),
        ...(f.employee ? { employee: f.employee } : {}),
        ...(f.name ? { name: f.name } : {}),
      },
      responseType: 'blob',
    });
  },
  // Remove a candidate from a training — backend allows it only while attendance
  // AND registration are both still unmarked (else 409).
  removeAttendee: (projectId: number, id: string, employeeCode: string) =>
    api.delete(`/apple-scorecard/trainings/${id}/attendees/${encodeURIComponent(employeeCode)}`, { params: { projectId } }),
  upsertAttendance: (projectId: number, id: string, data: { employee_code: string; attended: boolean }) =>
    api.post(`/apple-scorecard/trainings/${id}/attendance`, { project_id: projectId, ...data }),
  bulkUploadAttendance: (projectId: number, base64Data: string) =>
    api.post('/apple-scorecard/trainings/attendance/bulk-upload', { project_id: projectId, data: base64Data }),
  downloadAttendanceTemplate: (projectId: number) =>
    api.get('/apple-scorecard/trainings/attendance/bulk-upload/template', { params: { projectId }, responseType: 'blob' }),
  // Admin-marked registration (Yes/No), single + bulk, mirroring attendance.
  upsertRegistration: (projectId: number, id: string, data: { employee_code: string; registered: boolean }) =>
    api.post(`/apple-scorecard/trainings/${id}/registration`, { project_id: projectId, ...data }),
  bulkUploadRegistration: (projectId: number, base64Data: string) =>
    api.post('/apple-scorecard/trainings/registration/bulk-upload', { project_id: projectId, data: base64Data }),
  downloadRegistrationTemplate: (projectId: number) =>
    api.get('/apple-scorecard/trainings/registration/bulk-upload/template', { params: { projectId }, responseType: 'blob' }),
  // Bulk-create trainings from an uploaded file (one row per training+employee).
  bulkUploadTrainings: (projectId: number, base64Data: string) =>
    api.post('/apple-scorecard/trainings/bulk-upload', { project_id: projectId, data: base64Data }),
  downloadTrainingsTemplate: (projectId: number) =>
    api.get('/apple-scorecard/trainings/bulk-upload/template', { params: { projectId }, responseType: 'blob' }),
  // Download the rows a bulk upload rejected as xlsx. Stateless — post back the
  // same `errors` array the upload response returned; the server resolves the
  // training names and streams the file.
  exportBulkUploadErrors: (
    projectId: number,
    kind: 'attendance' | 'registration' | 'trainings',
    errors: unknown[],
  ) =>
    api.post(
      '/apple-scorecard/trainings/bulk-upload/errors/export',
      { project_id: projectId, kind, errors },
      { responseType: 'blob' },
    ),
};

// Market Regularization Reasons API
export const marketRegularizationReasonsAPI = {
  getAll: (projectId: number) => api.get('/legacy-attendance/market-regularization/reasons/all', { params: { projectId } }),
  create: (data: any) => api.post('/legacy-attendance/market-regularization/reasons', data),
  update: (id: string, data: any) => api.put(`/legacy-attendance/market-regularization/reasons/${id}`, data),
  delete: (id: string) => api.delete(`/legacy-attendance/market-regularization/reasons/${id}`),
};

// Yellow Flag reason-config API (admin). Reasons are project-scoped picker
// options for the manager store-visit Yellow Flag. Admin routes return INACTIVE
// reasons too. Envelope: { success, data }.
export const yellowFlagAPI = {
  getReasons: (projectId: number) =>
    api.get('/yellow-flags/admin/reasons', { params: { projectId } }),
  createReason: (projectId: number, data: { label: string; reasonCode?: string; sortOrder?: number }) =>
    api.post('/yellow-flags/admin/reasons', { projectId, ...data }),
  updateReason: (id: number | string, data: { label?: string; active?: boolean; sortOrder?: number }) =>
    api.put(`/yellow-flags/admin/reasons/${id}`, data),
  deleteReason: (id: number | string) => api.delete(`/yellow-flags/admin/reasons/${id}`),
};

// ─── Red Flag Rules (Master Data → Red Flag Rules) ──────────────────────────
// Per-project red-flag rule sets: which parameters raise red flags, on what
// logic, and for whom. Rules are set ROLE BY ROLE (each role its own on/off and N;
// everything else about a parameter is one setting shared by every role); months
// saved before that were set by audience and are read back projected role by role.
// Rule sets are MONTHLY snapshots; edits target a future
// month (the current month is read-only apart from first-time setup, so red flags
// already raised never change). Every call is gated server-side by the per-project
// `web.red_flag_rules` capability (super admins always pass).
// Envelope: { success, data } / { success: false, message }.

export type RedFlagTriggerMode = 'immediate' | 'every_nth' | 'once_at_nth';
export type RedFlagConditionOp = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq';
export type RedFlagRuleKind = 'system' | 'custom';
export type RedFlagRuleSetSource = 'saved' | 'default' | 'none' | 'unavailable';

export interface RedFlagRuleCondition {
  op: RedFlagConditionOp;
  value: number;
}

/** One of the six standard parameters, with its built-in defaults. */
export interface RedFlagSystemRuleSpec {
  code: string;
  label: string;
  description: string;
  category: string;
  unitNoun: string;
  defaultMode: RedFlagTriggerMode;
  defaultThreshold: number;
  weighting: 'per_day' | 'value';
  defaultJoinDayExempt: boolean;
  defaultAppealable: boolean;
  defaultInstanceAppealable: boolean;
}

/** One tuning knob a custom-parameter metric exposes (rendered generically). */
export interface RedFlagMetricParamSpec {
  key: string;
  label: string;
  type: 'enum' | 'boolean' | 'number';
  options?: Array<{ value: string; label: string }>;
  default: string | number | boolean | null;
  help: string;
  min?: number | null;
  max?: number | null;
  required?: boolean;
  dynamicOptions?: boolean;
  /** Dynamic choices could not be loaded right now (options is then empty). */
  optionsUnavailable?: boolean;
}

/** A metric a custom parameter can be built on. */
export interface RedFlagMetricSpec {
  key: string;
  name: string;
  category: string;
  description: string;
  unitNoun: string;
  levelHint: 'frontline' | 'manager' | 'any';
  valueType: 'event' | 'value';
  valueUnit?: string;
  conditionOps?: RedFlagConditionOp[];
  defaultCondition?: RedFlagRuleCondition | null;
  defaultTrigger: { mode: RedFlagTriggerMode; threshold: number };
  defaultJoinDayExempt: boolean;
  defaultNotifyEmployee?: boolean;
  defaultAppealable?: boolean;
  defaultCountsTowardExit?: boolean;
  evaluation: 'recompute' | 'nightly';
  includesToday: boolean;
  settleDays: number;
  /** role_feature_mapping keys a role must hold for this data to exist. */
  featureKeys?: string[];
  params: RedFlagMetricParamSpec[];
  availability: { available: boolean; reason?: string };
}

export interface RedFlagRoleOption {
  id: string;
  name: string;
  category: string;
  categoryLabel: string;
  /** Active roster holders of the role; null = the roster could not be read right now. */
  memberCount: number | null;
  managesPeopleCount: number | null;
  featureKeys: string[];
  /** 1 = store level, 2, 3 … (from the role's category; blank or odd = 2). */
  level?: number;
  /**
   * People whose GOVERNING role this is (their highest-level role), i.e. who follow this
   * role's rules; with two governing roles at one level a person counts under each.
   * null = the roster could not be read right now.
   */
  followerCount?: number | null;
}

export interface RedFlagRulesLimits {
  maxRules: number;
  maxEnabledCustomRules: number;
  maxActiveCustomRules: number;
  maxTargetRoles: number;
  maxMonthsAhead: number;
  /** Most roles one save may list. */
  maxRolesPerSet?: number;
  /** Most role lines (role × parameter) one save may carry. */
  maxRoleLines?: number;
}

export interface RedFlagRulesCatalog {
  systemRules: RedFlagSystemRuleSpec[];
  metrics: RedFlagMetricSpec[];
  categories: Array<{ key: string; label: string }>;
  roles: RedFlagRoleOption[];
  /** YYYY-MM (IST). */
  currentMonth: string;
  /** false = custom parameters are switched off on this server: none of them raise red flags. */
  customRulesEnabled?: boolean;
  limits?: RedFlagRulesLimits;
  /** Roster members holding no role in the project (the "No role" line); null = roster unreadable. */
  noRoleCount?: number | null;
}

/** One rule as configured for a month. */
export interface RedFlagRuleView {
  code: string;
  kind: RedFlagRuleKind;
  metricKey: string;
  label: string;
  description: string;
  category: string;
  unitNoun: string;
  enabled: boolean;
  mode: RedFlagTriggerMode;
  threshold: number;
  joinDayExempt: boolean;
  appealable: boolean;
  instanceAppealable: boolean;
  notifyEmployee: boolean;
  appliesToFrontline: boolean;
  appliesToPeopleManagers: boolean;
  targetRoleIds: string[];
  countsTowardExit: boolean;
  metricParams: Record<string, unknown> | null;
  condition: RedFlagRuleCondition | null;
  displayOrder: number;
  customRuleId?: number;
  archived?: boolean;
}

/** What PUT /config accepts per rule: the view minus its display-only fields. */
export type RedFlagRuleInput = Omit<
  RedFlagRuleView,
  'label' | 'description' | 'category' | 'unitNoun' | 'kind' | 'metricKey' | 'customRuleId' | 'archived'
>;

export interface RedFlagCustomRuleSummary {
  id: number;
  code: string;
  name: string;
  metricKey: string;
  archived: boolean;
}

export interface RedFlagRuleSetVersion {
  effectiveFrom: string;
  /** Saved role by role (the page's rules per role), not by audience. */
  perRole?: boolean;
  /** YYYY-MM-DD when this version starts part-way through its month (a project's first only). */
  startsOn?: string | null;
  setId: number;
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  enabledCount: number;
  backfillStatus: 'pending' | 'running' | 'done' | 'failed' | 'not_needed' | null;
}

/** One role's setting of one parameter, as GET /config returns it. */
export interface RedFlagRoleRuleLine {
  code: string;
  enabled: boolean;
  threshold: number;
}

/** A role's lines. `roleId` is a project role id, or the "No role" id (all zeros). */
export interface RedFlagRoleRules {
  roleId: string;
  rules: RedFlagRoleRuleLine[];
  /**
   * true only for a role a month saved per role still stores that no longer exists in the
   * project; every other entry is a live role. Older servers omit it (the page then calls
   * a role removed when the catalog does not list it).
   */
  removed?: boolean;
}

/** What PUT /config and POST /preview accept per role (a parameter left out = off for it). */
export interface RedFlagRoleRulesInput {
  roleId: string;
  rules: Array<{ code: string; enabled: boolean; threshold?: number }>;
}

/** A reference the server dropped from a disabled rule because it no longer exists. */
export type RedFlagStaleRef =
  | string
  | { code?: string; kind?: string; field?: string; value?: string; ref?: string; reason?: string; message?: string };

export interface RedFlagRulesConfig {
  /** YYYY-MM the rules were resolved for. */
  month: string;
  source: RedFlagRuleSetSource;
  stale: boolean;
  /** YYYY-MM(-01) of the saved snapshot in force; null unless source === 'saved'. */
  effectiveFrom: string | null;
  /**
   * YYYY-MM-DD the project's red flags start on, when that is not before this month's
   * 1st: the saved rules' own start day, or (source 'none') the day the first rules start.
   */
  startsOn?: string | null;
  /** Today on the server (IST), YYYY-MM-DD: the earliest start date that can be chosen. */
  today?: string;
  /** Saving this month would save the project's first rules, so a start date can be chosen. */
  canSetStartDate?: boolean;
  setId: number | null;
  version: number | null;
  /** The snapshot saved for exactly this month (echo it on PUT), or null. */
  baseVersion: { setId: number; version: number } | null;
  rules: RedFlagRuleView[];
  customRules: RedFlagCustomRuleSummary[];
  versions: RedFlagRuleSetVersion[];
  isLegacyProject: boolean;
  currentMonth: string;
  canEditMonth: boolean;
  reason?: string;
  staleRefs?: RedFlagStaleRef[];
  /** Shape of the month's resolved set: 'per_role', or null for audience / default / none. */
  rulesShape?: 'per_role' | null;
  /**
   * Every project role's lines, plus "No role", plus any stored role that no longer exists.
   * For an audience / default month: who those rules cover, projected role by role.
   */
  roleRules?: RedFlagRoleRules[];
  /** roleRules is a projection of a month set before rules per role. */
  roleRulesProjected?: boolean;
}

export interface RedFlagSaveConfigPayload {
  /** YYYY-MM. */
  effectiveFrom: string;
  /** YYYY-MM-DD inside that month, today or later. Only for a project's first rules. */
  startsOn?: string | null;
  baseVersion: { setId: number; version: number } | null;
  note?: string;
  /** Each parameter's settings; with `roles`, only the settings shared by every role count. */
  rules: RedFlagRuleInput[];
  /** Present (even empty) = saved role by role: every role's on/off and N. */
  roles?: RedFlagRoleRulesInput[];
}

export interface RedFlagSaveConfigResult {
  effectiveFrom: string;
  startsOn?: string | null;
  setId: number;
  version: number;
  ruleCount: number;
  enabledCount: number;
  laterVersions: Array<{ effectiveFrom: string; setId: number }>;
  backfill: 'pending' | 'not_needed';
  staleRefs?: RedFlagStaleRef[];
}

/** Month-to-date impact of one rule in a draft. null counts = fewer than 5 (suppressed). */
export interface RedFlagPreviewRule {
  code: string;
  label: string;
  kind: RedFlagRuleKind;
  enabled: boolean;
  subjects: number | null;
  subjectsFrontline: number | null;
  subjectsManagers: number | null;
  withoutManager: number | null;
  occurrences: number | null;
  flags: number | null;
  employeesFlagged: number | null;
  failed: boolean;
  evaluatedNightly: boolean;
  note?: string;
  /** This rule's counts are hidden from the viewer (small group, or a sensitive parameter). */
  suppressed?: boolean;
}

export interface RedFlagPreview {
  projectId: number;
  month: string;
  from: string;
  to: string;
  generatedAt: string;
  partial: boolean;
  rules: RedFlagPreviewRule[];
  totals: { flags: number | null; employeesFlagged: number | null };
  /** Some counts are hidden (then both totals are null too). */
  suppressed?: boolean;
  cached?: boolean;
}

/** An entitlement probe: a refusal means "no access", never "session over". */
type RedFlagRulesProbeConfig = NonNullable<Parameters<typeof api.get>[1]> & { skipSessionExpiry: true };
const redFlagRulesProbeConfig: RedFlagRulesProbeConfig = { skipSessionExpiry: true };

export interface RedFlagStartExitSetting {
  minRedFlags: number;
  /** false = the built-in default (nothing saved for the project). */
  saved: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  defaultMinRedFlags: number;
}

export const redFlagRulesAPI = {
  /**
   * Is the feature switched on on this server? Answered to any signed-in caller, even
   * while every other route is dark. Any failure (an older API answers 404) means OFF:
   * the web app then shows none of the feature's controls. Read it through
   * useRedFlagRulesEnabled (components/red-flag-rules), which asks once per page load.
   */
  status: () =>
    api.get<{ success: boolean; data: { enabled: boolean } }>('/red-flag-rules/status', redFlagRulesProbeConfig),
  /**
   * Call FIRST. 403 / canManage:false → the page shows its no-access panel; reason
   * 'unverified' = the caller's identity could not be verified (sign in again).
   */
  access: (projectId: number) =>
    api.get<{ success: boolean; data: { canManage: boolean; reason?: 'unverified' | 'no_capability' } }>(
      `/red-flag-rules/${projectId}/access`,
      redFlagRulesProbeConfig,
    ),
  catalog: (projectId: number) =>
    api.get<{ success: boolean; data: RedFlagRulesCatalog }>(`/red-flag-rules/${projectId}/catalog`),
  /** Start Exit: how many red flags (N or more) put an employee on the worklist. */
  getStartExit: (projectId: number) =>
    api.get<{ success: boolean; data: RedFlagStartExitSetting }>(`/red-flag-rules/${projectId}/start-exit`),
  setStartExit: (projectId: number, minRedFlags: number) =>
    api.put<{ success: boolean; data: RedFlagStartExitSetting }>(`/red-flag-rules/${projectId}/start-exit`, { minRedFlags }),
  /** The rules in force for `month` (YYYY-MM; omit for the current month). */
  getConfig: (projectId: number, month?: string) =>
    api.get<{ success: boolean; data: RedFlagRulesConfig }>(`/red-flag-rules/${projectId}/config`, {
      params: month ? { month } : {},
    }),
  /**
   * Whole-snapshot save. A 409 whose body carries `currentVersion` = someone else saved
   * this month's rules meanwhile; any other 409 (a save in progress, a custom parameter
   * archived meanwhile, …) carries only a message.
   */
  saveConfig: (projectId: number, payload: RedFlagSaveConfigPayload) =>
    api.put<{ success: boolean; data: RedFlagSaveConfigResult }>(`/red-flag-rules/${projectId}/config`, payload),
  createCustomRule: (projectId: number, data: { name: string; metricKey: string }) =>
    api.post<{ success: boolean; data: { id: number; code: string; name: string; metricKey: string } }>(
      `/red-flag-rules/${projectId}/custom-rules`,
      data,
    ),
  /** Rename or archive. Archiving is refused (409) while the rule is on in the current or a future month. */
  updateCustomRule: (projectId: number, id: number, data: { name?: string; archived?: boolean }) =>
    api.patch<{ success: boolean; data: RedFlagCustomRuleSummary }>(
      `/red-flag-rules/${projectId}/custom-rules/${id}`,
      data,
    ),
  /** Month-to-date dry run of a draft. Changes nothing. 429 = another preview is running. */
  preview: (projectId: number, data: { rules: RedFlagRuleInput[]; roles?: RedFlagRoleRulesInput[] }) =>
    api.post<{ success: boolean; data: RedFlagPreview }>(`/red-flag-rules/${projectId}/preview`, data),
};

// ── Red flag warning mails (Settings → Mailer Setup → "Red flag warnings") ────────
// Mails the system sends to an employee when they receive a red flag: one message per
// red-flag number, a per-project on/off switch, and a mail trail. Set up by whoever
// holds Red Flag Rules access for the project. Envelope: { success, data } or
// { success: false, message } — the message is written for the screen.

export interface RedFlagWarningSettings {
  isActive: boolean;
  /** 'YYYY-MM-DD': red flags recorded from this day are mailed. null = never switched on. */
  activeFrom: string | null;
  ccManager: boolean;
  ccAddresses: string[];
  replyTo: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** One message: the mail sent when an employee reaches this red-flag number. */
export interface RedFlagWarningStep {
  id: number;
  flagNumber: number;
  subjectTemplate: string;
  bodyTemplate: string;
  /** Also used for later red flags that have no message of their own. */
  andLater: boolean;
  isActive: boolean;
  /** Worked out by the server, e.g. "3rd and 4th red flags" or "Paused — not sent". */
  sentFor: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface RedFlagWarningStepInput {
  flagNumber: number;
  subjectTemplate: string;
  bodyTemplate: string;
  andLater: boolean;
  isActive: boolean;
}

export interface RedFlagWarningPlaceholder {
  /** Without braces, e.g. 'employee_name'; written into the text as {employee_name}. */
  token: string;
  label: string;
  sample: string;
}

export interface RedFlagWarningParameter {
  code: string;
  label: string;
  /** true = counts toward Start Exit, so a red flag on it is mailed. */
  mailed: boolean;
}

export interface RedFlagWarningConfig {
  settings: RedFlagWarningSettings;
  steps: RedFlagWarningStep[];
  /** Red-flag numbers no message covers, e.g. "1st red flag · 6th red flag onwards"; '' = none. */
  notCovered: string;
  placeholders: RedFlagWarningPlaceholder[];
  startExit: { minRedFlags: number };
  /** This month's switched-on parameters. */
  parameters: RedFlagWarningParameter[];
  rulesState: 'ok' | 'none' | 'unavailable';
  mailConfigured: boolean;
  /** false = stopped by the server's emergency switch. */
  serverEnabled: boolean;
  /**
   * May this caller see the mail trail? The same rule decides who may change who is
   * copied (PUT /settings answers 403 otherwise) and who is shown the counts below.
   */
  canReadTrail: boolean;
  /** All zeros for a caller without `canReadTrail` — the tab does not show the line then. */
  summary30d: { sent: number; notSent: number; failed: number };
  /** 'HH:MM' — when the day's mails start going out. Shown, never edited. */
  sendTime: string;
}

export interface RedFlagWarningDraft {
  flagNumber: number;
  subjectTemplate: string;
  bodyTemplate: string;
}

export type RedFlagWarningTrailState = 'sending' | 'sent' | 'failed' | 'skipped';

/** One row of the mail trail: one red flag, and what happened to its mail. */
export interface RedFlagWarningTrailRow {
  id: number;
  createdAt: string | null;
  sentAt: string | null;
  employeeCode: string;
  employeeName: string | null;
  flagType: string;
  flagLabel: string | null;
  /** 'YYYY-MM-DD' — the day the red flag is about. */
  behaviourDate: string;
  flagReason: string | null;
  storeName: string | null;
  /** The employee's red-flag number the mail was (or would have been) about. */
  flagNumber: number | null;
  stepFlagNumber: number | null;
  state: RedFlagWarningTrailState;
  skipReason: string | null;
  /** Why no mail was sent, in plain words. */
  skipReasonText: string | null;
  /**
   * Why a mail failed, in plain words ("The mail server refused this address", "… — will
   * be tried again"). null unless `state` is 'failed'.
   */
  failReasonText: string | null;
  /** The employee's address. On a failed row the mail did not reach it. */
  toAddress: string | null;
  /** Set on a sent row, and on a failed row whose mail still reached the people copied. */
  subject: string | null;
  /** A counter the server also uses as a "never again" marker — not shown as a number of tries. */
  attempts: number;
  flagToday: 'stands' | 'withdrawn' | 'unknown';
}

export interface RedFlagWarningTrailDetail extends RedFlagWarningTrailRow {
  /**
   * The mail exactly as sent, as plain text. On a FAILED row it is the mail the people
   * copied received: the employee's own address was refused.
   */
  bodyText: string | null;
  /** The copies the mail server accepted. */
  ccAddresses: string[];
}

export interface RedFlagWarningTrailSearch {
  limit?: number;
  beforeId?: number;
  state?: 'sent' | 'failed' | 'skipped';
  /** Employee name or code. Sent in the body, never in the URL. */
  q?: string;
}

export const redFlagWarningMailsAPI = {
  /**
   * Call FIRST. A refusal or canManage:false → the tab shows its no-access panel; reason
   * 'unverified' = the caller's identity could not be verified (sign in again).
   */
  access: (projectId: number) =>
    api.get<{ success: boolean; data: { canManage: boolean; reason?: 'unverified' | 'no_capability' } }>(
      `/red-flag-warning-mails/${projectId}/access`,
      redFlagRulesProbeConfig,
    ),
  getConfig: (projectId: number) =>
    api.get<{ success: boolean; data: RedFlagWarningConfig }>(`/red-flag-warning-mails/${projectId}/config`),
  /** 403 (with a message) for a caller who may not read the mail trail — see `canReadTrail`. */
  saveSettings: (projectId: number, data: { ccManager: boolean; ccAddresses: string[]; replyTo: string | null }) =>
    api.put<{ success: boolean; data: RedFlagWarningSettings }>(`/red-flag-warning-mails/${projectId}/settings`, data),
  /** Switching on is refused (400, plain message) without an active message or without red-flag rules. */
  setActive: (projectId: number, isActive: boolean) =>
    api.put<{ success: boolean; data: RedFlagWarningSettings }>(`/red-flag-warning-mails/${projectId}/active`, { isActive }),
  /** 409 = the red-flag number already has a message. */
  createStep: (projectId: number, data: RedFlagWarningStepInput) =>
    api.post<{ success: boolean; data: RedFlagWarningStep }>(`/red-flag-warning-mails/${projectId}/steps`, data),
  updateStep: (projectId: number, id: number, data: RedFlagWarningStepInput) =>
    api.put<{ success: boolean; data: RedFlagWarningStep }>(`/red-flag-warning-mails/${projectId}/steps/${id}`, data),
  deleteStep: (projectId: number, id: number) =>
    api.delete<{ success: boolean; data: { deleted: boolean } }>(`/red-flag-warning-mails/${projectId}/steps/${id}`),
  /** Suggested wording for a red-flag number. */
  starter: (projectId: number, flagNumber: number) =>
    api.get<{ success: boolean; data: { subjectTemplate: string; bodyTemplate: string; andLater: boolean } }>(
      `/red-flag-warning-mails/${projectId}/starter`,
      { params: { flagNumber } },
    ),
  /** The mail as it would be sent, filled with sample data. Sends nothing. */
  preview: (projectId: number, data: RedFlagWarningDraft) =>
    api.post<{ success: boolean; data: { subject: string; html: string; text: string } }>(
      `/red-flag-warning-mails/${projectId}/preview`,
      data,
    ),
  /** Sends the sample mail to ONE address. Never copies the project's other addresses. */
  testSend: (projectId: number, data: RedFlagWarningDraft & { to: string }) =>
    api.post<{ success: boolean; data: { sentTo: string } }>(`/red-flag-warning-mails/${projectId}/test-send`, data),
  searchLog: (projectId: number, data: RedFlagWarningTrailSearch) =>
    api.post<{ success: boolean; data: { rows: RedFlagWarningTrailRow[]; nextBeforeId: number | null } }>(
      `/red-flag-warning-mails/${projectId}/log/search`,
      data,
    ),
  getLogEntry: (projectId: number, id: number) =>
    api.get<{ success: boolean; data: RedFlagWarningTrailDetail }>(`/red-flag-warning-mails/${projectId}/log/${id}`),
};

// Yellow Flag Report API (admin/backend-team review). Read-only list of ALL
// yellow flags RAISED (by managers, for employees) across the viewer's scope —
// distinct from the appeals API which only surfaces APPEALED flags. Full history
// (open/appealed/cancelled/upheld/escalated). Envelope: { success, data }.
export const yellowFlagReportAPI = {
  // The full list of raised flags + employee/store/reason context (hierarchy-scoped).
  adminListFlags: (params: {
    projectId: number;
    status?: string;
    employeeCode?: string;
    storeCode?: string;
    reasonCode?: string;
    managerCode?: string;
    fromDate?: string;
    toDate?: string;
  }) => api.get('/yellow-flags/admin/list', { params }),
  // Time-limited URL to view a flag's attachment (evidence photo/file).
  getFlagDocument: (id: number | string) =>
    api.get(`/yellow-flags/${id}/document`),
  // Admin action — cancel (void) a raised yellow flag.
  cancelFlag: (id: number | string, note?: string) =>
    api.post(`/yellow-flags/admin/flag/${id}/cancel`, { note }),
  // Decision context (scorecard / attendance / red flags / trainings) for the review modal.
  getEmployeeContext: (params: { projectId: number; employeeCode: string }) =>
    api.get('/yellow-flags/admin/employee-context', { params }),
};

// Yellow Flag Appeals API (admin/backend-team review). Employees appeal a yellow
// flag; the backend team replies in a thread and takes a FINAL action — Cancel
// Yellow Flag (uphold) or Keep it (reject). Mirrors managerFocusAPI's red-flag
// appeals block. Admin reads/actions are ALWAYS available (writes are the ones
// gated server-side). Envelope everywhere: { success, data }.
export const yellowFlagAppealsAPI = {
  // The list: every appeal + employee/store/flag context + status (hierarchy-scoped).
  adminListAppeals: (params: { projectId?: number; status?: string }) =>
    api.get('/yellow-flags/admin/appeals', { params }),
  // Post an interim reply on an appeal (flips pending -> under_review).
  adminReply: (appealId: number, body: string) =>
    api.post(`/yellow-flags/admin/appeals/${appealId}/messages`, { body }),
  // Final actions.
  cancelYellowFlag: (appealId: number, note?: string) =>
    api.post(`/yellow-flags/admin/appeals/${appealId}/cancel`, { ...(note ? { note } : {}) }),
  rejectAppeal: (appealId: number, note?: string) =>
    api.post(`/yellow-flags/admin/appeals/${appealId}/reject`, { ...(note ? { note } : {}) }),
  // Time-limited URL to view the employee's appeal attachment.
  getAppealDocument: (appealId: number) =>
    api.get(`/yellow-flags/admin/appeals/${appealId}/document`),
  // Time-limited URL to view the RAISED FLAG's own attachment (the manager's
  // evidence when raising it). The reviewer is admin / in-scope, so the
  // flag-document endpoint's scope check passes.
  getFlagDocument: (flagId: number) =>
    api.get(`/yellow-flags/${flagId}/document`),
};


// Leave Policy API
export const leavePolicyAPI = {
  getActiveUsers: () => api.get('/leave-policy/active-users'),
  getAllPolicies: () => api.get('/leave-policy/policies'),
  getPolicyLeaveTypes: (policyId: number) => api.get(`/leave-policy/policies/${policyId}/leave-types`),
};

// Grooming Report API
export const groomingReportAPI = {
  getReport: (
    params?: {
      start_date?: string;
      end_date?: string;
      employeeCode?: string;
      search?: string;
      grooming_status?: string;
      face_match?: string;
      role_category?: string;
      /** Store-attribute filters — CSV lists. `states` carries a Region pick,
       *  already expanded into its member states (the server has no region map). */
      trades?: string;
      categories?: string;
      cities?: string;
      states?: string;
      projectId?: number;
      limit?: number;
      offset?: number;
      /** Column sort — the report is server-paginated, so it is sorted by the API. */
      sort_by?: string;
      sort_dir?: 'asc' | 'desc';
    },
    options?: { signal?: AbortSignal },
  ) => api.get('/grooming-analysis/report', { params, signal: options?.signal }),
  compareWithAws: (id: number | string, force = false) =>
    api.post(`/grooming-analysis/${id}/compare-aws`, undefined, {
      params: force ? { force: 1 } : undefined,
    }),
  // Force re-score every grooming record for a project+date range under the
  // project's current prompt (super_admin / project admin). Returns 202 + a
  // { queued, message } payload; the re-scoring runs in the background.
  bulkReanalyze: (params: { projectId: number; startDate?: string; endDate?: string; date?: string; search?: string; roleCategory?: string }) =>
    api.post('/grooming-analysis/admin/reanalyze-grooming-bulk', params),
  // Poll progress of a bulk re-analysis job started by bulkReanalyze.
  reanalyzeProgress: (jobId: string) =>
    api.get(`/grooming-analysis/admin/reanalyze-grooming-progress/${jobId}`),
};

// Mood / Emoji Report API
export const moodReportAPI = {
  getReport: (params?: {
    start_date?: string;
    end_date?: string;
    employeeCode?: string;
    search?: string;
    emoji?: string;
    projectId?: number;
  }) => api.get('/emoji/report', { params }),
};

// Store Visits Report API — flat manager check-in/out sessions for a date range,
// scoped to the caller (admins → whole project; else self + reportee hierarchy).
export const storeVisitsReportAPI = {
  getReport: (params: {
    start_date: string;
    end_date: string;
    employeeCode?: string;
    projectId?: number;
  }) => api.get('/store-visits/report', { params }),
};


// LMS API (Learning Management System)

// A journey is saved as one document: the journey fields plus its whole stage
// tree. Stages/content the payload omits are deleted server-side, so partial
// saves must omit `stages` entirely rather than send a trimmed list.
export interface LmsStageContentPayload {
  id?: string;
  content_type: 'module' | 'session';
  module_id?: string | null;
  session_id?: string | null;
  order_index: number;
  is_required: boolean;
}

export interface LmsStagePayload {
  id?: string;
  title: string;
  description?: string | null;
  order_index: number;
  unlock_condition: 'sequential' | 'score_based';
  required_score?: number | null;
  is_milestone: boolean;
  milestone_name?: string | null;
  milestone_points_bonus?: number;
  /** "Finish this stage within N days"; 0 or null = no stage due date. Left out = keep what is saved. */
  due_within_days?: number | null;
  content: LmsStageContentPayload[];
}

export interface LmsJourneyPayload {
  title: string;
  description?: string | null;
  thumbnail_url?: string | null;
  estimated_hours?: number;
  is_active?: boolean;
  /** 'everyone' = the whole project; 'assigned' = only its assignees. */
  audience?: 'everyone' | 'assigned';
  /** "Complete within N days"; 0 or null = no due date. Left out = keep what is saved. */
  due_within_days?: number | null;
  stages?: LmsStagePayload[];
}

export interface LmsAssignmentInput {
  type: 'employee' | 'role';
  employee_code?: string;
  role_id?: string;
  due_date?: string | null;
  is_mandatory?: boolean;
}

/** One person on Reports → Content Modules, in the list of one module's people. */
export interface LmsModuleLearner {
  employee_code: string;
  employee_name: string | null;
  role_name: string | null;
  /** How the module reached them. 'none' = it was not assigned to them; they opened it themselves. */
  via: 'named' | 'role' | 'everyone' | 'none';
  /** For 'role', the role's name. */
  via_label: string | null;
  state: 'completed' | 'failed' | 'in_progress' | 'not_started';
  /** Not finished, and past this person's due date (India day). */
  overdue: boolean;
  /** Completed, but on a day after the due date. */
  late: boolean;
  score: number | null;
  completed_at: string | null;
  /** 'YYYY-MM-DD': the same date the app shows this person. */
  due_date: string | null;
  time_spent_seconds: number | null;
  points_awarded: number | null;
}

/** Everyone a content module concerns, with where each of them stands. */
export interface LmsModuleLearnersData {
  module: {
    id: string;
    title: string;
    status: string;
    module_type: string;
    has_quiz: boolean;
    pass_score: number | null;
    points_reward: number | null;
    /** "Complete within N days"; null = the module has no rule of its own. */
    due_within_days: number | null;
    due_counted_from: string | null;
    /** Minimum reading time; null = none. */
    min_time_seconds: number | null;
  };
  /**
   * 'everyone' = the module has its own "Complete within N days" rule, so everybody in the project is expected;
   * 'assigned' = no such rule, but it is assigned by name and/or by role;
   * 'open'     = neither: only people who have started or finished it are listed.
   */
  audience: 'assigned' | 'everyone' | 'open';
  /** True when the list was cut short; `total_people` is the number before the cut. */
  capped: boolean;
  total_people: number;
  summary: { total: number; completed: number; failed: number; pending: number; overdue: number; completed_late: number };
  learners: LmsModuleLearner[];
}

/** Per module id: how many people it concerns and where they stand. A module nobody is counted for has no entry. */
export type LmsModuleLearnerCounts = Record<
  string,
  { expected: number; completed: number; failed: number; pending: number; overdue: number }
>;

export const lmsAPI = {
  // Content Modules (Admin)
  getContentModules: (params?: { status?: string; availableInJourney?: boolean; projectId?: number }) =>
    trackProductLoad('lms_content_list', () => api.get('/lms/admin/modules', { params })),
  getModuleById: (id: string, projectId?: number) =>
    api.get(`/lms/admin/modules/${id}`, { params: { projectId } }),
  createModule: (data: {
    title: string;
    description?: string;
    thumbnail_url?: string;
    points_reward?: number;
    pass_score?: number;
    estimated_duration_minutes?: number;
    quiz_time_limit_minutes?: number;
    /** "Complete within N days"; 0 or null = no due date. */
    due_within_days?: number | null;
    /** Minimum reading time in seconds; 0 or null = no minimum. */
    min_time_seconds?: number | null;
    status?: 'draft' | 'published' | 'archived';
    available_in_journey?: boolean;
    slides?: any[];
  }, projectId?: number) =>
    trackProductRequest('lms_content_save', { content_type: 'slides', save_mode: 'create', entry_point: 'lms_editor' },
      () => api.post('/lms/admin/modules', data, { params: { projectId } })),
  updateModule: (id: string, data: {
    title?: string;
    description?: string;
    thumbnail_url?: string;
    points_reward?: number;
    pass_score?: number;
    estimated_duration_minutes?: number;
    quiz_time_limit_minutes?: number;
    /** "Complete within N days"; 0 or null = no due date. */
    due_within_days?: number | null;
    /** Minimum reading time in seconds; 0 or null = no minimum. */
    min_time_seconds?: number | null;
    status?: 'draft' | 'published' | 'archived';
    available_in_journey?: boolean;
    slides?: any[];
  }, projectId?: number) =>
    // This endpoint edits both native modules and SCORM metadata; the payload
    // does not identify the type, so do not label all updates as native slides.
    trackProductRequest('lms_content_save', { save_mode: 'update', entry_point: 'lms_editor' },
      () => api.put(`/lms/admin/modules/${id}`, data, { params: { projectId } })),
  deleteModule: (id: string, projectId?: number) =>
    api.delete(`/lms/admin/modules/${id}`, { params: { projectId } }),
  publishModule: (id: string, projectId?: number) =>
    trackProductRequest('lms_content_publish', { entry_point: 'lms_editor' },
      () => api.put(`/lms/admin/modules/${id}/publish`, {}, { params: { projectId } })),
  unpublishModule: (id: string, projectId?: number) =>
    api.put(`/lms/admin/modules/${id}/unpublish`, {}, { params: { projectId } }),

  // SCORM
  importScormPackage: (file: File, title: string | undefined, projectId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    return trackProductRequest('lms_content_save', { content_type: 'scorm', save_mode: 'create', entry_point: 'lms_editor' },
      () => api.post('/lms/admin/modules/import-scorm', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { projectId },
      }));
  },
  launchScorm: (moduleId: string, projectId?: number) =>
    api.post(`/lms/scorm/${moduleId}/launch`, {}, { params: { projectId } }),

  // Journeys (Admin)
  getJourneys: (params?: { search?: string; projectId?: number }) =>
    api.get('/lms/admin/journeys', { params }),
  getJourney: (id: string, projectId?: number) =>
    api.get(`/lms/admin/journeys/${id}`, { params: { projectId } }),
  createJourney: (data: LmsJourneyPayload, projectId?: number) =>
    trackProductRequest('lms_journey_save', { content_type: 'journey', save_mode: 'create', entry_point: 'lms_editor' },
      () => api.post('/lms/admin/journeys', data, { params: { projectId } })),
  updateJourney: (id: string, data: LmsJourneyPayload, projectId?: number) =>
    trackProductRequest('lms_journey_save', { content_type: 'journey', save_mode: 'update', entry_point: 'lms_editor' },
      () => api.put(`/lms/admin/journeys/${id}`, data, { params: { projectId } })),
  setJourneyActive: (id: string, isActive: boolean, projectId?: number) =>
    api.put(`/lms/admin/journeys/${id}/active`, { is_active: isActive }, { params: { projectId } }),
  deleteJourney: (id: string, projectId?: number) =>
    api.delete(`/lms/admin/journeys/${id}`, { params: { projectId } }),
  getJourneyAssignments: (journeyId: string, projectId?: number) =>
    api.get(`/lms/admin/journeys/${journeyId}/assignments`, { params: { projectId } }),
  createJourneyAssignments: (journeyId: string, assignments: LmsAssignmentInput[], projectId?: number) =>
    api.post(`/lms/admin/journeys/${journeyId}/assignments`, { assignments }, { params: { projectId } }),

  // Sessions (Admin)
  getSessions: (params?: { status?: string; session_type?: string; availableInJourney?: boolean; projectId?: number }) =>
    trackProductLoad('lms_session_list', () => api.get('/lms/admin/sessions', { params })),
  getSession: (id: string, projectId?: number) =>
    api.get(`/lms/admin/sessions/${id}`, { params: { projectId } }),
  createSession: (data: {
    title: string;
    description?: string;
    session_type?: string;
    scheduled_at?: string;
    duration_minutes?: number;
    location?: string;
    venue_name?: string;
    meeting_link?: string;
    // null is meaningful: "no cap". An absent key leaves the stored value
    // alone, which is why clearing the field has to send an explicit null.
    max_participants?: number | null;
    points_reward?: number;
    trainer_name?: string;
    trainer_emp_code?: string;
    has_assessment?: boolean;
    assessment_pass_score?: number;
    available_in_journey?: boolean;
    available_standalone?: boolean;
    audience?: 'everyone' | 'assigned';
  }, projectId?: number) =>
    trackProductRequest('lms_session_save', { content_type: 'session', save_mode: 'create', entry_point: 'lms_editor' },
      () => api.post('/lms/admin/sessions', data, { params: { projectId } })),
  updateSession: (id: string, data: {
    title?: string;
    description?: string;
    session_type?: string;
    scheduled_at?: string;
    duration_minutes?: number;
    location?: string;
    venue_name?: string;
    meeting_link?: string;
    // null is meaningful: "no cap". An absent key leaves the stored value
    // alone, which is why clearing the field has to send an explicit null.
    max_participants?: number | null;
    points_reward?: number;
    trainer_name?: string;
    trainer_emp_code?: string;
    status?: string;
    has_assessment?: boolean;
    assessment_pass_score?: number;
    available_in_journey?: boolean;
    available_standalone?: boolean;
    audience?: 'everyone' | 'assigned';
  }, projectId?: number) =>
    trackProductRequest('lms_session_save', { content_type: 'session', save_mode: 'update', entry_point: 'lms_editor' },
      () => api.put(`/lms/admin/sessions/${id}`, data, { params: { projectId } })),
  cancelSession: (id: string, projectId?: number) =>
    api.post(`/lms/admin/sessions/${id}/cancel`, {}, { params: { projectId } }),
  deleteSession: (id: string, projectId?: number) =>
    api.delete(`/lms/admin/sessions/${id}`, { params: { projectId } }),
  getSessionRegistrations: (id: string, projectId?: number) =>
    api.get(`/lms/admin/sessions/${id}/registrations`, { params: { projectId } }),
  // Everyone a session concerns (assigned and registered) with where each stands.
  getSessionRoster: (id: string, projectId?: number) =>
    api.get(`/lms/admin/sessions/${id}/roster`, { params: { projectId } }),
  // The same for a content module: everyone it concerns, with who has completed it, who has not and who is overdue.
  getModuleLearners: (id: string, projectId?: number) =>
    api.get<{ success: boolean; data: LmsModuleLearnersData }>(`/lms/admin/modules/${id}/learners`, { params: { projectId } }),
  // For Reports → Content Modules, the table of all modules: those counts for every module of the project, in one call.
  getModuleLearnerCounts: (projectId?: number) =>
    api.get<{ success: boolean; data: LmsModuleLearnerCounts }>('/lms/admin/module-learner-counts', { params: { projectId } }),
  updateSessionStatus: (
    id: string,
    status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled',
    projectId?: number,
  ) => api.put(`/lms/admin/sessions/${id}/status`, { status }, { params: { projectId } }),
  getSessionFeedback: (id: string, projectId?: number) =>
    api.get(`/lms/admin/sessions/${id}/feedback`, { params: { projectId } }),
  getSessionAssignments: (id: string, projectId?: number) =>
    api.get(`/lms/admin/sessions/${id}/assignments`, { params: { projectId } }),
  createSessionAssignments: (id: string, assignments: LmsAssignmentInput[], projectId?: number) =>
    api.post(`/lms/admin/sessions/${id}/assignments`, { assignments }, { params: { projectId } }),
  markSessionAttendance: (
    id: string,
    attendees: Array<{
      employee_code: string;
      status: 'attended' | 'absent' | 'excused';
      /** Trainer's rating of this attendee, when the session collects one. */
      assessment_score?: number | null;
      /** Optional 1-5 breakdown behind the headline score. */
      rating_engagement?: number | null;
      rating_understanding?: number | null;
      rating_application?: number | null;
    }>,
    projectId?: number,
  ) => api.post(`/lms/admin/sessions/${id}/attendance`, { attendees }, { params: { projectId } }),

  // Reports (Admin)
  getQuizPerformanceReport: (params?: {
    fromDate?: string;
    toDate?: string;
    moduleIds?: string[];
    status?: string;
    q?: string;
    projectId?: number;
  }) =>
    api.get('/lms/admin/reports/quiz-performance', {
      params: {
        ...params,
        moduleIds: params?.moduleIds?.length ? params.moduleIds.join(',') : undefined,
      },
    }),

  getScormProgressReport: (params?: {
    fromDate?: string;
    toDate?: string;
    moduleIds?: string[];
    status?: string;
    q?: string;
    projectId?: number;
  }) =>
    api.get('/lms/admin/reports/scorm-progress', {
      params: {
        ...params,
        moduleIds: params?.moduleIds?.length ? params.moduleIds.join(',') : undefined,
      },
    }),

  // Full CMI record for a single learner+course. Split from the list endpoint because
  // suspend_data and the raw cmi tree run to tens of KB each.
  getScormProgressDetail: (params: {
    moduleId: string;
    employeeCode: string;
    projectId?: number;
  }) => api.get('/lms/admin/reports/scorm-progress/detail', { params }),

  // Journey progress. Unlike the two module reports this returns an OBJECT, not a
  // row array: a gated journey needs its stage funnel and its headline counts
  // alongside the learner rows, and neither can be derived from the rows the table
  // shows once a status filter has been applied to them.
  getJourneyProgressReport: (params?: {
    journeyId?: string;
    status?: string;
    q?: string;
    projectId?: number;
  }) => api.get('/lms/admin/reports/journey-progress', { params }),

  // Grow → Leaderboard: the mobile Grow tab's points ranking, whole project.
  getLeaderboard: (params: { projectId: number; period: 'monthly' | 'all_time' }) =>
    api.get('/lms/admin/leaderboard', { params }),

  // Module Assignments (Admin)
  getModuleAssignments: (moduleId: string, projectId?: number) =>
    api.get(`/lms/admin/modules/${moduleId}/assignments`, { params: { projectId } }),
  createAssignments: (moduleId: string, assignments: Array<{
    type: 'employee' | 'role';
    employee_code?: string;
    role_id?: string;
    due_date?: string | null;
    is_mandatory?: boolean;
  }>, projectId?: number) =>
    api.post(`/lms/admin/modules/${moduleId}/assignments`, { assignments }, { params: { projectId } }),
  deleteAssignment: (assignmentId: string, projectId?: number) =>
    api.delete(`/lms/admin/assignments/${assignmentId}`, { params: { projectId } }),
  getAssignmentCounts: (moduleIds: string[], projectId?: number) =>
    api.get('/lms/admin/assignment-counts', { params: { moduleIds: moduleIds.join(','), projectId } }),
};

// Mailer Setup API — scheduled email reports configurable from Settings.
export const mailersAPI = {
  list: (projectId: number) => api.get('/mailers', { params: { projectId } }),
  get: (id: number) => api.get(`/mailers/${id}`),
  create: (data: any) => api.post('/mailers', data),
  update: (id: number, data: any) => api.put(`/mailers/${id}`, data),
  remove: (id: number) => api.delete(`/mailers/${id}`),
  setActive: (id: number, isActive: boolean) =>
    api.patch(`/mailers/${id}/active`, { is_active: isActive }),
  duplicate: (id: number) => api.post(`/mailers/${id}/duplicate`),
  preview: (id: number) => api.post(`/mailers/${id}/preview`),
  testSend: (id: number, to: string) => api.post(`/mailers/${id}/test-send`, { to }),
  runNow: (id: number) => api.post(`/mailers/${id}/run-now`),
  history: (id: number, limit?: number) =>
    api.get(`/mailers/${id}/history`, { params: { limit } }),
  // Discovery endpoints that drive the form.
  dataSources: () => api.get('/mailers/data-sources'),
  tasksForProject: (projectId: number) =>
    api.get('/mailers/data-sources/task_responses/tasks', { params: { projectId } }),
  columnsForTasks: (taskIds: string[]) =>
    api.get('/mailers/data-sources/task_responses/columns', {
      params: { taskIds: taskIds.join(',') },
    }),
  customReportsForSource: (key: string, projectId: number, taskIds?: string[]) =>
    api.get(`/mailers/data-sources/${key}/custom-reports`, {
      params: {
        projectId,
        ...(taskIds && taskIds.length > 0 ? { taskIds: taskIds.join(',') } : {}),
      },
    }),
};

export const distributionListsAPI = {
  list: (projectId: number) => api.get('/distribution-lists', { params: { projectId } }),
  get: (id: number) => api.get(`/distribution-lists/${id}`),
  create: (data: any) => api.post('/distribution-lists', data),
  update: (id: number, data: any) => api.put(`/distribution-lists/${id}`, data),
  remove: (id: number) => api.delete(`/distribution-lists/${id}`),
};

// Every dynamic-reports request that reads or writes a report carries the
// caller's active project_id so the backend can enforce per-project
// visibility (a report is only visible in the project it was created in)
// and apply project-scoped CTEs at run time.
export const dynamicReportsAPI = {
  // Asked for the project on screen: the page tick that lets a person write reports with
  // AI is on a role of one project.
  access: (projectId?: number) =>
    api.get('/dynamic-reports/access', { params: projectId ? { project_id: projectId } : {} }),
  list: (params?: {
    status?: 'draft' | 'published';
    source_key?: string;
    project_id?: number;
    context_id?: string;
  }) => api.get('/dynamic-reports', { params }),
  get: (id: number, project_id?: number, context_id?: string) =>
    api.get(`/dynamic-reports/${id}`, {
      params: { ...(project_id ? { project_id } : {}), ...(context_id ? { context_id } : {}) },
    }),
  generate: (prompt: string, source_key: string, project_id: number, context_id?: string) =>
    api.post('/dynamic-reports/generate', {
      prompt,
      source_key,
      project_id,
      ...(context_id ? { context_id } : {}),
    }),
  run: (
    id: number,
    project_id?: number,
    params?: Record<string, string | number | null>,
    context_id?: string
  ) =>
    api.post(
      `/dynamic-reports/${id}/run`,
      params ? { params } : undefined,
      {
        params: { ...(project_id ? { project_id } : {}), ...(context_id ? { context_id } : {}) },
      }
    ),
  // The FULL result set as an .xlsx blob, with the same params the preview ran
  // with so the file matches what is on screen. responseType 'blob' is what keeps
  // this viable: the rows never become JavaScript objects, so a month of a
  // per-day report downloads instead of crashing the tab.
  exportXlsx: (
    id: number,
    project_id?: number,
    params?: Record<string, string | number | null>,
    context_id?: string
  ) =>
    api.post(
      `/dynamic-reports/${id}/export`,
      params ? { params } : undefined,
      {
        params: { ...(project_id ? { project_id } : {}), ...(context_id ? { context_id } : {}) },
        responseType: 'blob',
        // A month can take most of the server's 60s statement timeout, so this
        // must outlast it — otherwise the client gives up on a query that is
        // still about to succeed.
        timeout: 180_000,
      }
    ),
  modify: (id: number, prompt: string) =>
    api.post(`/dynamic-reports/${id}/modify`, { prompt }),
  messages: (id: number, project_id?: number, context_id?: string) =>
    api.get(`/dynamic-reports/${id}/messages`, {
      params: { ...(project_id ? { project_id } : {}), ...(context_id ? { context_id } : {}) },
    }),
  update: (
    id: number,
    body: Partial<{ title: string; description: string; chart_config: unknown; generated_sql: string }>
  ) => api.patch(`/dynamic-reports/${id}`, body),
  publish: (id: number) => api.post(`/dynamic-reports/${id}/publish`),
  unpublish: (id: number) => api.post(`/dynamic-reports/${id}/unpublish`),
  remove: (id: number) => api.delete(`/dynamic-reports/${id}`),
};

// Custom dashboards — prompt-built dashboards that appear as their own tabs on the
// Dashboard page. A dashboard is an ordered list of blocks (KPI cards / table /
// chart / text), each backed by its own SELECT. `project_id` is always sent, but it
// is a consistency check, not the authorization boundary: the server resolves the
// owning project from the persisted row and 404s on a mismatch.
/** A custom dashboard's Filters window, as the run endpoint reads it. Absent lists mean no filter. */
export interface CustomDashboardRunFilters {
  trades?: string[];
  states?: string[];
  cities?: string[];
  /** Staffing statuses; '__not_set__' for a store with none. */
  staffingStatuses?: string[];
  amCodes?: string[];
  thCodes?: string[];
}

export const customDashboardsAPI = {
  access: (project_id: number) =>
    api.get('/custom-dashboards/access', { params: { project_id } }),
  // Starter prompts built from the project's own vocabulary.
  suggestions: (project_id: number) =>
    api.get('/custom-dashboards/suggestions', { params: { project_id } }),
  list: (project_id: number, status?: 'draft' | 'published') =>
    api.get('/custom-dashboards', { params: { project_id, ...(status ? { status } : {}) } }),
  get: (id: number) => api.get(`/custom-dashboards/${id}`),
  // `clarifications` carries answers to questions a previous attempt asked. The
  // response is either { dashboard } or { status: 'needs_clarification', questions }.
  generate: (
    prompt: string,
    project_id: number,
    clarifications: Array<{ question: string; answer: string }> = []
  ) => api.post('/custom-dashboards/generate', { prompt, project_id, clarifications }),
  // `params` are the filter-bar values; `fresh` bypasses the 90s per-block cache;
  // `filters` are the page's Filters window (narrow the stores and people every block reads).
  run: (
    id: number,
    body?: { params?: Record<string, string | number | null>; fresh?: boolean; filters?: CustomDashboardRunFilters }
  ) => api.post(`/custom-dashboards/${id}/run`, body ?? {}),
  update: (
    id: number,
    body: Partial<{
      title: string;
      description: string;
      layout: { columns: 1 | 2 };
      audience_role_ids: string[];
    }>
  ) => api.patch(`/custom-dashboards/${id}`, body),
  publish: (id: number) => api.post(`/custom-dashboards/${id}/publish`),
  unpublish: (id: number) => api.post(`/custom-dashboards/${id}/unpublish`),
  remove: (id: number) => api.delete(`/custom-dashboards/${id}`),
  // Adds a block to an existing dashboard from a prompt.
  generateBlock: (
    id: number,
    body: {
      prompt: string; type?: string; chart_hint?: string; width?: 'full' | 'half'; title?: string;
      clarifications?: Array<{ question: string; answer: string }>;
    }
  ) => api.post(`/custom-dashboards/${id}/blocks/generate`, body),
  // Rewrites ONE block from a change request, replaying that block's own thread.
  // Response is either { block } or { status: 'needs_clarification', questions }.
  refineBlock: (
    id: number,
    blockId: number,
    prompt: string,
    clarifications: Array<{ question: string; answer: string }> = []
  ) => api.post(`/custom-dashboards/${id}/blocks/${blockId}/refine`, { prompt, clarifications }),
  messages: (id: number, block_id?: number) =>
    api.get(`/custom-dashboards/${id}/messages`, { params: block_id ? { block_id } : {} }),
  // Deterministic edits — no model call, so a chart-type switch is instant.
  updateBlock: (
    id: number,
    blockId: number,
    body: Partial<{ title: string; subtitle: string; width: 'full' | 'half'; config: unknown }>
  ) => api.patch(`/custom-dashboards/${id}/blocks/${blockId}`, body),
  removeBlock: (id: number, blockId: number) =>
    api.delete(`/custom-dashboards/${id}/blocks/${blockId}`),
  reorderBlocks: (id: number, order: number[]) =>
    api.post(`/custom-dashboards/${id}/blocks/reorder`, { order }),
};


// Manager / Employee Daily Focus — verification (super-admin only). Returns the
// persisted snapshot side-by-side with a live re-run trace + parity diff.
export const managerFocusAPI = {
  getDebug: (
    mode: 'manager' | 'employee',
    employeeCode: string,
    projectId?: number,
    // The window the table is scoped to. `month` (YYYY-MM) is still accepted by the
    // API for older callers; the page now always sends an explicit from/to range.
    window?: { from: string; to: string } | { month: string },
  ) =>
    api.get('/manager-focus/debug', {
      params: {
        mode,
        employeeCode,
        ...(projectId != null ? { projectId } : {}),
        ...(window ?? {}),
      },
    }),
  // Managers of a project — picker source for the verification screen's
  // "Manager (team)" mode (distinct manager_code from the scorecard roster).
  getManagers: (projectId?: number) =>
    api.get('/manager-focus/managers', { params: projectId != null ? { projectId } : {} }),
  // Members of a role — picker source for the Red Flags role cascade. `parentCode`
  // restricts to that manager's reporting subtree ("AMs under this Trade Head").
  getRoleMembers: (projectId: number, roleId: string, parentCode?: string) =>
    api.get('/manager-focus/role-members', {
      params: { projectId, roleId, ...(parentCode ? { parentCode } : {}) },
    }),
  // Direct reports of a manager (the cascade's drill-down levels below the seed role).
  // parentCode is comma-separated for a multi-select union.
  getDirectReports: (projectId: number, parentCode: string) =>
    api.get('/manager-focus/direct-reports', { params: { projectId, parentCode } }),
  // Same trace as getDebug, but scoped by a ROLE (all its holders) and/or a manager's
  // reporting SUBTREE instead of a single code — the role cascade's terminal query.
  getDebugScoped: (params: {
    projectId?: number;
    month?: string;   // legacy whole-month scope
    from?: string;    // YYYY-MM-DD window start (preferred)
    to?: string;      // YYYY-MM-DD window end
    allowFromRole?: string;
    allowFromSubtree?: string;
  }) =>
    api.get('/manager-focus/debug', {
      params: {
        ...(params.projectId != null ? { projectId: params.projectId } : {}),
        ...(params.month ? { month: params.month } : {}),
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
        ...(params.allowFromRole ? { allowFromRole: params.allowFromRole } : {}),
        ...(params.allowFromSubtree ? { allowFromSubtree: params.allowFromSubtree } : {}),
      },
    }),
  // Fast per-employee red-flag breakdown (counts + dates per parameter + appeals) for
  // the selected scope + month — powers the Red Flags page CSV download. Scopes exactly
  // like getDebug/getDebugScoped (single employee / role / manager subtree).
  getRedFlagExport: (params: {
    projectId?: number;
    month?: string;   // legacy whole-month scope
    from?: string;    // YYYY-MM-DD window start (preferred)
    to?: string;      // YYYY-MM-DD window end
    mode?: 'employee';
    employeeCode?: string;
    allowFromRole?: string;
    allowFromSubtree?: string;
  }) =>
    api.get('/manager-focus/red-flags-export', {
      params: {
        ...(params.projectId != null ? { projectId: params.projectId } : {}),
        ...(params.month ? { month: params.month } : {}),
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
        ...(params.mode ? { mode: params.mode } : {}),
        ...(params.employeeCode ? { employeeCode: params.employeeCode } : {}),
        ...(params.allowFromRole ? { allowFromRole: params.allowFromRole } : {}),
        ...(params.allowFromSubtree ? { allowFromSubtree: params.allowFromSubtree } : {}),
      },
    }),
  // Per-parameter drill-down for ONE employee + month — the data behind a clicked
  // red-flag cell (grooming captures with image+score / training sessions / flagged
  // dates). Only called for grooming, missed_training, uninformed_absence; the
  // date-based gated params are rendered from the table payload client-side.
  getRedFlagDetail: (params: {
    projectId: number;
    employeeCode: string;
    flagType: string;
    month?: string;   // legacy whole-month scope
    from?: string;    // YYYY-MM-DD window start (preferred)
    to?: string;      // YYYY-MM-DD window end
  }) =>
    api.get('/manager-focus/red-flag-detail', {
      params: {
        projectId: params.projectId,
        employeeCode: params.employeeCode,
        flagType: params.flagType,
        ...(params.month ? { month: params.month } : {}),
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
      },
    }),
  // Per-employee red-flag lifecycle (raised → notified → discussed) + TAT — the
  // audit modal opened from the "Red Flags" count on the verification screen.
  // Omit `window` entirely to get the full retained history.
  getFlagTimeline: (
    employeeCode: string,
    projectId?: number,
    window?: { from: string; to: string } | { month: string },
  ) =>
    api.get('/manager-focus/flag-timeline', {
      params: {
        employeeCode,
        ...(projectId != null ? { projectId } : {}),
        ...(window ?? {}),
      },
    }),
  // One-time (idempotent) backfill of the current year's red-flag history.
  runBackfill: (projectId?: number) =>
    api.post('/manager-focus/backfill', null, { params: projectId != null ? { projectId } : {} }),

  // ── Appeals (admin review) ──────────────────────────────────────────────
  // Appeals an employee has raised (per employee / project / status).
  getAppeals: (params: { employeeCode?: string; projectId?: number; status?: string }) =>
    api.get('/manager-focus/appeals', { params }),
  // Post an interim reply on an appeal (before the final decision).
  appealReply: (appealId: number, body: string, actorName?: string) =>
    api.post(`/manager-focus/appeals/${appealId}/messages`, { body, ...(actorName ? { actorName } : {}) }),
  // Final actions.
  cancelRedFlag: (appealId: number, note?: string, actorName?: string) =>
    api.post(`/manager-focus/appeals/${appealId}/cancel`, { ...(note ? { note } : {}), ...(actorName ? { actorName } : {}) }),
  rejectAppeal: (appealId: number, note?: string, actorName?: string) =>
    api.post(`/manager-focus/appeals/${appealId}/reject`, { ...(note ? { note } : {}), ...(actorName ? { actorName } : {}) }),
  // Time-limited URL to view the employee's attachment.
  getAppealDocument: (appealId: number) =>
    api.get(`/manager-focus/appeals/${appealId}/document`),

  // ── Appeal Workflow report (standalone Reports page) ──────────────────────
  // The list: every appeal + store/employee context + total flag count.
  getAppealWorkflowReport: (params: { projectId?: number; status?: string }) =>
    api.get('/manager-focus/appeals/report', { params }),
  // The per-appeal drill-down: full appeal (thread + attachment) + parameter-
  // specific evidence (grooming/training/attendance-window/random/absence).
  getAppealDrilldown: (appealId: number) =>
    api.get(`/manager-focus/appeals/${appealId}/drilldown`),
  // One employee's CASE FILE: every red flag they ever received, each carrying its
  // appeal (if any) and whether it was later reversed. Carries no evidence —
  // getFlagEvidence below fetches that for the one flag the reviewer selects,
  // because those builders hit the CRS gateway.
  getAppealCaseFile: (employeeCode: string, projectId?: number) =>
    api.get(`/manager-focus/appeals/case-file/${encodeURIComponent(employeeCode)}`, { params: { projectId } }),
  // Evidence for ONE flag, keyed by the flag rather than an appeal — so a flag that
  // was never appealed shows the same cross-checking panel. Pass kind: 'instance' for
  // an appealed occurrence: the evidence is then that single day, not the gated window
  // that produced a flag (a non-crossing day has no window).
  getFlagEvidence: (params: { employeeCode: string; flagType: string; behaviourDate: string; projectId?: number; kind?: 'flag' | 'instance' }) =>
    api.get('/manager-focus/appeals/flag-evidence', { params }),
};

// ── App Banners ──────────────────────────────────────────────────────────────
/** The audience, exactly as POST /announcements/send takes it. */
export type AnnouncementAudience =
  | { allProject: true }
  | { empCodes: string[] }
  | { managerCodes: string[]; teamScope: 'self' | 'direct' | 'all' };

// ── PIP (Performance Improvement Plan) page ──────────────────────────────────
// list = every case (both tabs); drill-down = case + event trail + frozen score
// breakdown; extend/terminate act on a `pending_decision` case. All hierarchy-scoped
// server-side (an AM sees/acts only on their reportees; admins on the whole project).

/** GET /pip/report's `hiddenNotFinal`: the plans it LEFT OUT of rows and counts because
 *  their cohort was picked on a month that is not final for the person's role yet (or,
 *  when the hold state could not be read, on a month that could be). Nothing is deleted:
 *  they show again once the month is final and PIP is rebuilt. null when nothing is
 *  hidden; an older server does not send the field at all. */
export interface PipHiddenNotFinal {
  /** Plans left out, within the viewer's reach. */
  count: number;
  /** The months that are not final yet, 'YYYY-MM'. */
  months: string[];
  /** The roles they are not final for (e.g. 'iPro'). */
  roles: string[];
  /** The uploads awaited (e.g. 'Target Achieved %age'). */
  awaiting: string[];
}

/** GET /pip/report's envelope. The rows' shape lives with the page that reads them. */
export interface PipReportResponse {
  success: boolean;
  data?: {
    rows?: unknown[];
    standings?: Record<string, Record<string, string>>;
    cohortWaits?: unknown[];
    hiddenNotFinal?: PipHiddenNotFinal | null;
  };
}

export const pipAPI = {
  getReport: (params: { projectId?: number }) => api.get<PipReportResponse>('/pip/report', { params }),
  // Months the selector offers + which is the live-mode anchor.
  getMonths: (projectId?: number) => api.get('/pip/months', { params: { projectId } }),
  // Read-only "who would be flagged as-of <month>" projection (past months).
  getProjection: (params: { projectId?: number; month: string }) => api.get('/pip/projection', { params }),
  getDrilldown: (caseId: number) => api.get(`/pip/${caseId}/drilldown`),
  extend: (caseId: number, note?: string) =>
    api.post(`/pip/${caseId}/extend`, { ...(note ? { note } : {}) }),
  terminate: (caseId: number, note?: string) =>
    api.post(`/pip/${caseId}/terminate`, { ...(note ? { note } : {}) }),
  // Time-limited URL to the generated PIP reason document (PDF).
  getDocument: (caseId: number) => api.get(`/pip/${caseId}/document`),
  // Admin-only: clear untouched engine-created open cases and re-run the engine as-of
  // the latest month (so a selection-logic change takes effect). Returns {deleted,...}.
  rebuild: (projectId?: number) => api.post('/pip/rebuild', {}, { params: { projectId } }),
};

// ── Start Exit (admin exit worklist) ─────────────────────────────────────────
// The review list for employees who have crossed the exit bar — more than N red
// flags in the window, or a failed PIP — plus the single irreversible action on
// it: filing the resignation in 1Office.
//
// Access is the `web.start_exit` page right (its page’s tick) (+ super admins), enforced by
// `requireStartExitAccess` on every route. The client cannot evaluate that, so a
// 403 from getCandidates IS the access answer and the page renders
// AccessDeniedPanel on it rather than an error banner.
//
// `window` is 'rolling' (last 12 months) or 'calendar' (needs `year`). It decides
// WHO qualifies, so it is threaded through the evidence drill-down and the
// exception grant too — an exception freezes a window-relative flag count.
//
// Envelope: { success, data } / { success: false, message, data? }. As with every
// other *API here the raw AxiosResponse comes back, so callers read `res.data.data`.
export const startExitAPI = {
  getCandidates: (p: { projectId: number; window: string; year?: string }) =>
    api.get(`/start-exit/${p.projectId}/candidates`, { params: { window: p.window, year: p.year } }),
  // Everything behind one row's numbers: each counted flag (dated on the BEHAVIOUR
  // day), the PIP event trail, and the 6-month series where `scored:false` means
  // "never scored" and must not read as a real 0.
  getEvidence: (p: { projectId: number; employeeCode: string; window: string; year?: string }) =>
    api.get(`/start-exit/${p.projectId}/evidence/${encodeURIComponent(p.employeeCode)}`, {
      params: { window: p.window, year: p.year },
    }),
  // The 1Office-side facts the exit form needs: notice period, reportee/recruiter
  // reassignment targets, and `blockedReason` when the employee's 1Office userId
  // could not be resolved — submitting then would exit SOMEONE ELSE.
  getExitPrefill: (p: { projectId: number; employeeCode: string }) =>
    api.get(`/start-exit/${p.projectId}/exit-prefill/${encodeURIComponent(p.employeeCode)}`),
  // Read-only: what was filed when an exit was initiated from this page (dates,
  // notice arithmetic, waiver, reason, reassignments, who/when, whether 1Office
  // confirmed it) plus 1Office's current dates. For an exit filed directly in
  // 1Office, only 1Office's dates — the form itself lives there.
  getExitDetails: (p: { projectId: number; employeeCode: string }) =>
    api.get(`/start-exit/${p.projectId}/exit-details/${encodeURIComponent(p.employeeCode)}`),
  // Excuse an employee (201 `{ id }`) / put them back on the worklist (200
  // `{ employeeCode }`). Neither write ever leaves ChannelPlex.
  markException: (p: { projectId: number; employeeCode: string; note: string; window: string; year?: string }) =>
    api.post(`/start-exit/${p.projectId}/exception`, p),
  revertException: (p: { projectId: number; employeeCode: string; note: string }) =>
    api.post(`/start-exit/${p.projectId}/revert-exception`, p),
  // THE irreversible one: generates the proof PDF and files the resignation in
  // 1Office. Non-idempotent — never retry it automatically. On failure the body
  // carries `data.indeterminate`: false = definitely not filed (the form may be
  // corrected and resent), true = outcome unknown (the caller must lock and send
  // the admin to 1Office to look).
  initiateExit: (p: Record<string, unknown> & { projectId: number }) =>
    api.post(`/start-exit/${p.projectId}/initiate`, p),
};

// ── Onboarding / shadow training ─────────────────────────────────────────────
// The new-joiner worklist (Dashboard → Onboarding) and the Buddy Pool master
// (Grow → Onboarding Setup → Buddy Pool).
//
// Access is the `web.onboarding` page right (its page’s tick) (+ super admins), enforced by
// `requireOnboardingAccess` on EVERY route including the reads. The client cannot
// evaluate that, so a 403 from getJoiners IS the access answer and both pages
// render AccessDeniedPanel on it rather than an error banner.
//
// The one thing worth knowing before calling saveAssignment: it writes a REAL
// store deployment for the joiner (that is what lets them log in to the mobile
// app and sign in against a geofence at all), and the nightly sweep removes it
// once the window closes. It refuses outright for a joiner who is already
// deployed, because placing them would erase that posting.
//
// Envelope: { success, data, meta? } / { success: false, message }. As with every
// other *API here the raw AxiosResponse comes back, so callers read `res.data.data`.
export const onboardingAPI = {
  // Employees whose date of joining falls within the last `days` days (default 6).
  // Sourced live from the 1Office roster, not store_employees — a brand-new joiner
  // has no store row yet, which is the whole problem.
  getJoiners: (p: { projectId: number; days?: number }) =>
    api.get(`/onboarding/${p.projectId}/joiners`, { params: { days: p.days } }),
  // Per-day checklist + Sr iPro sign-off compliance for the given joiners (history
  // modal + day-wise download). The row facts travel in the body — re-deriving them
  // server-side would cost an uncached full roster fetch; answers are read server-side.
  getCompliance: (p: {
    projectId: number;
    joiners: Array<{ employeeCode: string; dateOfJoining: string | null; planEndDate: string | null }>;
  }) => api.post(`/onboarding/${p.projectId}/compliance`, { joiners: p.joiners }),
  // Ranked buddy candidates. The joiner's city/state travel as params because the
  // worklist already resolved them (including the store-master state back-fill);
  // re-deriving server-side would mean a second full roster fetch per modal open.
  getSuggestions: (p: { projectId: number; employeeCode: string; city?: string; state?: string }) =>
    api.get(`/onboarding/${p.projectId}/suggestions/${encodeURIComponent(p.employeeCode)}`, {
      params: { city: p.city, state: p.state },
    }),
  getAssignments: (p: { projectId: number; status?: string }) =>
    api.get(`/onboarding/${p.projectId}/assignments`, { params: { status: p.status } }),
  // Shadow dates are DERIVED server-side from the Journey's per-day shadow flags,
  // so start/end are no longer sent (and must NOT be — sending a window would make
  // the server treat it as contiguous and override the per-day set).
  saveAssignment: (p: {
    projectId: number; employeeCode: string; employeeName?: string; dateOfJoining?: string | null;
    buddyCode: string;
  }) => api.post(`/onboarding/${p.projectId}/assignments`, p),
  // Change a LIVE assignment: its dates, its buddy, or both. Swapping the buddy
  // MOVES the trainee between stores in one transaction. The start date is
  // locked once they have actually started — that day already has attendance
  // and store history attached.
  updateAssignment: (p: {
    projectId: number; id: number; startDate?: string; endDate?: string; buddyCode?: string;
  }) => api.put(`/onboarding/${p.projectId}/assignments/${p.id}`, p),
  // Everything the buddy recorded in the app for one assignment, grouped by day.
  getAssignmentResponses: (p: { projectId: number; id: number }) =>
    api.get(`/onboarding/${p.projectId}/assignments/${p.id}/responses`),
  // What one Filled cell of the history pop-up says (its View): the form's questions
  // in order with the answers as filled — choices as labels, ratings with their scale,
  // files as API paths behind the same Onboarding access. `data` is OnboardingSubmission
  // (components/dashboard/OnboardingAnswersView). 404 = nothing filled that day.
  getJoinerAnswers: (p: { projectId: number; employeeCode: string; form: 'checklist' | 'signoff'; date: string }) =>
    api.get(`/onboarding/${p.projectId}/joiners/${encodeURIComponent(p.employeeCode)}/answers`, {
      params: { form: p.form, date: p.date },
    }),
  // The bytes of one answer's file — the `url` an answer carries — as a blob, because
  // that route needs the Authorization bearer (see components/AuthedImage).
  getAnswerFile: (path: string) => api.get(path, { responseType: 'blob' }),
  // Ends a placement early. The ledger row is never deleted — it is the audit
  // trail — and the trainee is undeployed only if still at the shadow store.
  endAssignment: (p: { projectId: number; id: number; note?: string }) =>
    api.delete(`/onboarding/${p.projectId}/assignments/${p.id}`, { data: { note: p.note } }),
  // Run the city auto-assign pass NOW for this project: place a same-city Buddy-
  // Pool member on every eligible, not-yet-assigned new joiner. Returns per-joiner
  // outcomes. Explicit coordinator action, so it runs regardless of the daily
  // scheduler's env gate.
  autoAssign: (p: { projectId: number }) =>
    api.post(`/onboarding/${p.projectId}/auto-assign`, {}),
  // Initiate Onboarding Batch: preview the activities (with URL fields) + eligible
  // joiners, then commit — which materialises the activities as Offline Trainings.
  getBatchPreview: (projectId: number) =>
    api.get(`/onboarding/${projectId}/batch/preview`),
  initiateBatch: (p: { projectId: number; employeeCodes: string[]; meetingUrls: Record<string, string>; note?: string }) =>
    api.post(`/onboarding/${p.projectId}/batch/initiate`, {
      employeeCodes: p.employeeCodes, meetingUrls: p.meetingUrls, note: p.note,
    }),
  // One joiner's resolved day-by-day plan for the worklist drill-down. The joiner's
  // DOJ (and, when assigned, their actual shadow days) travel as params so those
  // days are marked as this joiner's shadow days rather than the template's. The
  // server prefers the day list; start/end is the legacy contiguous fallback.
  getJoinerJourney: (p: {
    projectId: number; doj: string; shadowDates?: string[] | null; shadowStart?: string; shadowEnd?: string;
  }) =>
    api.get(`/onboarding/${p.projectId}/journey/resolve`, {
      params: {
        doj: p.doj,
        shadowDates: p.shadowDates && p.shadowDates.length ? p.shadowDates.join(',') : undefined,
        shadowStart: p.shadowStart,
        shadowEnd: p.shadowEnd,
      },
    }),
  // Extend a joiner's onboarding plan to a later end date — moves them from the
  // Completed tab back to Active.
  extendPlan: (p: { projectId: number; employeeCode: string; extendedTo: string; note?: string }) =>
    api.post(`/onboarding/${p.projectId}/joiners/${encodeURIComponent(p.employeeCode)}/extend`, {
      extendedTo: p.extendedTo, note: p.note,
    }),

  // --- Onboarding score (buddy sign-off ratings → deploy / extend) ---
  // Scores for the worklist, one POST for all rows (same joiner facts as
  // getCompliance). `data` is JoinerScore[] (components/dashboard/onboardingScore.ts).
  getScores: (p: {
    projectId: number;
    joiners: Array<{ employeeCode: string; dateOfJoining: string | null; planEndDate: string | null }>;
  }) => api.post(`/onboarding/${p.projectId}/scores`, { joiners: p.joiners }),
  // One joiner's score with per-question ratings and the full decision history.
  getScoreDetail: (p: { projectId: number; employeeCode: string; dateOfJoining: string | null; planEndDate: string | null }) =>
    api.get(`/onboarding/${p.projectId}/joiners/${encodeURIComponent(p.employeeCode)}/score`, {
      params: { dateOfJoining: p.dateOfJoining ?? undefined, planEndDate: p.planEndDate ?? undefined },
    }),
  // Record the coordinator's call. 'extend' also pushes the plan end (extendedTo),
  // exactly as extendPlan does. A note is required when going against the advice.
  // `data` is { decision: ScoreDecision, score: JoinerScore }.
  recordScoreDecision: (p: {
    projectId: number; employeeCode: string; dateOfJoining: string | null; planEndDate: string | null;
    decision: 'deploy' | 'extend'; note?: string; extendedTo?: string;
  }) =>
    api.post(`/onboarding/${p.projectId}/joiners/${encodeURIComponent(p.employeeCode)}/score-decision`, {
      dateOfJoining: p.dateOfJoining, planEndDate: p.planEndDate,
      decision: p.decision, note: p.note, extendedTo: p.extendedTo,
    }),
  // The project's pass mark (whole %, default 70). `data` is ScoreSettings.
  getScoreSettings: (projectId: number) => api.get(`/onboarding/${projectId}/score-settings`),
  saveScoreSettings: (p: { projectId: number; passMark: number }) =>
    api.put(`/onboarding/${p.projectId}/score-settings`, { passMark: p.passMark }),

  // --- Buddy Pool master ---
  getBuddyPool: (projectId: number) => api.get(`/onboarding/${projectId}/buddy-pool`),
  // The state/city combinations that actually have a deployable buddy. Drives
  // the Add Buddy picker, which chooses an AREA first and then the person.
  getBuddyLocations: (projectId: number) => api.get(`/onboarding/${projectId}/buddy-pool/locations`),
  // Deployed employees, narrowed to the chosen area. The keys are the canonical
  // ones returned by getBuddyLocations, not raw place names — store spellings
  // vary and matching on the raw text misses most of the pool.
  getPoolCandidates: (p: { projectId: number; stateKey?: string; cityKey?: string }) =>
    api.get(`/onboarding/${p.projectId}/buddy-pool/candidates`, {
      params: { stateKey: p.stateKey, cityKey: p.cityKey },
    }),
  // Upsert on (project, employeeCode) — the same call adds and edits.
  saveBuddyPoolEntry: (p: {
    projectId: number; employeeCode: string; employeeName?: string | null; designation?: string | null;
    state?: string | null; city?: string | null; active?: boolean; note?: string | null;
  }) => api.post(`/onboarding/${p.projectId}/buddy-pool`, p),
  deleteBuddyPoolEntry: (p: { projectId: number; id: number }) =>
    api.delete(`/onboarding/${p.projectId}/buddy-pool/${p.id}`),

  // --- Sign-off form builder (Grow → Onboarding Setup → Sign-off Form) ---
  // A form is valid for a RANGE of PROGRAM days — "Day 1" is the first day of a
  // given trainee's window, not a calendar date — so a project runs several
  // active forms, one per phase. Active ranges may not overlap. Questions inside
  // a form are a flat ordered list using the same rules contract as Tasks.
  getSignoffForms: (projectId: number) => api.get(`/onboarding/${projectId}/signoff/forms`),
  getSignoffForm: (p: { projectId: number; formId: number }) =>
    api.get(`/onboarding/${p.projectId}/signoff/forms/${p.formId}`),
  // catchUpFromDay/catchUpToDay: 0-based shadow days the form is asked AGAIN of
  // buddies who missed it. Omit both to keep the form's catch-up; null clears it.
  createSignoffForm: (p: {
    projectId: number; title: string; description?: string | null; fromDay?: number; toDay?: number;
    catchUpFromDay?: number | null; catchUpToDay?: number | null;
  }) => api.post(`/onboarding/${p.projectId}/signoff/forms`, p),
  updateSignoffForm: (p: {
    projectId: number; formId: number; title?: string; description?: string | null;
    fromDay?: number; toDay?: number; catchUpFromDay?: number | null; catchUpToDay?: number | null;
  }) => api.put(`/onboarding/${p.projectId}/signoff/forms/${p.formId}`, p),
  setSignoffFormActive: (p: { projectId: number; formId: number; active: boolean }) =>
    api.put(`/onboarding/${p.projectId}/signoff/forms/${p.formId}/active`, { active: p.active }),
  deleteSignoffForm: (p: { projectId: number; formId: number }) =>
    api.delete(`/onboarding/${p.projectId}/signoff/forms/${p.formId}`),
  createSignoffQuestion: (p: { projectId: number; formId: number } & Record<string, unknown>) =>
    api.post(`/onboarding/${p.projectId}/signoff/forms/${p.formId}/questions`, p),
  updateSignoffQuestion: (p: { projectId: number; formId: number; questionId: string } & Record<string, unknown>) =>
    api.put(`/onboarding/${p.projectId}/signoff/forms/${p.formId}/questions/${p.questionId}`, p),
  deleteSignoffQuestion: (p: { projectId: number; formId: number; questionId: string }) =>
    api.delete(`/onboarding/${p.projectId}/signoff/forms/${p.formId}/questions/${p.questionId}`),
  reorderSignoffQuestions: (p: { projectId: number; formId: number; questionIds: string[] }) =>
    api.post(`/onboarding/${p.projectId}/signoff/forms/${p.formId}/reorder`, { questionIds: p.questionIds }),
  // What a buddy is asked for one assignment on one date — the contract the
  // buddy-facing screen will consume.
  getSignoffDay: (p: { projectId: number; assignmentId: number; date?: string }) =>
    api.get(`/onboarding/${p.projectId}/signoff/day/${p.assignmentId}`, { params: { date: p.date } }),

  // --- Onboarding Checklist builder (Grow → Onboarding Setup → Checklist) ---
  // The per-day questions the NEW JOINER answers themselves across their plan.
  // Same day-range/flat-question shape as the sign-off form, but journey-day
  // based (Day 0 = joining day) and joiner-answered. Mirrors the sign-off API.
  getChecklistForms: (projectId: number) => api.get(`/onboarding/${projectId}/checklist/forms`),
  getChecklistForm: (p: { projectId: number; formId: number }) =>
    api.get(`/onboarding/${p.projectId}/checklist/forms/${p.formId}`),
  createChecklistForm: (p: {
    projectId: number; title: string; description?: string | null; fromDay?: number; toDay?: number;
  }) => api.post(`/onboarding/${p.projectId}/checklist/forms`, p),
  updateChecklistForm: (p: {
    projectId: number; formId: number; title?: string; description?: string | null;
    fromDay?: number; toDay?: number;
  }) => api.put(`/onboarding/${p.projectId}/checklist/forms/${p.formId}`, p),
  setChecklistFormActive: (p: { projectId: number; formId: number; active: boolean }) =>
    api.put(`/onboarding/${p.projectId}/checklist/forms/${p.formId}/active`, { active: p.active }),
  deleteChecklistForm: (p: { projectId: number; formId: number }) =>
    api.delete(`/onboarding/${p.projectId}/checklist/forms/${p.formId}`),
  createChecklistQuestion: (p: { projectId: number; formId: number } & Record<string, unknown>) =>
    api.post(`/onboarding/${p.projectId}/checklist/forms/${p.formId}/questions`, p),
  updateChecklistQuestion: (p: { projectId: number; formId: number; questionId: string } & Record<string, unknown>) =>
    api.put(`/onboarding/${p.projectId}/checklist/forms/${p.formId}/questions/${p.questionId}`, p),
  deleteChecklistQuestion: (p: { projectId: number; formId: number; questionId: string }) =>
    api.delete(`/onboarding/${p.projectId}/checklist/forms/${p.formId}/questions/${p.questionId}`),
  reorderChecklistQuestions: (p: { projectId: number; formId: number; questionIds: string[] }) =>
    api.post(`/onboarding/${p.projectId}/checklist/forms/${p.formId}/reorder`, { questionIds: p.questionIds }),

  // ── Onboarding Journey (Grow → Onboarding Setup → Journey) ─────────────────
  // The per-project 14-day session template + the shadow-training window that
  // Assign Buddy defaults its dates from.
  getJourneySessions: (projectId: number) =>
    api.get(`/onboarding/${projectId}/journey/sessions`),
  createJourneySession: (p: { projectId: number } & Record<string, unknown>) =>
    api.post(`/onboarding/${p.projectId}/journey/sessions`, p),
  updateJourneySession: (p: { projectId: number; sessionId: number } & Record<string, unknown>) =>
    api.put(`/onboarding/${p.projectId}/journey/sessions/${p.sessionId}`, p),
  deleteJourneySession: (p: { projectId: number; sessionId: number }) =>
    api.delete(`/onboarding/${p.projectId}/journey/sessions/${p.sessionId}`),
  getJourneySettings: (projectId: number) =>
    api.get(`/onboarding/${projectId}/journey/settings`),
  saveJourneySettings: (p: { projectId: number; shadowFromDay?: number | null; shadowToDay?: number | null; journeyDays?: number }) =>
    api.put(`/onboarding/${p.projectId}/journey/settings`, p),
};

// ── Social Connect moderation (web admin) ────────────────────────────────────
// The review queue for REPORTED feed content. Every call carries projectId
// explicitly — the backend authorises on it (project-wide data scope + the
// `web.social_moderate` key), and a moderator legitimately works several
// projects, so it is never inferred. A 403 here means "not a moderator for this
// project"; the page renders AccessDeniedPanel rather than an error banner.
// Envelope: { success, data } / { success: false, message }.
export const socialModerationAPI = {
  // One row per reported TARGET (a brigaded post is a single row carrying its
  // report count), newest/most-reported first. `status` is one of
  // open | removed | dismissed | author_deleted, or 'all'.
  getQueue: (params: { projectId: number; status?: string; from?: string; to?: string }) =>
    api.get('/social-moderation/queue', { params }),
  // Every post in the project — reported or not, live or already removed. Server
  // paged (limit/offset, with an exact `total` on EVERY page, not just the first)
  // because it spans the whole project rather than a bounded worklist. `q` is a
  // server param for the same reason: searching one page in the browser would
  // report "not found" for a post that exists on page 7.
  // Envelope: { success, data, count, total }.
  getPosts: (params: {
    projectId: number; from?: string; to?: string; q?: string; limit: number; offset: number;
  }) => api.get('/social-moderation/posts', { params }),
  // Full review detail: the post (incl. media), the whole comment thread, and
  // EVERY report in the cluster with its reporter, reason and free-text note.
  getItem: (targetType: 'post' | 'comment', targetId: string, projectId: number) =>
    api.get(`/social-moderation/items/${targetType}/${targetId}`, { params: { projectId } }),
  // "No violation" — closes every open report on the target, content untouched.
  dismiss: (targetType: 'post' | 'comment', targetId: string, projectId: number, note?: string) =>
    api.post(`/social-moderation/items/${targetType}/${targetId}/dismiss`, {
      projectId,
      ...(note ? { note } : {}),
    }),
  // `reason` is MANDATORY — the server 400s without it. It is the audit record
  // that answers "why was my post removed" when the employee escalates.
  takedown: (targetType: 'post' | 'comment', targetId: string, projectId: number, reason: string) =>
    api.post(`/social-moderation/items/${targetType}/${targetId}/takedown`, { projectId, reason }),
  // Reverses a MODERATOR takedown only; the server 400s on author-deleted content.
  restore: (targetType: 'post' | 'comment', targetId: string, projectId: number, note?: string) =>
    api.post(`/social-moderation/items/${targetType}/${targetId}/restore`, {
      projectId,
      ...(note ? { note } : {}),
    }),
  // Image bytes for a media `path` returned by getItem (e.g.
  // '/social-moderation/media/<id>' or '…?thumb=1'). Fetched through this axios
  // instance — and therefore as a blob, not an <img src> — because the endpoint
  // requires the Authorization bearer. See components/AuthedImage.
  getMedia: (path: string) => api.get(path, { responseType: 'blob' }),
};

// ── Social Connect — DM (private message) moderation ─────────────────────────
/**
 * The EMPLOYEE-facing feed endpoints, called from the console's composer and the
 * like/comment controls on the feed view.
 *
 * ⚠️ A DIFFERENT GRANT FROM socialModerationAPI, and a different failure mode.
 * Moderation is authorised by `web.social_moderate` + project-wide data scope.
 * These routes are the ones the mobile app calls, so they require the MOBILE keys
 * (`social_feed` + `social_post_create`) on a role the caller holds IN THIS PROJECT,
 * and they have no super-admin bypass. A platform admin with no project role gets
 * 403 here while the moderation tabs beside them work perfectly — that is the server
 * behaving correctly, not a bug, and the UI says so rather than retrying.
 *
 * Reading the feed is NOT how this console lists posts: /social-feed is
 * audience-filtered, so a narrowed post the admin was not addressed in would be
 * invisible. The console reads socialModerationAPI.getPosts, which sees the whole
 * project. These endpoints are only for WRITING and for reacting.
 */
export const socialFeedAPI = {
  // Audience choices for the composer, including the targeted ones (a management
  // level, one manager's team). Server-authored and advisory — createPost re-derives
  // and re-authorises everything, so this list can never widen a post.
  getAudienceOptions: (projectId: number) =>
    api.get('/social-feed/audience-options', { params: { projectId } }),

  // `audienceTarget` is the targeted-audience string ('level:th', 'mgr:207878', …).
  // Send it INSTEAD of leaning on `audience`; the server stores audience='team' for
  // targeted posts so old mobile builds label them truthfully.
  createPost: (body: {
    projectId: number;
    content: string;
    postType?: 'post' | 'announcement';
    audience?: string;
    audienceTarget?: string | null;
    mediaItems?: Array<{ blobPath: string; thumbnailBlobPath?: string | null; width?: number; height?: number }>;
    /** Employee codes the post @-tags. The CONTENT carries the readable "@Name"; this
     *  carries identity. The server re-validates every code against project membership
     *  and drops anything that isn't a member — on a restricted post a tag grants
     *  access, so an unvalidated list would be a way to read a team-only post to
     *  anyone. Never send names here; two people share a first name eventually. */
    mentions?: string[];
  }) => api.post('/social-feed', body),

  /** People this caller may @-tag. Deliberately NOT the DM directory, which requires
   *  the messaging feature key — tagging a colleague shouldn't depend on being allowed
   *  to message them. */
  searchPeople: (projectId: number, q: string) =>
    api.get('/social-feed/people/search', { params: { projectId, q } }),

  // Explicit desired state, not a toggle — idempotent under a double click.
  setLike: (postId: string, liked: boolean, projectId: number) =>
    api.post(`/social-feed/${postId}/like`, { liked, projectId }),

  listComments: (postId: string, projectId: number) =>
    api.get(`/social-feed/${postId}/comments`, { params: { projectId } }),

  addComment: (postId: string, content: string, projectId: number) =>
    api.post(`/social-feed/${postId}/comments`, { content, projectId }),

  /**
   * Upload one composer photo and return the blobPath createPost expects.
   *
   * Goes through the generic /upload/file route, which mobile does not use — mobile
   * mints a SAS and PUTs straight to Azure, and doing that from a browser would need
   * CORS rules on the storage account that nobody has configured. The generic route
   * has the server write the bytes, so no CORS is involved.
   *
   * ⚠️ THE FILENAME IS SANITISED HERE, and it has to be. /upload/file stores
   * `originalname` verbatim while createPost validates the finished path against a
   * strict pattern, so a perfectly ordinary "Screenshot 2026-08-19 (1).png" produces
   * a path the post is then REJECTED for — after a successful upload, which reads as
   * a baffling failure. The SAS route sanitises server-side; this one does not.
   *
   * ⚠️ VITE_AZURE_STORAGE_FOLDER must match the API's AZURE_STORAGE_FOLDER or every
   * path fails the same validator. Same mirroring ProjectManagementPage relies on.
   */
  uploadPhoto: (file: File) => {
    const envFolder = (import.meta.env.VITE_AZURE_STORAGE_FOLDER
      || import.meta.env.AZURE_STORAGE_FOLDER || 'DEV').trim();
    const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
    const safeFile = safeName === file.name ? file : new File([file], safeName, { type: file.type });
    return uploadAPI.uploadFile(safeFile, `${envFolder}/social-post`);
  },
};

// A SEPARATE grant from socialModerationAPI: the server gates every route here on
// `web.social_dm_moderate`, which means "may read private mail" and is granted
// independently of `web.social_moderate` (see social-dm-moderation.controller).
// The two refusals a caller must distinguish:
//   403 "You do not have message review access for this project" — no key/scope;
//   503 "DM unavailable"                                          — DM schema not provisioned.
// `projectId` is MANDATORY on EVERY call — a query param on the GETs, a body
// field on the POSTs — and both writes key on a MESSAGE id even though the queue
// lists CONVERSATIONS, so a row's actionable handle is its `latestMessageId`.
export const socialDmModerationAPI = {
  // One row per reported CONVERSATION, capped at 500, most-open-reports first.
  // Envelope is { success, data } — there is NO `total` and no limit/offset, so
  // the caller pages in memory. `status` is the CASE status and reaches all four
  // values (open / removed / dismissed / author_deleted): the queue LEFT JOINs
  // social_dm_messages for the soft-delete quartet, so `?status=removed` returns
  // real rows.
  getQueue: (params: { projectId: number; status?: string; from?: string; to?: string }) =>
    api.get('/social-dm-moderation/queue', { params }),
  // The ENTITLEMENT PROBE. Identical request to getQueue, but flagged so the
  // response interceptor cannot escalate its 401 into a session-expired sign-out.
  // There is no client-side oracle for `web.social_dm_moderate`, so the only way
  // to know is to ask — and asking must be free for the viewer who is not
  // entitled, since they are exactly who this call is for. Every non-2xx (401 no
  // resolvable caller, 403 no grant, 503 no DM schema) means one thing here: no.
  probeQueue: (params: { projectId: number; from?: string; to?: string }) =>
    api.get('/social-dm-moderation/queue', { params, skipSessionExpiry: true } as any),
  // The review detail: a BOUNDED ±25-message window around the reported message —
  // never the whole thread — plus the participants, every user report, and whether
  // a counter-report exists. Note `messages[].createdAt` is EPOCH MILLISECONDS,
  // not an ISO string. Every successful call writes social_dm_access_log BEFORE
  // responding. `reason` is required only to open a conversation carrying no user
  // report, which the queue cannot produce.
  getItem: (params: { projectId: number; conversationId: string; messageId?: string; reason?: string }) =>
    api.get('/social-dm-moderation/item', { params }),
  // `reason` is MANDATORY — the server 400s without it. Soft-deletes the message,
  // writes the synthetic audit report and resolves every open report on it. There
  // is NO restore route: unlike a post takedown, this is not reversible from the
  // web app. Returns `{ success: true }` and nothing else, and deliberately
  // broadcasts nothing, so the caller must refetch.
  takedown: (params: { projectId: number; messageId: string; reason: string }) =>
    api.post('/social-dm-moderation/takedown', params),
  // "No violation" — closes every open report on that MESSAGE, content untouched.
  dismiss: (params: { projectId: number; messageId: string; note?: string }) =>
    api.post('/social-dm-moderation/dismiss', params),
  // The API-RELATIVE path for a reported message's attachment bytes — a path, not a
  // request, because it is fed to useAuthedMediaUrl, which fetches it through the axios
  // instance so the Authorization header actually goes with it.
  //
  // NO ?t= SIGNED TOKEN, unlike the participant route: a browser can carry a bearer,
  // and a six-hour standing grant to a private voice note has no business in browser
  // history. `projectId` is mandatory and is checked against the conversation's own
  // project_id server-side. Every call writes an access-log row BEFORE the bytes move,
  // which is why this is not something to prefetch speculatively.
  mediaPath: (params: { projectId: number; messageId: string; thumb?: boolean }) =>
    `/social-dm-moderation/media/${encodeURIComponent(params.messageId)}` +
    `?projectId=${params.projectId}${params.thumb ? '&thumb=1' : ''}`,
};

// Per-user visibility of financial numbers — payout bands, salary/CTC and the
// redeployment financial impact built on both. The grant lives in
// `financial_visibility_users` and is per USER, not per role (two holders of the
// same role can differ), so there is nothing about it on the auth store's user
// object; it has to be asked for.
//
// Advisory for layout ONLY. Every endpoint carrying a money field redacts it
// independently server-side, so a component that ignores this flag still renders
// nulls rather than figures.
export const financialVisibilityAPI = {
  access: (projectId?: number | null) =>
    api.get('/financial-visibility/access', {
      params: projectId ? { projectId } : undefined,
    }),
};

// Master Data → Sales & Targets.
//
// Project-agnostic by construction: what can be targeted comes from that
// project's Product Master, what a multiplier may key on comes from that
// project's own store / employee custom fields, and which side owns the numbers
// is that project's setting. Nothing here knows about any particular project.
//
// Note what is NOT here: there is no endpoint to write an employee's target.
// Employee targets are DERIVED — from the store target, the store's multiplier
// and the deployment history — and are recalculated on every read, so a
// redeployment needs no "recalculate" step anyone could forget to run.
export const salesTargetsAPI = {
  getConfig: (projectId: number) => api.get(`/sales-targets/${projectId}/config`),
  updateConfig: (
    projectId: number,
    body: {
      sourceScope?: 'store' | 'employee';
      productGranularity?: 'product' | 'sku';
      applyDefaults?: boolean;
      salesScope?: 'store' | 'employee';
      salesSource?: 'upload' | 'running_stock' | 'detailed';
      /** null = promoters are identified by Employee Code alone. */
      employeeKeyFieldId?: string | null;
    },
  ) => api.put(`/sales-targets/${projectId}/config`, body),

  // The employee fields that could identify a promoter in an uploaded sheet, each
  // with how complete and how unique its values are.
  listEmployeeKeyFields: (projectId: number) =>
    api.get(`/sales-targets/${projectId}/employee-key-fields`),

  // What a target or a sales figure can be set against, in template order.
  listCoordinates: (projectId: number, granularity?: 'product' | 'sku') =>
    api.get(`/sales-targets/${projectId}/coordinates`, { params: { granularity } }),
  // Every store (or promoter) a figure can be set against — including the ones
  // with nothing set, which are the rows the grid exists to fill in.
  listSubjects: (projectId: number, month: string, scope?: 'store' | 'employee', side?: 'target' | 'sales') =>
    api.get(`/sales-targets/${projectId}/subjects`, { params: { month, scope, side } }),
  // The project's store / employee custom fields a downloaded sheet can add as
  // read-only reference columns. A store sheet offers store fields; a promoter
  // sheet offers the promoter's fields and their store's.
  listReferenceFields: (projectId: number, scope: 'store' | 'employee') =>
    api.get(`/sales-targets/${projectId}/reference-fields`, { params: { scope } }),

  // ── Targets ──────────────────────────────────────────────────────────────
  listTargets: (
    projectId: number,
    params: { month: string; scope?: 'store' | 'employee'; storeId?: string; employeeCode?: string; productId?: string },
  ) => api.get(`/sales-targets/${projectId}/targets`, { params }),
  saveTargets: (
    projectId: number,
    body: {
      month: string;
      scope?: 'store' | 'employee';
      targets: { storeId?: string; employeeCode?: string; productId: string; skuId?: string | null; targetValue: number }[];
    },
  ) => api.post(`/sales-targets/${projectId}/targets`, body),
  deleteTarget: (projectId: number, targetId: string) =>
    api.delete(`/sales-targets/${projectId}/targets/${targetId}`),
  clearTargetMonth: (projectId: number, month: string, scope?: 'store' | 'employee') =>
    api.delete(`/sales-targets/${projectId}/targets/month`, { params: { month, scope } }),
  // Pre-filled with whatever is already set for the month — download, change a
  // few cells, upload it back. `referenceFields` adds the chosen store / employee
  // custom fields as read-only columns; the upload ignores them. `products`
  // (coordinate keys) narrows the sheet to those product columns; left empty it
  // is one file for every product, which is also what the upload reads.
  downloadTargetTemplate: (projectId: number, month: string, scope?: 'store' | 'employee', referenceFields: string[] = [], products: string[] = []) =>
    api.get(`/sales-targets/${projectId}/targets/bulk-upload/template`, {
      params: {
        month, scope,
        referenceFields: referenceFields.length > 0 ? referenceFields.join(',') : undefined,
        products: products.length > 0 ? products.join(',') : undefined,
      },
      responseType: 'blob',
    }),
  // The response carries the RECALCULATED picture, not just the row count:
  // coverage and warnings depend on deployment and multipliers, which a row of
  // the sheet says nothing about, so they can only be worked out after it lands.
  uploadTargets: (projectId: number, data: string, month: string, scope?: 'store' | 'employee') =>
    api.post(`/sales-targets/${projectId}/targets/bulk-upload`, { data, month, scope }),

  // ── Sales ────────────────────────────────────────────────────────────────
  listSales: (
    projectId: number,
    params: { month: string; scope?: 'store' | 'employee'; storeId?: string; employeeCode?: string; productId?: string },
  ) => api.get(`/sales-targets/${projectId}/sales`, { params }),
  saveSales: (
    projectId: number,
    body: {
      month: string;
      scope?: 'store' | 'employee';
      sales: {
        storeId?: string; employeeCode?: string; productId: string; skuId?: string | null;
        soldValue: number; customValues?: Record<string, unknown>;
      }[];
    },
  ) => api.post(`/sales-targets/${projectId}/sales`, body),
  deleteSales: (projectId: number, salesId: string) =>
    api.delete(`/sales-targets/${projectId}/sales/${salesId}`),
  clearSalesMonth: (projectId: number, month: string, scope?: 'store' | 'employee') =>
    api.delete(`/sales-targets/${projectId}/sales/month`, { params: { month, scope } }),
  downloadSalesTemplate: (projectId: number, month: string, scope?: 'store' | 'employee', referenceFields: string[] = [], products: string[] = []) =>
    api.get(`/sales-targets/${projectId}/sales/bulk-upload/template`, {
      params: {
        month, scope,
        referenceFields: referenceFields.length > 0 ? referenceFields.join(',') : undefined,
        products: products.length > 0 ? products.join(',') : undefined,
      },
      responseType: 'blob',
    }),
  uploadSales: (projectId: number, data: string, month: string, scope?: 'store' | 'employee') =>
    api.post(`/sales-targets/${projectId}/sales/bulk-upload`, { data, month, scope }),

  // Per-project extra details recorded with a sales figure — the sales twin of
  // store / employee custom fields.
  listSalesFields: (projectId: number, includeInactive = false) =>
    api.get(`/sales-targets/${projectId}/sales/fields`, { params: { includeInactive } }),
  createSalesField: (projectId: number, body: { label: string; fieldType: string; options?: string[] | null; isRequired?: boolean }) =>
    api.post(`/sales-targets/${projectId}/sales/fields`, body),
  updateSalesField: (projectId: number, fieldId: string, body: { label: string; fieldType: string; options?: string[] | null; isRequired?: boolean }) =>
    api.put(`/sales-targets/${projectId}/sales/fields/${fieldId}`, body),
  setSalesFieldActive: (projectId: number, fieldId: string, active: boolean) =>
    api.put(`/sales-targets/${projectId}/sales/fields/${fieldId}/active`, { active }),
  deleteSalesField: (projectId: number, fieldId: string) =>
    api.delete(`/sales-targets/${projectId}/sales/fields/${fieldId}`),

  // What the stock / sales tasks already know. Strictly better than an upload
  // where it is available: the task answer carries the promoter who recorded the
  // sale, so nothing has to be allocated.
  getRunningStockSales: (projectId: number, month: string) =>
    api.get(`/sales-targets/${projectId}/sales/from-running-stock`, { params: { month } }),
  importRunningStockSales: (projectId: number, month: string) =>
    api.post(`/sales-targets/${projectId}/sales/import-running-stock`, { month }),

  // ── The computed picture ─────────────────────────────────────────────────
  // Targets, sales and achievement for a month, plus everything the calculation
  // objected to. Nothing is stored; this is derived on every call.
  getComputed: (
    projectId: number,
    params: { month: string; coordKeys?: string; storeId?: string; employeeCode?: string; withSales?: boolean },
  ) => api.get(`/sales-targets/${projectId}/computed`, { params }),
  getEmployeeDetail: (projectId: number, employeeCode: string, month: string) =>
    api.get(`/sales-targets/${projectId}/computed/employee/${encodeURIComponent(employeeCode)}`, { params: { month } }),

  // ── Multipliers ──────────────────────────────────────────────────────────
  listMultipliers: (projectId: number, scope?: 'store' | 'employee', includeInactive = false) =>
    api.get(`/sales-targets/${projectId}/multipliers`, { params: { scope, includeInactive } }),
  listMultiplierFields: (projectId: number, scope?: 'store' | 'employee') =>
    api.get(`/sales-targets/${projectId}/multipliers/fields`, { params: { scope } }),
  // What the rules actually DO, per store. Rules compose across fields, so the
  // effective figure is not readable off the rule list — and an admin who cannot
  // see the result will not trust it.
  previewMultipliers: (projectId: number, scope?: 'store' | 'employee') =>
    api.get(`/sales-targets/${projectId}/multipliers/preview`, { params: { scope } }),
  createMultiplier: (projectId: number, body: Record<string, unknown>) =>
    api.post(`/sales-targets/${projectId}/multipliers`, body),
  updateMultiplier: (projectId: number, multiplierId: string, body: Record<string, unknown>) =>
    api.put(`/sales-targets/${projectId}/multipliers/${multiplierId}`, body),
  setMultiplierActive: (projectId: number, multiplierId: string, active: boolean) =>
    api.put(`/sales-targets/${projectId}/multipliers/${multiplierId}/active`, { active }),
  reorderMultipliers: (projectId: number, scope: 'store' | 'employee', ids: string[]) =>
    api.post(`/sales-targets/${projectId}/multipliers/reorder`, { scope, ids }),
  deleteMultiplier: (projectId: number, multiplierId: string) =>
    api.delete(`/sales-targets/${projectId}/multipliers/${multiplierId}`),

  // ── Defaults ─────────────────────────────────────────────────────────────
  listDefaults: (projectId: number, scope?: 'store' | 'employee') =>
    api.get(`/sales-targets/${projectId}/defaults`, { params: { scope } }),
  createDefault: (projectId: number, body: Record<string, unknown>) =>
    api.post(`/sales-targets/${projectId}/defaults`, body),
  updateDefault: (projectId: number, defaultId: string, body: Record<string, unknown>) =>
    api.put(`/sales-targets/${projectId}/defaults/${defaultId}`, body),
  deleteDefault: (projectId: number, defaultId: string) =>
    api.delete(`/sales-targets/${projectId}/defaults/${defaultId}`),

  // ── Downloads ────────────────────────────────────────────────────────────
  downloadValidation: (projectId: number, month: string) =>
    api.get(`/sales-targets/${projectId}/download/validation`, { params: { month }, responseType: 'blob' }),
  // `coordKeys` keeps the file matching the product the screen is showing.
  downloadCoverage: (projectId: number, month: string, coordKeys?: string) =>
    api.get(`/sales-targets/${projectId}/download/coverage`, {
      params: { month, coordKeys }, responseType: 'blob',
    }),
  downloadEmployeeTargets: (projectId: number, month: string) =>
    api.get(`/sales-targets/${projectId}/download/employee-targets`, { params: { month }, responseType: 'blob' }),
};

// Master Data → Sales & Targets → Incentive.
//
// A plan is the grid an admin already keeps in a spreadsheet: products across,
// achievement bands down, an amount in each cell. Several plans exist because
// schemes differ by store type, and the first plan whose condition matches a
// promoter's store (or the promoter) is the one they are paid under.
//
// Note what is NOT here: there is no endpoint to write a payout. What somebody
// earns is DERIVED from their target, their sales and the plan, and recalculated
// on every read — a payout written down silently disagrees with the sheet the
// moment a sale is corrected, and nobody would know which was right.
export const incentiveAPI = {
  listPlans: (projectId: number, opts: { month?: string; includeInactive?: boolean } = {}) =>
    api.get(`/sales-targets/${projectId}/incentive/plans`, { params: opts }),
  // body.planKind ('grid' | 'per_unit') is fixed at creation; the server defaults
  // to 'grid' and updatePlan never changes it, so updatePlan does not send it.
  createPlan: (projectId: number, body: Record<string, unknown>) =>
    api.post(`/sales-targets/${projectId}/incentive/plans`, body),
  updatePlan: (projectId: number, planId: string, body: Record<string, unknown>) =>
    api.put(`/sales-targets/${projectId}/incentive/plans/${planId}`, body),
  // Just the on/off flag. updatePlan is a full replace, so a list-row toggle that
  // reused it would have to send the entire plan back to change one boolean.
  setPlanActive: (projectId: number, planId: string, active: boolean) =>
    api.patch(`/sales-targets/${projectId}/incentive/plans/${planId}/active`, { active }),
  deletePlan: (projectId: number, planId: string) =>
    api.delete(`/sales-targets/${projectId}/incentive/plans/${planId}`),
  // Order decides which plan somebody is paid under — the first match wins — so
  // the specific plans have to be movable above the catch-all.
  reorderPlans: (projectId: number, ids: string[]) =>
    api.post(`/sales-targets/${projectId}/incentive/plans/reorder`, { ids }),

  // The whole grid in one call. Cells address their band by INDEX, so a row added
  // in the browser carries its amounts in the same save without a round trip to
  // learn its id.
  saveGrid: (
    projectId: number,
    planId: string,
    body: {
      bands: { id?: string | null; label: string; fromPct: number; toPct: number | null }[];
      products: { productId: string; skuId: string | null; weightagePct: number; maxPotential: number }[];
      amounts: { bandIndex: number; productId: string; skuId: string | null; amount: number }[];
    },
  ) => api.put(`/sales-targets/${projectId}/incentive/plans/${planId}/grid`, body),

  // A per-unit plan's settings in one call, for the same reason as the grid: the
  // ladder is one edit in the admin's head. The grid call refuses a per-unit plan.
  // products is [] when allProducts is true; rates are sorted, the first from unit 1.
  savePerUnit: (
    projectId: number,
    planId: string,
    body: {
      minUnits: number | null;
      allProducts: boolean;
      products: { productId: string; skuId: string | null }[];
      rates: { fromUnit: number; rate: number }[];
    },
  ) => api.put(`/sales-targets/${projectId}/incentive/plans/${planId}/per-unit`, body),

  createRule: (projectId: number, planId: string, body: Record<string, unknown>) =>
    api.post(`/sales-targets/${projectId}/incentive/plans/${planId}/rules`, body),
  updateRule: (projectId: number, ruleId: string, body: Record<string, unknown>) =>
    api.put(`/sales-targets/${projectId}/incentive/rules/${ruleId}`, body),
  // Off rules stay in the editor and are skipped by the run.
  setRuleActive: (projectId: number, ruleId: string, active: boolean) =>
    api.put(`/sales-targets/${projectId}/incentive/rules/${ruleId}/active`, { active }),
  deleteRule: (projectId: number, ruleId: string) =>
    api.delete(`/sales-targets/${projectId}/incentive/rules/${ruleId}`),

  // What everyone earns this month, with the inputs behind each figure so a
  // disputed amount can be traced without re-running anything.
  run: (projectId: number, month: string, employeeCode?: string) =>
    api.get(`/sales-targets/${projectId}/incentive/run`, { params: { month, employeeCode } }),
  download: (projectId: number, month: string) =>
    api.get(`/sales-targets/${projectId}/incentive/download`, { params: { month }, responseType: 'blob' }),
  // The designation multiplier (iPro ×1.0, Senior iPro ×1.3 from a month on): every
  // version, earliest first. A save with the same From month replaces that version, a
  // later one adds a version, and no rows removes it; answers with every version.
  listDesignationMultipliers: (projectId: number) =>
    api.get(`/sales-targets/${projectId}/incentive/designation-multipliers`),
  saveDesignationMultipliers: (
    projectId: number,
    body: { effectiveFrom: string; rows: { designation: string; multiplier: number | null }[] },
  ) => api.put(`/sales-targets/${projectId}/incentive/designation-multipliers`, body),
  // The project's designations (1Office budget names) to offer; `available` is false
  // when 1Office could not be read, and a typed designation still saves.
  listDesignations: (projectId: number) =>
    api.get(`/sales-targets/${projectId}/incentive/designations`),
  // One promoter's individual sales, each with the verdict the payout used — the
  // same judge, so the screen cannot disagree with what was paid. `planId` picks
  // one plan of a month split between plans; without it, the main plan's verdicts.
  cases: (
    projectId: number,
    params: { month: string; employeeCode: string; productId?: string; skuId?: string | null; planId?: string },
  ) => api.get(`/sales-targets/${projectId}/incentive/cases`, { params }),

  // Detailed (per-case) sales — one row per SALE. Needed only when incentive
  // rules judge individual sales; a monthly total carries one set of details for
  // the whole month, so no such rule can be answered from it.
  listDetailed: (projectId: number, month: string, employeeCode?: string) =>
    api.get(`/sales-targets/${projectId}/sales/detailed`, { params: { month, employeeCode } }),
  downloadDetailedTemplate: (projectId: number, month: string, referenceFields: string[] = []) =>
    api.get(`/sales-targets/${projectId}/sales/detailed/template`, {
      params: { month, referenceFields: referenceFields.length > 0 ? referenceFields.join(',') : undefined },
      responseType: 'blob',
    }),
  // 'add' keeps what is recorded, so a month can be sent in parts; a sale whose
  // Reference is already recorded is updated, not counted twice. 'replace' first
  // deletes the month for every promoter the sheet names; 'replace_products'
  // deletes only their sales of the products the sheet carries.
  // `confirmMonth` answers the server's question when every Sale Date in the sheet
  // falls in another month: yes, record them for this one.
  // The file goes up as it is (not base64 inside JSON): a whole month of every
  // product can be a few hundred thousand rows, which the server reads a row at a
  // time, and base64 would also push it past the JSON body limit.
  uploadDetailed: (
    projectId: number, file: File, month: string, mode: 'add' | 'replace' | 'replace_products',
    confirmMonth?: boolean,
  ) =>
    api.post(`/sales-targets/${projectId}/sales/detailed/upload`, file, {
      headers: { 'Content-Type': 'application/octet-stream' },
      params: { month, mode, fileName: file.name, ...(confirmMonth ? { confirmMonth: true } : {}) },
      timeout: 600_000,
    }),
  clearDetailed: (projectId: number, month: string) =>
    api.delete(`/sales-targets/${projectId}/sales/detailed`, { params: { month } }),
};

// ── Mobile Usage (Reports → Mobile Usage) ────────────────────────────────────
// How field staff actually use the mobile app, for ONE business project: active
// employees, screens opened, journey success/failure, and where time goes.
//
// The numbers come from PostHog, but the browser never talks to PostHog: the read
// credential can see every project in the organisation, so the API queries it
// server-side and returns only the requested project's rows.
//
// Access is the `web.mobile_usage` page right (its page’s tick) (+ super admins), enforced by
// `requireMobileUsageAccess` on the route. The client cannot evaluate that, so a
// 403 from getSnapshot IS the access answer and the page renders AccessDeniedPanel
// on it rather than an error banner — same contract as startExitAPI above.
//
// `days` is clamped server-side to 1..90. `refresh` bypasses the API's short
// snapshot cache and is what the page's Refresh button sends.
//
// Envelope: { status, data } — as with every other *API here the raw AxiosResponse
// comes back, so callers read `res.data.data`.
export const mobileUsageAPI = {
  getSnapshot: (p: { projectId: number; days?: number; refresh?: boolean }) =>
    api.get(`/mobile-usage/${p.projectId}`, {
      params: { days: p.days, ...(p.refresh ? { refresh: 1 } : {}) },
    }),
  // The people behind ONE number on the page. `params` are the selectors that pin the
  // drill-down to the clicked figure (a role group, an IST day, a page); the API validates
  // every one of them against a closed set and answers 400 for anything else, so nothing
  // here needs to be trusted.
  getPeople: (p: { projectId: number; days?: number; drill: string; params?: Record<string, string> }) =>
    api.get(`/mobile-usage/${p.projectId}/people/${encodeURIComponent(p.drill)}`, {
      params: { days: p.days, ...(p.params ?? {}) },
    }),
  // Is the PostHog credential present on this environment at all? Lets the page tell
  // "not set up" apart from "set up, but no events yet".
  getStatus: () => api.get('/mobile-usage/status'),
};

// ── Uniform & Asset Management (Dashboard → Uniform & Asset Management) ──────
// 1Office Admin Management's Uniform Dashboard for ONE business project: the
// uniform requests by stage — of employees (Joined) and of candidates who have not
// joined yet (Pre Joinee) — this month's SLA counts, the stock of the project's
// kits, three Excel workbooks and three per-request lookups. Read-only: ChannelPlex
// stores no uniform data, and nothing here changes any in 1Office.
//
// The browser never talks to Admin Management: it is reached with the internal
// service secret, which stays on the API. The API also builds the upstream project
// scope itself, from the `projectId` in the URL — so nothing below sends a project
// list, a user id or an employee code, and nothing may be added that does.
//
// Access is the `web.uniform_dashboard` page right (its page’s tick) (+ super admins), enforced by
// `requireUniformDashboardAccess` on every route and checked against the project in
// the URL. The client cannot evaluate that, so a 403 IS the access answer — from ANY
// of the calls below, a download or a per-request lookup as much as a load — and the
// tab renders its own access panel on it rather than an error banner, showing the
// 403's message. That message matters: the gate answers 403, never 401, also for a
// caller it could not verify ("could not verify your identity"), because the
// interceptor above reads any 401 as "your session expired" and a 1Office hiccup
// must not sign the user out of the whole app. Three more statuses mean something
// specific:
//   • 503 with the envelope below — this server has no Admin Management secret
//     configured ("not set up"), which is not the user's to fix.
//   • 502 — Admin Management could not be reached, OR it refused the API's secret.
//     The API never relays an upstream 401 either, for the same reason as above: a
//     mismatched secret must not sign everyone out.
//   • 429, downloads only — too many workbooks are being built at once. Its message
//     ("try again in a moment") is shown as an ordinary, retryable error.
//
// `refresh` bypasses and replaces the API's 60-second snapshot (the five sub-tabs
// share one upstream call). The tab's Refresh button sends it — and so does a
// sub-tab opened while that Refresh is still in flight, so that the API answers it
// from the Refresh rather than from the snapshot being replaced. The per-request
// lookups and the downloads are never cached.
//
// Envelope: { status, data } / { status: 'error', message } — as with every other
// *API here the raw AxiosResponse comes back, so callers read `res.data.data`. The
// three downloads answer with the workbook itself; see blobErrorMessage below for
// what a refused one looks like.
export type UniformTabKey = 'ack_pending' | 'approval_pending' | 'submitted' | 'in_process' | 'completed';
export type UniformDownloadKind = 'report' | 'kit-stock' | 'kit-report';

export const uniformDashboardAPI = {
  // One sub-tab's rows plus the counts of ALL five (`counts` are true totals; `rows`
  // are capped upstream at the newest 5,000 per half — never count the rows).
  getRequests: (p: { projectId: number; tab: UniformTabKey; refresh?: boolean }) =>
    api.get(`/uniform-dashboard/${p.projectId}/requests`, {
      params: { tab: p.tab, ...(p.refresh ? { refresh: 1 } : {}) },
    }),
  // The current India-time month's Within SLA / Out of TAT counts. There is no month
  // parameter: Admin Management only ever measures the current month.
  getKpi: (p: { projectId: number; refresh?: boolean }) =>
    api.get(`/uniform-dashboard/${p.projectId}/kpi`, { params: p.refresh ? { refresh: 1 } : undefined }),
  // Stock of the garments on the kits attached to the project's active budgets.
  getKitStock: (p: { projectId: number; refresh?: boolean }) =>
    api.get(`/uniform-dashboard/${p.projectId}/kit-stock`, { params: p.refresh ? { refresh: 1 } : undefined }),
  // The kit lines of one request. 404 "Request not found in your projects" when the
  // request is not on this project — the upstream check, relayed.
  getKit: (p: { projectId: number; requestId: number }) =>
    api.get(`/uniform-dashboard/${p.projectId}/requests/${p.requestId}/kit`),
  // The courier shipments booked on one request, then one shipment's tracking
  // history. `awb` must be an awb_code getAwbs returned for that same request.
  getAwbs: (p: { projectId: number; requestId: number }) =>
    api.get(`/uniform-dashboard/${p.projectId}/requests/${p.requestId}/awbs`),
  getTimeline: (p: { projectId: number; requestId: number; awb: string }) =>
    api.get(`/uniform-dashboard/${p.projectId}/requests/${p.requestId}/timeline`, { params: { awb: p.awb } }),
  // A Pre Joinee candidate's size-form link. A POST because it hands out a
  // capability link (the API logs who asked) — but it mails nobody and marks
  // nothing sent. Refused upstream for a Joined request.
  getIntakeLink: (p: { projectId: number; requestId: number }) =>
    api.post(`/uniform-dashboard/${p.projectId}/requests/${p.requestId}/intake-link`),
  // 'report' = one row per person with their request and status; 'kit-stock' = every
  // garment's stock by size; 'kit-report' = who was given which kit, garment, size
  // and quantity (refused with a 422 past 10,000 completed requests).
  download: (p: { projectId: number; kind: UniformDownloadKind }) =>
    api.get(`/uniform-dashboard/${p.projectId}/downloads/${p.kind}`, { responseType: 'blob' }),
};

// What a download is saved as when the response carries no usable file name — the
// same three fallbacks the API itself uses.
export const UNIFORM_DOWNLOAD_FALLBACK_NAME: Record<UniformDownloadKind, string> = {
  report: 'Uniform_Report.xlsx',
  'kit-stock': 'Uniform_Kit_Stock.xlsx',
  'kit-report': 'Uniform_Kit_Report.xlsx',
};

// The file name for a uniform workbook, from its Content-Disposition header.
//
// The API already rebuilds that header from a sanitised name, so this is a second
// check, not the first: the value still ends up as an <a download> name on the
// user's machine, and a header is exactly the kind of thing a proxy in between can
// rewrite. Anything that is not a plain `.xlsx` name — or a header the browser was
// not allowed to read at all (cross-origin without Access-Control-Expose-Headers) —
// falls back to the fixed name for that workbook.
export const uniformDownloadFileName = (kind: UniformDownloadKind, contentDisposition: unknown): string => {
  const fallback = UNIFORM_DOWNLOAD_FALLBACK_NAME[kind];
  const match = /filename="?([^";]+)"?/i.exec(typeof contentDisposition === 'string' ? contentDisposition : '');
  const name = (match?.[1] ?? '').trim();
  return /^[A-Za-z0-9._ -]+\.xlsx$/i.test(name) && !name.startsWith('.') ? name : fallback;
};

// A Blob's text, without assuming Blob.text() exists — jsdom's Blob has none even
// though every browser does, so the FileReader path is what keeps this testable.
const readBlobText = (blob: Blob): Promise<string> => {
  if (typeof (blob as { text?: unknown }).text === 'function') return blob.text();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

// The server's message for a failed request whose body may be a Blob.
//
// A download asks for `responseType: 'blob'`, so when the API refuses it — no
// access, Admin Management unreachable, "send fewer projects" — its JSON envelope
// arrives AS a Blob, and `err.response.data.message` is undefined. Without this the
// user sees a generic "download failed" for a refusal that came with a reason.
// A plain JSON body is read the ordinary way, so this is safe on any request.
export const blobErrorMessage = async (err: unknown, fallback: string): Promise<string> => {
  const body = (err as { response?: { data?: unknown } })?.response?.data;
  // Tag-checked rather than `instanceof Blob`: instanceof is false for a Blob made in
  // another realm (a test file, an iframe), and the message would be lost.
  if (body && Object.prototype.toString.call(body) === '[object Blob]') {
    try {
      const parsed = JSON.parse(await readBlobText(body as Blob)) as { message?: unknown };
      if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message;
    } catch {
      /* not JSON (a gateway's HTML error page, say) — fall through to the fallback */
    }
    return fallback;
  }
  const message = (body as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

// ── Project Management launch links ─────────────────────────────────────────
// A link on the web that opens ONE page of 1Office Project Management for the
// signed-in employee without a second login. The API asks 1Office for a one-time
// sign-in link in that employee's name; the browser is then sent to it.
//
// The browser never talks to 1Office for this and never names the employee: who the
// link is for is taken by the API from this request's own sign-in, checked with
// 1Office. So nothing below sends an employee code, and nothing may be added that does.
//
// Both calls set `skipSessionExpiry`: neither is about the ChannelPlex session, and
// a refusal here must never sign the user out of the whole app.
//   • getAccess — a 200 from a current API: { enabled, available }. `enabled` is the
//     server not having stopped the feature (PM_LAUNCH_ENABLED=false is the emergency
//     off; unset = on); `available` is whether THIS user would be
//     given a link for this project. Anything else (a 404 from an older API, a 401
//     where sign-in is enforced, a network error) reads as "no".
//   • requestLink — { launchUrl }, or a refusal whose `message` says what happened
//     and is shown as it is (403 no access / not confirmed, 404 switched off, 429 too
//     many, 502–503 1Office could not issue it; a 401 only from the sign-in check in
//     front of the route, shown as "signed out"). The link works once, for about five
//     minutes, so it is fetched only at the moment of opening and never kept.
//
// Envelope: { status, data } / { status: 'error', message }; the raw AxiosResponse
// comes back, so callers read `res.data.data`.
//
// The pages mirror PM_LAUNCH_PAGES on the API (services/pm-launch.service.ts):
//   attrition-classification — Managed vs Unmanaged Attrition, offered on Dashboard → Attrition.
//   manage-budgets           — 1Office's Manage Budgets page, offered on the hiring screens
//                              to the holders of "Raise & cancel hiring". It opens the page,
//                              not a budget or an opening on it.
export type PmLaunchPageKey = 'attrition-classification' | 'manage-budgets';

export const pmLaunchAPI = {
  getAccess: (p: { projectId: number; page: PmLaunchPageKey }) =>
    api.get(`/pm-launch/${p.projectId}/pages/${p.page}/access`, { skipSessionExpiry: true } as any),
  // POST with no body: the answer is a one-time sign-in and must never be cached.
  // Two minutes: the API may wait on 1Office for well over a minute in the worst case
  // (its sign-in service starts cold, and the API asks up to three times); past that the
  // page should say so, not spin forever.
  requestLink: (p: { projectId: number; page: PmLaunchPageKey }) =>
    api.post(`/pm-launch/${p.projectId}/pages/${p.page}/link`, undefined, { skipSessionExpiry: true, timeout: 120_000 } as any),
};

// ── Hiring (Master Data → Stores: the Hiring column and the Hiring tab) ───────
// A Recruitment (1Office) opening followed against a store, or against a manager role
// that has no store, and how that hiring is going. Phase 1 only reads: a person links an
// opening that ALREADY EXISTS in Recruitment, can stop following it, and sees its
// figures. Nothing of phase 1 sends anything to Recruitment.
//
// Phase 2, "Raise": ONE call below makes Recruitment do something — `raise`, which asks
// it to CREATE an opening. That is a real, live opening: it mails its recruiters and can
// alert candidates, and ChannelPlex cannot take it back. `previewRaise` (the review),
// the three `raise…` pickers, `checkRaise` and `settleRaise` send nothing to Recruitment.
// Raise has one more switch of its own: the project's Raise switch, off for every project
// until a super admin turns it on (with a second step, on the same card as Show). Nothing
// has to be added on the server for it; the server setting HIRING_LINK_RAISE_ENABLED is
// only the emergency off of Raise. The list answer says whether this person may raise
// here (`raise.canRaise`), and nothing of Raise is drawn until it does.
//
// Who may raise (since 14 Oct 2026): a person whose role has "Raise & cancel hiring"
// ticked in Access Management for the project - a row of its own, not the Stores tick.
// The access answer says it (`canRaise`). Anyone else is refused with 403
// `HIRING_NO_ACCESS` by the review, by `raise` and by `confirmRequest` alike, and the
// window opens in ask mode for them from the start. ChannelPlex no longer checks
// 1Office's own who-may-create rule. Asking (the `request…` calls) needs only the Stores tick.
//
// Hiring requests: one person asks for hiring to be raised, another confirms. A person
// with the Stores tick alone fills the same form and sends it for confirmation — the
// `request…` calls below, none of which sends anything to Recruitment. A person with
// "Raise & cancel hiring" (a row of its own in Access Management) opens the waiting hiring request, gets a fresh review in their own
// 1Office sign-in (`reviewRequest`), and confirms it (`confirmRequest`: beside `raise`,
// THE other call behind which Recruitment creates an opening, in the confirmer's name) or
// declines it with a reason. They exist exactly where Raise is on for the project, and
// the access answer says whether to ask for them at all (`canSeeRequests`).
//
// Cancel: ONE more call makes Recruitment do something — `cancel`, which asks it to
// DELETE an opening ChannelPlex follows. Recruitment deletes it at once, in the name of the
// person who confirms, and then tells every candidate still in process that the position
// is closed. It cannot be undone. `previewCancel` (the review) and `checkCancel` send
// nothing. Cancel has its own project switch (the third on the same card), off for every
// project until a super admin turns it on with a second step; the server setting
// HIRING_LINK_CHANGE_ENABLED is only its emergency off. The list answer says whether this
// person may cancel here (`cancel.canCancel`), and nothing of Cancel is drawn until it
// does. Who may: a holder of "Raise & cancel hiring", as for a raise (anyone else: 403
// `HIRING_NO_ACCESS`). A person who may only ask, or only view, can never cause a delete. A cancel is reported as done only once
// Recruitment's own list no longer shows the opening; until then the link stays followed
// and carries `cancel`, and the only thing left to press is "Check again".
//
// On from the day it is deployed, and dark for every project until a super admin turns
// that project's own switch on (Project Management → Budget Settings). Until then every
// project route answers 404 `HIRING_NOT_ENABLED` and the web looks exactly as it did
// before: read `access` through useHiringAccess (components/hiring), which treats any
// answer but a 200 as "off" and shows nothing. The server setting HIRING_LINK_ENABLED is
// only an emergency off: set to false, every route answers that same 404, the settings
// routes included.
//
// Who may do what is the `web.hiring_view` / `web.hiring_request` / `web.hiring_manage` page rights (all three: the Stores page’s tick) (+ super
// admins), checked on the server against the project in the URL. Nothing below sends a
// project, an employee code or a user id in a body.
//
// Envelope: { success: true, data } / { success: false, code, message }. `code` is a
// fixed word per kind of refusal and `message` is written for the screen. Refusals are
// never 401. The shapes below are held to the API by one shared file,
// apps/api/src/services/__fixtures__/hiring-link.contract.json (see
// components/hiring/hiringLinks.contract.test.tsx).
export type HiringStatus =
  | 'no_hiring' | 'raising' | 'on_hold' | 'hiring_in_progress' | 'shortlisted_offered'
  | 'joined_not_placed' | 'no_open_position' | 'not_listed' | 'numbers_unavailable'
  // A message from Recruitment said the opening was deleted or closed there.
  | 'closed_in_recruitment';
export type HiringUnavailableReason =
  | 'no_identity' | 'sign_in_failed' | 'unreachable' | 'recruitment_error'
  | 'budget_not_in_project' | 'timed_out' | 'malformed'
  // Phase 2. An opening ChannelPlex raised a moment ago is not in Recruitment's list yet;
  // and a link that came back with a raise, check or settle answer, which reads no figures.
  | 'not_listed_yet' | 'not_read';
export type HiringLinkReason = 'replacement' | 'new_store' | 'extra_headcount' | 'existing_opening';
export interface HiringNumbers { positions: number; filled: number; offered: number; exiting: number; open: number }
/**
 * How many candidates an opening has at each stage. Counts only: the API never sends who.
 * `inProcess` is screening + interview + selected (everyone still moving); `dropped` is
 * out before joining; `other` is a status the server does not know. These come from
 * another read than HiringNumbers and need not add up to it.
 */
export interface HiringStageCounts {
  inProcess: number;
  screening: number;
  interview: number;
  selected: number;
  offered: number;
  joined: number;
  dropped: number;
  leftAfterJoining: number;
  other: number;
}
/**
 * An opening's address as it is shown: the text only, never the map point. `stateName`, the State
 * of its city by Recruitment's city list, comes with the list of links only (since 26 Oct 2026),
 * null until the server has read that list.
 */
export interface HiringAddressText { cityName: string | null; location: string | null; stateName?: string | null }
export type HiringSubject =
  | { kind: 'store'; storeId: string | null; storeCode: string | null; storeName: string | null }
  | { kind: 'role'; roleId: string | null; roleLabel: string | null };
export interface HiringLinkFlags {
  addressChanged: boolean;
  positionsChanged: boolean;
  joinerExiting: boolean;
  candidatesUnknown: boolean;
}
/**
 * Where a raise stands, on a link ChannelPlex raised that still has something to say:
 * it is being sent, Recruitment did not confirm it, or the opening was created and is
 * not known to be complete. `message` is the server's sentence for it.
 */
export interface HiringRaiseInfo {
  sendId: string;
  /** 'claimed' while it is being sent, 'unknown' when Recruitment did not confirm, 'confirmed' once created. */
  sendStatus: string;
  sentAt: string | null;
  /** The opening number Recruitment's own answer carried, when it carried one. */
  answeredOpeningId: number | null;
  /** From when a request Recruitment did not confirm may be closed as not created. */
  settleAfter: string | null;
  message: string;
}
/**
 * Where a cancel of a link's opening stands, while that cancel is still open: it is being
 * sent, or Recruitment did not confirm it. On a link that is still followed - and on one a
 * person stopped following while its cancel could not be settled, so that its card can
 * still check it. `message` is the server's one line for a list. A cancel that has ended
 * is on the timeline, not here.
 */
export interface HiringCancelInfo {
  sendId: string;
  /** 'claimed' while it is being sent, 'unknown' when Recruitment did not confirm. */
  sendStatus: string;
  sentAt: string | null;
  /** From when an opening Recruitment's list still shows is said to be not cancelled. */
  settleAfter: string | null;
  message: string;
}
export interface HiringLink {
  id: string;
  subject: HiringSubject;
  budget: { id: number; name: string };
  openingId: number | null;
  /** Positions this link accounts for: the opening's when it was linked, 1 for a shared link. */
  positions: number;
  shared: boolean;
  origin: string;
  reason: string;
  replacesEmpCode: string | null;
  owner: string;
  raisedBy: string;
  raisedAt: string;
  state: 'sending' | 'live' | 'closed';
  closedAs: string | null;
  closedBy: string | null;
  closedAt: string | null;
  onHold: boolean;
  status: HiringStatus;
  statusLabel: string;
  numbers: HiringNumbers | null;
  numbersFrom: 'live' | 'last_seen' | null;
  /** Candidates per stage. Null when no read gave them (zeros mean it was read and nobody is there). */
  stages: HiringStageCounts | null;
  /** Read just now, or kept from the last read: marked the way the numbers are. */
  stagesFrom: 'live' | 'last_seen' | null;
  /**
   * When the numbers shown were read from Recruitment — except for an opening that is no
   * longer listed there: its numbers are those of its last listing, while this moves on
   * to each later read that found it gone. Not a date for those numbers.
   */
  seenAt: string | null;
  lastKnownStatus: HiringStatus | null;
  unavailableReason: HiringUnavailableReason | null;
  flags: HiringLinkFlags;
  /** The address when the opening was linked, and as Recruitment has it now. */
  address: { linked: HiringAddressText | null; now: HiringAddressText | null };
  /** Phase 2: the open questions about this link, as the server's words. Absent from a server without Raise. */
  attention?: string[];
  /** Phase 2: where its raise stands. Null for a link made by hand, and once there is nothing left to say. */
  raise?: HiringRaiseInfo | null;
  /**
   * Cancel: where an open cancel of its opening stands. Null when none is open. Absent from
   * a server without Cancel. A link that was stopped following (`closedAs: 'released'`) can
   * still carry one: its cancel was let go of unsettled, and can still be checked.
   */
  cancel?: HiringCancelInfo | null;
  // ── Messages from Recruitment. On every link of a list; absent from an answer that is not a list's, and from an older server. ──
  /** Not closed, and an open question a person must answer. */
  needsAttention?: boolean;
  /** The expected joining date of the offer that is out (`YYYY-MM-DD`), when Recruitment said one. */
  offerJoiningDate?: string | null;
  /** Who joined on the opening: an employee, by code and date. Null when not known. */
  joined?: { empCode: string; on: string | null } | null;
  /** Where the kept numbers came from; `message` = a message from Recruitment. */
  seenSource?: string | null;
  /** One per open question a message from Recruitment left on the link. */
  attentionItems?: HiringAttentionItem[];
  // ── The Hiring dashboard (15 Oct 2026). On every link; absent from an older server. ──
  /** The dashboard's word for it (see HiringDashStatus); `closed` for a link no longer followed. */
  dashStatus?: HiringDashStatus | 'closed';
  /** When the link last changed in ChannelPlex: the dashboard's "Updated". */
  updatedAt?: string;
}
/**
 * The Hiring dashboard's four statuses, worked out by the server from what it holds of a link:
 * `being_raised` (the create is being sent, or Recruitment has not confirmed it), `needs_attention`
 * (an open question - see `attention`, and `status` 'not_listed' / 'closed_in_recruitment' - for the
 * hover), `hiring` (a position open or an offer out) and `filled` (none open, no offer).
 */
export type HiringDashStatus = 'being_raised' | 'hiring' | 'filled' | 'needs_attention';
/** One page of the Hiring dashboard: which page, how many a page holds, how many the filter holds, and how many of each status. */
export interface HiringListPage {
  number: number;
  perPage: number;
  /** Links of the status asked for (or all of them) that the filter holds. */
  total: number;
  /** The numbers of the dashboard's status buttons: `all`, and each status, for the search typed. */
  counts: Record<'all' | HiringDashStatus, number>;
}
export interface HiringLinkList {
  projectId: number;
  recruitment: { read: 'ok' | 'partial' | 'unavailable'; reason: HiringUnavailableReason | null; readAt: string };
  /**
   * Phase 2. `enabled`: Raise is on for this project and not stopped on the server. `canRaise`: and
   * this person may manage hiring and has a 1Office sign-in to raise in. Absent from a
   * server without Raise, which reads as off.
   */
  raise?: { enabled: boolean; canRaise: boolean };
  /**
   * Cancel. `enabled`: Cancel is on for this project and not stopped on the server.
   * `canCancel`: and this person may manage hiring and has a 1Office sign-in to cancel in.
   * Absent from a server without Cancel, which reads as off.
   */
  cancel?: { enabled: boolean; canCancel: boolean };
  links: HiringLink[];
  nextCursor: string | null;
  /** How many of the project's links need a person, whatever page is on show. Null when it could not be counted; absent from an older server. */
  attention?: { count: number | null };
}
/** The Hiring dashboard's list (15 Oct 2026, `hiringLinksAPI.dashboard`): one page, with its counts and the strip. */
export interface HiringDashboardList extends HiringLinkList {
  page: HiringListPage;
  /**
   * The strip ("3 requests waiting · Review"): how many hiring requests wait. Null for a person
   * who is not shown requests (Raise off, or no request / manage / raise right), or when it could not be read.
   */
  requestsWaiting: number | null;
}
/** What the Hiring dashboard asks for: a page from 1, the search box, one status, and a fresh read. */
export interface HiringDashboardQuery {
  page?: number;
  /** A store's name or code, the opening's number, the budget's name, or the name of a hiring with no store. */
  q?: string;
  status?: HiringDashStatus;
  state?: 'live' | 'closed' | 'all';
  subject?: 'store' | 'role';
  refresh?: boolean;
}
/**
 * Dashboard › Hiring's Notice period tab (16 Oct 2026, `hiringLinksAPI.notice`): the people
 * deployed at the stores this person looks after who are serving notice, so hiring to replace
 * them can be raised from there. `serving_notice`: 1Office's employee list has their notice
 * dates (the last working day today or later, or not set yet); `resigned`: their budget's
 * Exit/Transition list names them (HR has not started the exit; no date yet).
 */
export type HiringNoticeState = 'serving_notice' | 'resigned';
/** How far 1Office was read: everything; not every budget; nothing (no 1Office account); not even the employee list. */
export type HiringNoticeRead = 'read' | 'partial' | 'no_identity' | 'unavailable';
/** What already replaces a person: an opening followed, an opening being raised, a hiring request waiting. */
export type HiringNoticeHiringBy = 'opening' | 'being_raised' | 'request';
/** The store of a row: what the Raise window shows of it. */
export interface HiringNoticeStore {
  storeId: string;
  storeName: string | null;
  storeCode: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  /** Six digits, or null. */
  pincode: string | null;
  hasMapLocation: boolean;
  status: string | null;
  staffingStatus: string | null;
}
export interface HiringNoticeRow {
  store: HiringNoticeStore;
  /** An employee, by code and name - never a candidate. */
  person: { empCode: string; name: string | null; designation: string | null; budgetId: number | null };
  state: HiringNoticeState;
  /** `YYYY-MM-DD`, or null when 1Office has none (always null for `resigned`). */
  resignedOn: string | null;
  lastWorkingDay: string | null;
  /** Whether hiring already replaces them, and how. */
  hiring: { raised: boolean; by: HiringNoticeHiringBy | null; openingId: number | null };
}
export interface HiringNoticeList {
  readAt: string;
  oneOffice: {
    read: HiringNoticeRead;
    /** How many budgets the answer needed 1Office's Exit/Transition list for, and how many of them it could not read. */
    budgets: number;
    budgetsUnread: number;
    /** How many people 1Office could not be asked about: they may be serving notice and not shown. */
    notChecked: number;
  };
  page: { number: number; perPage: number; total: number; counts: { all: number; notRaised: number } };
  rows: HiringNoticeRow[];
}
/** The Notice period tab's count for the home screen's Hiring card (GET .../notice/count): no row, only the number and how 1Office was read. */
export interface HiringNoticeCount {
  readAt: string;
  oneOffice: HiringNoticeList['oneOffice'];
  /** People serving notice within reach whose hiring is not raised or linked yet. Only a whole read ('read') makes it the tab's number. */
  notRaised: number;
}
/** What the Notice period tab asks for: a page from 1, and the dashboard's search box (a store's name or code, a person's name or code). */
export interface HiringNoticeQuery {
  page?: number;
  q?: string;
  /** Only the people whose hiring is not raised yet (the home screen's "Notice period, no hiring" card opens the tab on them). */
  notRaisedOnly?: boolean;
}
export interface HiringTimelineEvent {
  id: string;
  type: string;
  source: string;
  actor: string | null;
  occurredAt: string;
  openingId: number | null;
  detail: Record<string, unknown>;
}
/** One page of a timeline. `nextAfter`: where the next page starts (sent back as `after`); null when this is the last page. */
export interface HiringTimeline { linkId: string; events: HiringTimelineEvent[]; nextAfter: string | null }
export interface HiringBudget { budgetId: number; budgetName: string }
export interface HiringPickerOpening {
  openingId: number;
  cityName: string | null;
  /**
   * The State of the opening's city, from Recruitment's city list (16 Oct 2026). Null when
   * that list could not be read in time or does not hold the city; absent from an older server.
   */
  stateName?: string | null;
  location: string | null;
  positions: number;
  filled: number;
  offered: number;
  /** Recruitment's own figure: positions - joined - offered. */
  open: number;
  /**
   * Whether a live link already holds this opening, in any project. 'raising': it
   * appeared after a request under this budget was sent and that request is not resolved
   * yet, so it may be the opening being raised and cannot be linked by hand meanwhile.
   */
  linked: 'no' | 'exclusive' | 'shared' | 'raising';
  /**
   * Who is on the opening, by name, as Recruitment's openings list names them (16 Oct 2026):
   * openings can share an address and differ only in their team. Null when that list carried
   * no team for it; absent from an older server.
   */
  team?: HiringPickerTeam | null;
}
/** An opening's team in Link an opening: the shapes of one opening's Team tab (hiringOpeningApi.ts OpeningTeam). */
export interface HiringPickerTeam {
  recruiters: Array<{ name: string }>;
  offerMakers: Array<{ name: string }>;
  /** Each round that names someone, in round order. A client's round names the client for a holder of "Raise & cancel hiring" only (null otherwise). */
  rounds: HiringPickerRound[];
}
export type HiringPickerRound =
  | { round: number; kind: 'interviewer'; name: string }
  | { round: number; kind: 'client'; name: string | null };
export interface HiringBudgetOpenings {
  budgetId: number;
  /** An opening with more positions than this is linked as shared (the project's setting). */
  positionsPerOpening: number;
  openings: HiringPickerOpening[];
}
export interface HiringSettingsView {
  projectId: number;
  readEnabled: boolean;
  raiseEnabled: boolean;
  changeEnabled: boolean;
  carryOverKm: number;
  positionsPerOpening: number;
  /**
   * Phase 2: true unless Raise has been stopped on the server (the emergency off,
   * HIRING_LINK_RAISE_ENABLED=false). False: nothing can be sent to Recruitment and Raise
   * cannot be turned on for a project. Absent: a server without Raise.
   */
  raiseAvailableOnServer?: boolean;
  /**
   * Cancel: true unless Cancel has been stopped on the server (the emergency off,
   * HIRING_LINK_CHANGE_ENABLED=false). False: no delete can be sent to Recruitment and
   * Cancel cannot be turned on for a project. Absent: a server without Cancel, and the
   * card draws no Cancel switch.
   */
  changeAvailableOnServer?: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}
export interface HiringAccessAnswer {
  canView: boolean;
  canManage: boolean;
  /**
   * Whether this person holds "Raise & cancel hiring" (a row of its own in Access
   * Management, since 14 Oct 2026) or is a super admin: they raise hiring, confirm or decline
   * hiring requests, and cancel openings. Everyone else with the Stores tick asks. Absent
   * from a server from before the row, which reads as no. Says nothing of the switches.
   */
  canRaise?: boolean;
  /**
   * Whether to ask for the project's hiring requests at all: Raise is on for the project
   * and this person holds the Stores tick, "Raise & cancel hiring" or is a super admin.
   * Absent from a server without hiring requests, which reads as no.
   */
  canSeeRequests?: boolean;
}
export interface HiringLinkInput {
  /**
   * A store, or NO store (`role`, 15 Oct 2026: "Link an opening" with the store left empty). A
   * hiring with no store is named after its budget by the server: send no `roleLabel`.
   */
  subject: { kind: 'store'; storeId: string } | { kind: 'role'; roleLabel?: string };
  budgetId: number;
  openingId: number;
  shared: boolean;
  /**
   * The Link hiring window sends none of these three since 14 October 2026: the API
   * then saves `existing_opening`, nobody replaced and no note. It still accepts them.
   */
  reason?: HiringLinkReason;
  replacesEmpCode?: string;
  note?: string;
}
export interface HiringListQuery {
  state?: 'live' | 'closed' | 'all';
  subject?: 'store' | 'role';
  /** One store's links only (the Notice period tab's "Open hiring for" a store). */
  storeId?: string;
  limit?: number;
  cursor?: string;
  refresh?: boolean;
  /** Phase 2: only the requests still being raised (sent or being sent, not yet confirmed). */
  raising?: boolean;
  /** Only the links that need a person (the Hiring tab's "Needs attention"). */
  attention?: boolean;
}

// ── Phase 2, "Raise" ──
export type HiringRaiseReason = 'replacement' | 'new_store' | 'extra_headcount';
/** Where a manager role's address comes from. */
export type HiringRoleAddressSource = 'leaver_opening' | 'budget_default' | 'typed';
/** A budget hiring may be raised under, with Recruitment's count of its positions. */
export interface HiringRaiseBudget {
  budgetId: number;
  budgetName: string;
  /** Ticked Store-level for this project on ChannelPlex's own Project Management page. */
  storeLevel: boolean;
  /** Recruitment's category of the budget (Project, Corporate, …). Null when it does not say. */
  category: string | null;
  total: number;
  initiated: number;
  free: number;
  /** No free position: offered greyed ("Full"), never picked. Absent from an older server. */
  full?: boolean;
}
/** The Raise hiring form's budgets: the same list for the Raise form (`raiseBudgets`) and for a person who asks (`requestBudgets`). */
export interface HiringRaiseBudgets { budgets: HiringRaiseBudget[] }
/** Someone whose exit Recruitment has processed under a budget in the last 30 days. By employee code. */
export interface HiringRaiseExit {
  empCode: string;
  lastWorkingDate: string | null;
  exitProcessedOn: string | null;
  cityName: string | null;
  /** Recruitment already marks them replaced. */
  alreadyReplaced: boolean;
  /** Hiring is already linked or being raised for them in ChannelPlex. */
  hiringOpen: boolean;
  /** ChannelPlex holds them on this store's roster or in its history. Null when no store was asked about. */
  atThisStore: boolean | null;
  /**
   * The name ChannelPlex itself holds for someone on this store's roster; null for anyone
   * else. Sent by the API, and NOT drawn: a person is shown by employee code.
   */
  name: string | null;
}
/**
 * "Who is being replaced?": the one tag a card carries.
 *   serving_notice    1Office's employee list or recent exits has a last working day of today or later (`day`).
 *   resigned          on 1Office's Exit/Transition list: a resignation HR has not processed. No date.
 *   left              left this store in the last 30 days, or still mapped and gone (`day`: their last working day, or the day they left; may be null).
 *   not_checked       1Office could not be read for them: say nothing about a notice. Still pickable.
 *   not_resigned      deployed here and none of the above (a store only). Hover: "1Office lists no resignation or exit for them."
 *   hiring_raised     greyed: a ChannelPlex opening already replaces them (`openingId`, null while it is being raised).
 *   waiting           greyed: a hiring request to replace them is waiting (`requestedBy`).
 *   already_replaced  greyed: Recruitment already marks them replaced.
 */
export type HiringReplaceTag =
  | 'serving_notice' | 'resigned' | 'left' | 'not_checked' | 'not_resigned' | 'hiring_raised' | 'waiting' | 'already_replaced';
/** One person on "Who is being replaced?". An employee, never a candidate. The server sends them in the window's order. */
export interface HiringReplaceCard {
  empCode: string;
  /** ChannelPlex's own name for someone here; otherwise the name 1Office's employee list holds. Null: show the code. */
  name: string | null;
  /** Their budget's name on 1Office's employee list (e.g. "Promoter"); else what ChannelPlex filed. */
  designation: string | null;
  /** Their own budget: the window puts it first in the budget cards, and preselects it unless it is full or not offered. */
  budgetId: number | null;
  /** The store's primary person. */
  primary: boolean;
  /**
   * 1Office lists one exit of theirs under the card's budget, naming the opening they were
   * hired on: a manager role's "The address of the opening the person was hired on" is
   * offered, and chosen, only for such a person.
   */
  leaverOpening: boolean;
  tag: HiringReplaceTag;
  /** 'YYYY-MM-DD' for serving_notice (last day) and left; null otherwise. */
  day: string | null;
  /** False for the three greyed tags. */
  pickable: boolean;
  /** hiring_raised: "Opening {openingId} already replaces them." */
  openingId: number | null;
  /** waiting: who asked for their replacement. */
  requestedBy: string | null;
}
/**
 * The cards of "Who is being replaced?". `oneOffice` 'partial' or 'unavailable' with
 * `notChecked` > 0: "1Office could not be read for {notChecked} people, so a notice may not
 * show. Try again." (the cards stay pickable). `preselected`: the card to open selected.
 */
export interface HiringReplacePeople {
  readAt: string;
  oneOffice: 'read' | 'partial' | 'unavailable';
  notChecked: number;
  people: HiringReplaceCard[];
  preselected: string | null;
}
/**
 * A manager role's "Who is being replaced?" (GET …/budgets/:budgetId/exits): the budget's
 * people who are leaving or left (`people` and the rest), and the older "Who left" rows
 * (`exits`), kept until every window reads the cards.
 */
export type HiringRaiseExits = { budgetId: number; exits: HiringRaiseExit[] } & HiringReplacePeople;

/** A member of Recruitment's staff, as the Recruitment team section and the review name them. Staff only; shown, never stored. */
export interface HiringStaff { userId: number; name: string; empCode: string | null }
/** One interview round as the window sends it: a member of staff by user id, the client's contact, or nothing picked yet. */
export type HiringTeamRound = { userId: number } | { client: { name: string; email: string; mobile?: string | null } } | null;
/**
 * The recruitment team a raise is sent with - the same shape the team section's `prefill`
 * answers with. Recruiters are also sent as the schedulers. One entry per interview round.
 */
export interface HiringRaiseTeam { recruiters: number[]; offerMakers: number[]; rounds: HiringTeamRound[] }
/** GET raise-options/budgets/:budgetId/team: what the Recruitment team section offers and opens with. */
export interface HiringRaiseTeamOptions {
  budgetId: number;
  budgetName: string;
  /** "From the budget's defaults." when true; "No defaults saved for this budget." when false. */
  defaultsSaved: boolean;
  /** Everyone with the Recruiter role, company-wide, by name. Search by name or code. */
  recruiters: HiringStaff[];
  /** Everyone with the Offer Maker role. */
  offerMakers: HiringStaff[];
  /** One per round: a round the client takes (name, company email, optional mobile), or one with its own interviewers. */
  rounds: Array<{ round: number; kind: 'client' } | { round: number; kind: 'staff'; interviewers: HiringStaff[] }>;
  /** The budget's saved defaults, less whoever no longer holds the role: send it back as `team` if nothing is changed. */
  prefill: HiringRaiseTeam;
  /** At most `people` recruiters and offer makers each; a client's name and email up to `clientText` characters. */
  limits: { people: number; clientText: number };
}
/** The Recruitment team block of a review: drawn before `sentences[teamAt]`. Null in the asker's review. */
export interface HiringReviewTeam {
  recruiters: HiringStaff[];
  offerMakers: HiringStaff[];
  rounds: Array<{ round: number; kind: 'staff'; userId: number; name: string; empCode: string | null } | { round: number; kind: 'client'; name: string }>;
  /** How many people Recruitment normally mails (each member of staff once; a client is not mailed). */
  mailed: number;
  /** 'Recruitment normally mails "A new position has been initiated" to these 5 people. The mail names you.' */
  mailSentence: string;
}

/** One city of Recruitment's own list. */
export interface HiringRaiseCity { cityId: number; cityName: string; stateName: string; hasMapLocation: boolean }
/** A store of the store pickers (15 Oct 2026). */
export interface HiringStoreOption {
  storeId: string;
  storeName: string | null;
  storeCode: string | null;
  city: string | null;
  state: string | null;
  address: string | null;
  /** Six digits, or null when the store has none. */
  pincode: string | null;
  hasMapPoint: boolean;
  status: string | null;
  staffingStatus: string | null;
  /** Why a raise would refuse this store (greyed, `refusedMessage` on hover). Always null in the Link window. */
  refused: 'not_active' | 'redeploy' | 'address_incomplete' | null;
  refusedMessage: string | null;
}
/**
 * The store pickers: one page (50) of the stores within reach that hold the text, how many do,
 * and where the next page starts (null: the last). The picker loads the next as it is scrolled.
 */
export interface HiringStoreSearch { q: string | null; total: number; offset?: number; nextOffset?: number | null; stores: HiringStoreOption[] }
/** One person of "Who is replaced?" (15 Oct 2026). An employee by code and name; never a candidate. */
export interface HiringPersonOption {
  empCode: string;
  name: string | null;
  designation: string | null;
  /** Their own budget: the window puts it first ("Their budget") and picks it for them. */
  budgetId: number | null;
  /** Where they are deployed (the primary store when several), or the store they left in the last 30 days. Null: "No store". */
  store: { storeId: string; storeName: string | null; storeCode: string | null } | null;
  tag: 'serving_notice' | 'resigned' | 'left' | 'active';
  /** serving_notice: the last working day; left: the last working day or the day they left the store. */
  day: string | null;
  pickable: boolean;
  /** Why they cannot be picked: a ChannelPlex opening replaces them, a request for them waits, or Recruitment marks them replaced. */
  blocked: { why: 'hiring_raised' | 'waiting' | 'already_replaced'; openingId: number | null; requestedBy: string | null } | null;
  /** The chosen store's own people, the project's leavers, a match of the text - or, with nothing typed, everyone else after them. */
  group: 'store' | 'leaving' | 'match' | 'other';
  primary: boolean;
}
export interface HiringPeopleSearch {
  q: string | null;
  storeId: string | null;
  readAt: string;
  /** How far 1Office's employee list (and the store's cards) could be read. */
  oneOffice: 'read' | 'partial' | 'unavailable';
  /** How many people this person can search: "Type 2 letters to search 3,214 people". For a store, its people. */
  searchable: number;
  /**
   * How many matched; with nothing typed, how many the list holds - for a store, that store's
   * people alone (15 Oct 2026); with no store, the Leaving list, then everyone else.
   */
  total: number;
  /** Where this page (50) starts, and where the next one does (null: the last). The picker loads the next as it is scrolled. */
  offset?: number;
  nextOffset?: number | null;
  people: HiringPersonOption[];
  /** The chosen store's one person serving notice or resigned, to open selected. */
  preselected: string | null;
}
/**
 * What a person asks for when they raise hiring. The review and the confirm carry the
 * SAME body; the project and the person are never in it.
 */
export interface HiringRaiseInput {
  /**
   * A store, or NO store (`role`). Since 15 Oct 2026 a hiring with no store is named after its
   * budget by the server: send no `roleLabel`, and the typed address in `roleAddress` (with the
   * city in `cityId`).
   */
  subject: { kind: 'store'; storeId: string } | { kind: 'role'; roleLabel?: string };
  budgetId: number;
  reason: HiringRaiseReason;
  /** The person being replaced. Sent for a replacement only. */
  replacesEmpCode?: string;
  /** The Recruitment city a person picked: for a store whose own city did not match one, or for a typed address. */
  cityId?: number;
  /**
   * The address of a hiring with no store: typed (a city of Recruitment's list in `cityId`, a
   * 6-digit PIN code, the address). With no `source` the server takes the typed one, and only it;
   * `leaver_opening` and `budget_default` are read for requests saved before 15 Oct 2026.
   * `lat` / `lng`: the map point of an address picked from Google's suggestions, as text - the
   * opening is then placed there in Recruitment, not at the city's point. Both or neither: the
   * server refuses half of one, or one outside India.
   */
  roleAddress?: { source?: HiringRoleAddressSource; address?: string; pincode?: string; lat?: string; lng?: string };
  /** "This store needs one more person". */
  confirmAdditional?: boolean;
  /** "Raise although the store is marked …". */
  confirmStatus?: boolean;
  /** "ChannelPlex has no record of … at this store - raise anyway". */
  confirmNoRecord?: boolean;
  /**
   * @deprecated The window has no Note any more and sends none. The server still reads one,
   * so that hiring requests that waited from before still read back.
   */
  note?: string;
  /**
   * The recruitment team the person picked (the team section). Required for a raise and for
   * a confirmer's review and confirm; the asker's form sends none - the confirmer picks it.
   */
  team?: HiringRaiseTeam;
}
/** The confirm: the reviewed body, the key made when the review step opened, and the review's hash. */
export type HiringRaiseConfirm = HiringRaiseInput & { requestKey: string; previewHash: string };
/** The review: what Recruitment will do, in the server's own sentences. Nothing was sent. */
export interface HiringRaisePreview {
  /** What was reviewed. The confirm carries it back, and is refused if it no longer holds. */
  previewHash: string;
  title: string;
  /**
   * The opening as it will be SENT (the review's own reading of the address and the city).
   * Optional only until every screen reads it: the server always sends it.
   */
  opening?: {
    positions: number;
    cityId: number | null;
    cityName: string | null;
    stateName?: string | null;
    cityFrom?: string | null;
    distanceKm?: number | null;
    location: string | null;
    pincode: number | string | null;
    lat?: string | null;
    lng?: string | null;
    published?: boolean | null;
  } | null;
  /** The budget as reviewed, with its free positions now. Optional only until every screen reads it. */
  budget?: { id: number; name: string; category?: string | null; total?: number | null; initiated?: number | null; free?: number | null } | null;
  sentences: string[];
  confirm: { yes: string; no: string; footnote: string };
  /**
   * Every link the store or role already has open. `seatBeingReplaced`: it is on the
   * opening the person being replaced was hired on, which is the seat being replaced and
   * not hiring in progress.
   */
  existingHiring: { linkId: string; state: string; openingId: number | null; seatBeingReplaced: boolean }[];
  /**
   * Openings Recruitment already lists, unlinked, with this store's name on them (a role:
   * the same address). One of them may be the very opening this request is for: the
   * review says so, and offers to link it instead.
   */
  existingUnlinked: { openingId: number }[];
  /** A manager role: which address sources are complete in Recruitment. Null for a store. */
  roleAddressSources: { source: HiringRoleAddressSource; usable: boolean }[] | null;
  /**
   * The Recruitment team block, by name and code, drawn before `sentences[teamAt]`. Null in
   * the asker's review: there `sentences[teamAt]` is "The person who confirms this picks the
   * recruitment team." Optional only until every screen reads it: the server always sends both.
   */
  team?: HiringReviewTeam | null;
  teamAt?: number;
  /**
   * A replacement, as the checks found it; null for any other reason. `alreadyMarked`:
   * Recruitment already marks the person as replaced, by an earlier request of ChannelPlex
   * that created nothing, so no second marker is sent. `presentLinkOpeningId`: the store
   * or role is still linked to the opening that person was hired on. The server's own
   * `sentences` say both in words; these are the same facts as fields.
   */
  replacement: {
    empCode: string;
    /** The person's name for the review: ChannelPlex's own, else 1Office's employee list's. Optional only until every screen reads it. */
    name?: string | null;
    /** Recruitment lists their exit under this budget. False: a plain opening (no replacement marker). */
    listed?: boolean;
    markerSent: boolean;
    atThisStore: boolean | null;
    alreadyMarked: boolean;
    presentLinkOpeningId: number | null;
  } | null;
}
/** Every way a raise, a check or a settle can end. */
export type HiringRaiseOutcome =
  | 'created' | 'not_confirmed' | 'needs_choice' | 'in_progress' | 'refused' | 'not_sent'
  | 'closed_not_created' | 'held_elsewhere' | 'already_linked';
/** One opening a person may pick as "the one this request created". Never a name. */
export interface HiringRaiseCandidate {
  openingId: number;
  cityName: string | null;
  location: string | null;
  positions: number;
  /** It is exactly what was sent: city, address, map point and positions. */
  matches: boolean;
  /** A link already holds it, so it cannot be picked. */
  linked: boolean;
}
/** How a raise, a check or a settle answers once a request exists. */
export interface HiringRaiseResult {
  outcome: HiringRaiseOutcome;
  message: string;
  sendId: string;
  link: HiringLink;
  candidates: HiringRaiseCandidate[];
}
/** A person says how a request Recruitment did not confirm ended. */
export type HiringSettleInput =
  | { result: 'created'; openingId: number }
  | { result: 'not_created'; noneOfThese?: boolean; note?: string };
/**
 * What is in the way under a budget (the `detail` of that refusal): a request to raise
 * hiring that Recruitment has not confirmed, or (`what: 'cancel'`) a cancel of one of the
 * budget's openings that it has not confirmed. Either way `linkId` is the link to open.
 */
export interface HiringRaiseBlocking {
  linkId: string;
  label: string | null;
  subjectKind: string;
  sentAt: string | null;
  settleAfter: string | null;
  /** Only when it is a cancel that is in the way. Absent for a request to raise hiring. */
  what?: 'cancel';
}

// ── Cancel: ChannelPlex asks Recruitment to delete an opening it follows ──
/**
 * The review of a cancel: what Recruitment will do, in the server's sentences, and the
 * hash of what was shown. Of the opening's candidates there is one number, a count
 * (`inProcess`; null when they could not be counted just then) - never who.
 */
export interface HiringCancelPreview {
  /** Sent back unchanged with the confirm: what was reviewed is what is cancelled, or nothing is. */
  previewHash: string;
  title: string;
  link: { id: string; openingId: number; origin: string; shared: boolean };
  budget: { id: number; name: string };
  opening: { positions: number; filled: number; offered: number; open: number; inProcess: number | null };
  sentences: string[];
  /** The reason box: its limits and its label. */
  reason: { min: number; max: number; label: string };
  confirm: { yes: string; no: string; footnote: string };
}
/** "Yes, cancel this opening": why, the key made when the review arrived, and the review's hash. Nothing else is sent. */
export interface HiringCancelConfirm { reason: string; requestKey: string; previewHash: string }
/**
 * Every way a cancel, or a check of one, can end. `cancelled` only once Recruitment's own
 * list no longer shows the opening; `not_cancelled` only once that list still showed it at
 * or after the settle time. `not_confirmed` and `in_progress` are open: checked, never sent again.
 */
export type HiringCancelOutcome = 'cancelled' | 'not_confirmed' | 'in_progress' | 'refused' | 'not_sent' | 'not_cancelled';
/** How a cancel, or a check of one, answers once a claim exists. `link` reads no figures: read the list again for those. */
export interface HiringCancelResult {
  outcome: HiringCancelOutcome;
  message: string;
  sendId: string;
  link: HiringLink;
}

// ── Hiring requests: one person asks, another confirms ──
/**
 * Where a hiring request stands. `sent`: confirmed, and Recruitment has not confirmed the
 * opening yet. `not_created`: confirmed, Recruitment created nothing, and it could not go
 * back to waiting because another request for the same store or person is waiting (several
 * with no store may wait under one budget since 15 Oct 2026).
 */
export type HiringRequestStanding = 'waiting' | 'sent' | 'raised' | 'not_created' | 'declined' | 'withdrawn';
/** One hiring request, as the server answers it. The form it was sent with is never in it. */
export interface HiringRequest {
  id: string;
  subject: HiringSubject;
  budget: { id: number; name: string };
  reason: string;
  replacesEmpCode: string | null;
  /** What the person who asked ticked on the form. */
  ticks: { additional: boolean; status: boolean; noRecord: boolean };
  note: string | null;
  requestedBy: string;
  /** The asker's name, when the server could read it; the page falls back to the code. */
  requestedByName?: string | null;
  /** The name of the person being replaced, likewise. */
  replacesName?: string | null;
  requestedAt: string;
  /** This person is the one who asked. */
  mine: boolean;
  standing: HiringRequestStanding;
  /** The server's sentence for where it stands, with its moments as "2031-03-14 04:45 UTC". */
  line: string;
  /** What stands in the way of a waiting request, in the server's sentences. */
  notes: string[];
  /** Whole days since it was asked, while it waits; null once it no longer does. */
  waitingDays: number | null;
  old: boolean;
  /** The last confirm, whatever came of it. */
  confirmed: { by: string; at: string; linkId: string | null; openingId: number | null } | null;
  ended: { by: string; at: string; reason: string | null } | null;
}
export interface HiringRequestList {
  projectId: number;
  /** What this person may do with hiring requests here. */
  can: { send: boolean; confirm: boolean; decline: boolean };
  /** How many of the project's hiring requests are waiting for confirmation, whatever the page shows. */
  waiting: number;
  oldAfterDays: number;
  requests: HiringRequest[];
  nextCursor: string | null;
}
export interface HiringRequestListQuery {
  status?: 'waiting' | 'past' | 'all';
  subject?: 'store' | 'role';
  storeId?: string;
  limit?: number;
  cursor?: string;
}
/** "Send for confirmation": the raise form, and the key the window made when its review step opened. */
export type HiringRequestSend = HiringRaiseInput & { requestKey: string };
/** How a send answers: the request as it stands, and the server's sentence for it. */
export interface HiringRequestSent { request: HiringRequest; message: string }
/** The confirmer's review: a raise's review in their own 1Office sign-in, with the request beside it. */
export type HiringRequestReview = HiringRaisePreview & { request: HiringRequest };
/** How a confirm answers: a raise's own answer, with the request as it stands now. */
export type HiringRequestConfirmed = HiringRaiseResult & { request: HiringRequest };

// ── Messages from Recruitment: what they leave for a person to see and answer ──
/** The three questions a message from Recruitment can leave on a link. */
export type HiringAttentionReason = 'address_changed' | 'positions_changed' | 'closed_in_recruitment';
/** What a person can answer. "Stop following" is not one of these: it is the release call, unchanged. */
export type HiringAttentionChoice = 'keep' | 'close' | 'raise_again';
/** One open question of a link, as the server answers it. */
export interface HiringAttentionItem {
  reason: HiringAttentionReason;
  /**
   * When it changed in Recruitment; when that is not known, since when the question is
   * open. An instant - or a calendar day alone (`YYYY-MM-DD`) for an opening Recruitment
   * recorded as closed on a day, with no time of day: print that as a day.
   */
  at: string | null;
  /** The employee code Recruitment recorded for the change. It can be missing, and it can be out of date. */
  byEmpCode: string | null;
  /** How the opening came to be closed. Null for the other two questions. */
  how: 'deleted' | 'closed_on_exit' | 'other' | null;
  /** The address accepted, and as last seen. `onlyMapMoved`: the words are the same and only the map point moved. */
  address: { was: HiringAddressText | null; now: HiringAddressText | null; onlyMapMoved: boolean } | null;
  /** The positions this link accounts for, and the opening's as last seen. `linkedStores`: a shared opening's live links. */
  positions: { was: number; now: number | null; linkedStores: number | null } | null;
  /** What THIS person may answer here. Empty for someone who may not manage hiring, and when nothing is left to keep. */
  choices: HiringAttentionChoice[];
  /** Sent back with an answer. The server refuses an answer whose token is no longer the question's. */
  token: string;
}
export interface HiringAttentionAnswer { reason: HiringAttentionReason; choice: HiringAttentionChoice; token: string }
export interface HiringAttentionResult {
  result: 'kept' | 'closed';
  /** Closed to raise hiring again: the screen opens the Raise hiring window next. */
  raiseAgain: boolean;
  link: HiringLink;
}
/** Whether Recruitment's messages are being accepted on this server (super admins, for the Hiring card). */
export interface HiringMessagesStatus {
  accepting: boolean;
  stopped: boolean;
  lastReceivedAt: string | null;
}

type HiringOk<T> = { success: true; data: T };
/** A question asked on the page's behalf: a refusal means "not here", never "session over". */
type HiringProbeConfig = NonNullable<Parameters<typeof api.get>[1]> & { skipSessionExpiry: true };
const hiringProbeConfig: HiringProbeConfig = { skipSessionExpiry: true };
/** How long the review, a check and a settle wait: the server reads Recruitment for up to a minute. */
export const HIRING_REVIEW_TIMEOUT_MS = 90_000;
/**
 * How long the confirm waits for the server's answer: 200 seconds.
 *
 * Longer than the server can take, and shorter than Azure's front gateway allows: the
 * gateway cuts a request at about 230 seconds, and no answer can arrive after that. (It
 * was 240 seconds, which waited past the cut.)
 *
 * The server's longest for one raise, every wait running to its limit
 * (apps/api: hiring-link-raise.service.ts and recruitment.client.ts):
 *
 *    25 s   the checks before sending                    CHECKS_DEADLINE_MS
 *    25 s   the budget's count of positions, read fresh   CHECKS_DEADLINE_MS again (only when the checks used a remembered count)
 *    60 s   Recruitment's answer to the create            CREATE_TIMEOUT_MS
 *     1.5 s the pause before the read-back                READBACK_WAIT_MS
 *    12 s   one read-back of the budget's list            READBACK_DEADLINE_MS
 *     5 s   who is on the new opening                     TEAM_READ_WAIT_MS
 *   128.5 s an ordinary raise
 *    61 s   more, only when an older request under the same budget is still not confirmed
 *           and is first read to its end: 12 + 12 + 25 + 12 (its list, its opening, the count, the exits)
 *   189.5 s the longest
 *
 * So 200 seconds is about 10 above the longest and 30 below the gateway's cut.
 *
 * Past this wait the answer is "not known", never "failed": the window offers only "Ask
 * again", which repeats the same request with the same key, and the server sends a key
 * to Recruitment at most once.
 */
export const HIRING_RAISE_TIMEOUT_MS = 200_000;

export const hiringLinksAPI = {
  /** Is hiring on for this project, and may this user see it / manage it? Never reads Recruitment. */
  access: (projectId: number) =>
    api.get<HiringOk<HiringAccessAnswer>>(`/hiring-links/${projectId}/access`, hiringProbeConfig),
  /**
   * The project's links with their numbers. Answers 200 even when Recruitment is down
   * (`recruitment.read` says so). `background` is for a page that only decorates itself
   * with the answer (the Stores page's Hiring column): a refusal must not end the session.
   */
  list: (projectId: number, q: HiringListQuery = {}, opts: { background?: boolean } = {}) =>
    api.get<HiringOk<HiringLinkList>>(`/hiring-links/${projectId}/links`, {
      params: {
        ...(q.state ? { state: q.state } : {}),
        ...(q.subject ? { subject: q.subject } : {}),
        ...(q.storeId ? { storeId: q.storeId } : {}),
        ...(q.limit ? { limit: q.limit } : {}),
        ...(q.cursor ? { cursor: q.cursor } : {}),
        ...(q.refresh ? { refresh: 1 } : {}),
        ...(q.raising ? { raising: 1 } : {}),
        ...(q.attention ? { attention: 1 } : {}),
      },
      ...(opts.background ? hiringProbeConfig : {}),
    }),
  /**
   * The Hiring dashboard (15 Oct 2026): one page of 12, newest change first, with the count of
   * each status (`page`) and the hiring requests waiting (`requestsWaiting`). The search box and
   * the status narrow it; the counts follow the search, not the status. Answers 200 when
   * Recruitment is down, as `list` does.
   */
  dashboard: (projectId: number, q: HiringDashboardQuery = {}) =>
    api.get<HiringOk<HiringDashboardList>>(`/hiring-links/${projectId}/links`, {
      params: {
        page: q.page && q.page > 0 ? q.page : 1,
        ...(q.q && q.q.trim() ? { q: q.q.trim() } : {}),
        ...(q.status ? { status: q.status } : {}),
        ...(q.state ? { state: q.state } : {}),
        ...(q.subject ? { subject: q.subject } : {}),
        ...(q.refresh ? { refresh: 1 } : {}),
      },
    }),
  /**
   * The Notice period tab (16 Oct 2026): one page of 12 of the people serving notice at the stores
   * this person looks after, the soonest last working day first, with whether hiring already
   * replaces each. The search box narrows it, and the count follows the search. Reads 1Office's
   * employee list and, per budget, its Exit/Transition list; answers 200 when 1Office cannot be
   * read (`oneOffice.read` says so).
   */
  notice: (projectId: number, q: HiringNoticeQuery = {}) =>
    api.get<HiringOk<HiringNoticeList>>(`/hiring-links/${projectId}/notice`, {
      params: {
        page: q.page && q.page > 0 ? q.page : 1,
        ...(q.q && q.q.trim() ? { q: q.q.trim() } : {}),
        ...(q.notRaisedOnly ? { hiring: 'not_raised' } : {}),
      },
      timeout: HIRING_REVIEW_TIMEOUT_MS,
    }),
  /** Follow an opening that already exists in Recruitment. Answers 201 with the new link. */
  link: (projectId: number, body: HiringLinkInput) =>
    api.post<HiringOk<HiringLink>>(`/hiring-links/${projectId}/links`, body),
  /**
   * Stop following an opening. The opening itself is not touched, and nothing is sent to
   * Recruitment. The answer is the link as it was closed. Beside a cancel Recruitment had
   * not confirmed the server reads Recruitment's list first: the link then comes back
   * closed as `cancelled` when that cancel did go through, or as `released` still carrying
   * `cancel` when the list could not say.
   */
  release: (projectId: number, linkId: string) =>
    api.post<HiringOk<HiringLink>>(`/hiring-links/${projectId}/links/${linkId}/release`, {}),
  /**
   * One page of a link's events, oldest first (200 lines a page, unless the server is told
   * otherwise). `after` is the `nextAfter` of the page before; the answer's own `nextAfter`
   * is null on the last page. A card reads every page: `readTimeline` in components/hiring.
   */
  timeline: (projectId: number, linkId: string, after?: string) =>
    api.get<HiringOk<HiringTimeline>>(
      `/hiring-links/${projectId}/links/${linkId}/timeline`,
      after ? { params: { after } } : undefined,
    ),
  budgets: (projectId: number) =>
    api.get<HiringOk<{ budgets: HiringBudget[] }>>(`/hiring-links/${projectId}/budgets`),
  budgetOpenings: (projectId: number, budgetId: number) =>
    api.get<HiringOk<HiringBudgetOpenings>>(`/hiring-links/${projectId}/budgets/${budgetId}/openings`),
  /** "Link an opening"'s store picker (15 Oct 2026): the stores within this person's reach, none marked. */
  linkStores: (projectId: number, q?: string, offset?: number) =>
    api.get<HiringOk<HiringStoreSearch>>(`/hiring-links/${projectId}/link-options/stores`, { params: { ...(q ? { q } : {}), ...(offset ? { offset } : {}) } }),
  /** The project's switches (super admins). 404 only while Hiring is stopped on the server (the emergency off). */
  getSettings: (projectId: number) =>
    api.get<HiringOk<HiringSettingsView>>(`/hiring-links/${projectId}/settings`, hiringProbeConfig),
  setReadEnabled: (projectId: number, readEnabled: boolean) =>
    api.put<HiringOk<HiringSettingsView>>(`/hiring-links/${projectId}/settings`, { readEnabled }),
  /** The project's Raise switch (super admins). Turning it on is refused while Raise is stopped on the server. */
  setRaiseEnabled: (projectId: number, raiseEnabled: boolean) =>
    api.put<HiringOk<HiringSettingsView>>(`/hiring-links/${projectId}/settings`, { raiseEnabled }),
  /** The project's Cancel switch (super admins). Turning it on is refused while Cancel is stopped on the server. */
  setChangeEnabled: (projectId: number, changeEnabled: boolean) =>
    api.put<HiringOk<HiringSettingsView>>(`/hiring-links/${projectId}/settings`, { changeEnabled }),

  // ── Phase 2, "Raise". Only `raise` sends anything to Recruitment. ──
  /** The budgets hiring may be raised under, for a store or a manager role - and whether this person may send from here (`send`). */
  raiseBudgets: (projectId: number, subject: 'store' | 'role', reason?: HiringRaiseReason) =>
    api.get<HiringOk<HiringRaiseBudgets>>(`/hiring-links/${projectId}/raise-options/budgets`, {
      params: { subject, ...(reason ? { reason } : {}) },
    }),
  /** A manager role's "Who is being replaced?" (`people`), with the older "Who left" rows (`exits`). */
  raiseExits: (projectId: number, budgetId: number, storeId?: string) =>
    api.get<HiringOk<HiringRaiseExits>>(
      `/hiring-links/${projectId}/raise-options/budgets/${budgetId}/exits`,
      { params: storeId ? { storeId } : {} },
    ),
  /** A store's "Who is being replaced?": its people and who left it in the last 30 days, as cards in the window's order. */
  raisePeople: (projectId: number, storeId: string) =>
    api.get<HiringOk<HiringReplacePeople>>(`/hiring-links/${projectId}/raise-options/stores/${storeId}/people`, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /**
   * The Recruitment team section for a budget: both role lists, the rounds, and the budget's
   * defaults as `prefill`. Read again whenever the budget changes. A confirmer of a hiring
   * request reads it too (there is no request twin: the asker never picks the team).
   */
  raiseTeam: (projectId: number, budgetId: number) =>
    api.get<HiringOk<HiringRaiseTeamOptions>>(`/hiring-links/${projectId}/raise-options/budgets/${budgetId}/team`, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /** Recruitment's own cities, searched by at least two characters. */
  raiseCities: (projectId: number, q: string) =>
    api.get<HiringOk<{ cities: HiringRaiseCity[] }>>(`/hiring-links/${projectId}/raise-options/cities`, { params: { q } }),
  /** The Raise window's store picker (15 Oct 2026): the stores within reach, a page from `offset` and a total, a raise's refusals marked. */
  raiseStores: (projectId: number, q?: string, offset?: number) =>
    api.get<HiringOk<HiringStoreSearch>>(`/hiring-links/${projectId}/raise-options/stores`, { params: { ...(q ? { q } : {}), ...(offset ? { offset } : {}) } }),
  /**
   * "Who is replaced?" (15 Oct 2026): `q` of 2 to 50 characters searches the project's people by
   * name or code (a page of 50 from `offset`, and how many matched); without it, the Leaving
   * list - the store's own people first when `storeId` is given - and then everyone else. The
   * text is never put anywhere but here.
   */
  raisePeopleSearch: (projectId: number, query: { q?: string; storeId?: string; offset?: number } = {}) =>
    api.get<HiringOk<HiringPeopleSearch>>(`/hiring-links/${projectId}/raise-options/people`, {
      params: { ...(query.q ? { q: query.q } : {}), ...(query.storeId ? { storeId: query.storeId } : {}), ...(query.offset ? { offset: query.offset } : {}) },
      timeout: HIRING_REVIEW_TIMEOUT_MS,
    }),
  /** The review: every check, and what Recruitment will do. Sends nothing, saves nothing. */
  previewRaise: (projectId: number, body: HiringRaiseInput) =>
    api.post<HiringOk<HiringRaisePreview>>(`/hiring-links/${projectId}/raises/preview`, body, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /**
   * "Yes, raise hiring": THE call behind which Recruitment creates an opening. A repeat
   * with the same `requestKey` is answered from the first request and creates nothing
   * more. The server may take up to about three minutes (its checks, Recruitment's own
   * answer, one read back), so this waits longer than any other call here - and still
   * gives up before Azure's gateway would cut the request (HIRING_RAISE_TIMEOUT_MS).
   */
  raise: (projectId: number, body: HiringRaiseConfirm) =>
    api.post<HiringOk<HiringRaiseResult>>(`/hiring-links/${projectId}/raises`, body, { timeout: HIRING_RAISE_TIMEOUT_MS }),
  /**
   * "Check again": how a raised request stands, after reading Recruitment. Never sends.
   * Also asked by the page itself a few times after a request, so a refusal must not end
   * the session.
   */
  checkRaise: (projectId: number, linkId: string) =>
    api.post<HiringOk<HiringRaiseResult>>(
      `/hiring-links/${projectId}/links/${linkId}/check`, {}, { ...hiringProbeConfig, timeout: HIRING_REVIEW_TIMEOUT_MS },
    ),
  /** A person says how a request Recruitment did not confirm ended. Never sends. */
  settleRaise: (projectId: number, linkId: string, body: HiringSettleInput) =>
    api.post<HiringOk<HiringRaiseResult>>(`/hiring-links/${projectId}/links/${linkId}/settle`, body, { timeout: HIRING_REVIEW_TIMEOUT_MS }),

  // ── Cancel. Only `cancel` sends anything to Recruitment. ──
  /** The review of a cancel: every check, and what Recruitment will do. Sends nothing, saves nothing. */
  previewCancel: (projectId: number, linkId: string) =>
    api.post<HiringOk<HiringCancelPreview>>(`/hiring-links/${projectId}/links/${linkId}/cancel/preview`, {}, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /**
   * "Yes, cancel this opening": THE call behind which Recruitment deletes an opening, in
   * the name of the person who confirms. A repeat with the same `requestKey` is answered
   * from the first request and deletes nothing more. It waits as long as a raise does: the
   * server's checks, Recruitment's own answer (up to a minute) and one read back of the
   * budget's list fit well inside it. Past this wait the answer is "not known", never
   * "failed": the card offers only "Ask again", with the same key.
   */
  cancel: (projectId: number, linkId: string, body: HiringCancelConfirm) =>
    api.post<HiringOk<HiringCancelResult>>(`/hiring-links/${projectId}/links/${linkId}/cancel`, body, { timeout: HIRING_RAISE_TIMEOUT_MS }),
  /**
   * "Check again": how the link's latest cancel stands, after reading Recruitment's list.
   * Never sends. Also asked by the card itself a few times after a cancel, so a refusal
   * must not end the session. It answers after Cancel was turned off or stopped, too.
   */
  checkCancel: (projectId: number, linkId: string) =>
    api.post<HiringOk<HiringCancelResult>>(
      `/hiring-links/${projectId}/links/${linkId}/cancel/check`, {}, { ...hiringProbeConfig, timeout: HIRING_REVIEW_TIMEOUT_MS },
    ),

  // ── Hiring requests. What a person who may ASK does. None of these seven sends anything to Recruitment. ──
  /** The Raise hiring form's budgets, read for a person who asks. */
  requestBudgets: (projectId: number, subject: 'store' | 'role', reason?: HiringRaiseReason) =>
    api.get<HiringOk<{ budgets: HiringRaiseBudget[] }>>(`/hiring-links/${projectId}/request-options/budgets`, {
      params: { subject, ...(reason ? { reason } : {}) },
    }),
  /** A manager role's "Who is being replaced?", read for a person who asks. */
  requestExits: (projectId: number, budgetId: number, storeId?: string) =>
    api.get<HiringOk<HiringRaiseExits>>(
      `/hiring-links/${projectId}/request-options/budgets/${budgetId}/exits`,
      { params: storeId ? { storeId } : {} },
    ),
  /** A store's "Who is being replaced?", read for a person who asks. */
  requestPeople: (projectId: number, storeId: string) =>
    api.get<HiringOk<HiringReplacePeople>>(`/hiring-links/${projectId}/request-options/stores/${storeId}/people`, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /** Recruitment's own cities, read for a person who asks. */
  requestCities: (projectId: number, q: string) =>
    api.get<HiringOk<{ cities: HiringRaiseCity[] }>>(`/hiring-links/${projectId}/request-options/cities`, { params: { q } }),
  /** The store picker, read for a person who asks. */
  requestStores: (projectId: number, q?: string, offset?: number) =>
    api.get<HiringOk<HiringStoreSearch>>(`/hiring-links/${projectId}/request-options/stores`, { params: { ...(q ? { q } : {}), ...(offset ? { offset } : {}) } }),
  /** "Who is replaced?", read for a person who asks. */
  requestPeopleSearch: (projectId: number, query: { q?: string; storeId?: string; offset?: number } = {}) =>
    api.get<HiringOk<HiringPeopleSearch>>(`/hiring-links/${projectId}/request-options/people`, {
      params: { ...(query.q ? { q: query.q } : {}), ...(query.storeId ? { storeId: query.storeId } : {}), ...(query.offset ? { offset: query.offset } : {}) },
      timeout: HIRING_REVIEW_TIMEOUT_MS,
    }),
  /** The requester's review: every check of a raise, in the words of someone asking. Sends nothing, saves nothing. */
  previewRequest: (projectId: number, body: HiringRaiseInput) =>
    api.post<HiringOk<HiringRaisePreview>>(`/hiring-links/${projectId}/requests/preview`, body, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /**
   * "Send for confirmation": saves a hiring request (201), or answers the one this key
   * already made (200). Nothing is sent to Recruitment. The server runs the full review
   * first, so it waits as long as a review does.
   */
  sendRequest: (projectId: number, body: HiringRequestSend) =>
    api.post<HiringOk<HiringRequestSent>>(`/hiring-links/${projectId}/requests`, body, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /**
   * The project's hiring requests: waiting (the default), past, or all. `background` is
   * for a page that only decorates itself with the answer: a refusal must not end the session.
   */
  requests: (projectId: number, q: HiringRequestListQuery = {}, opts: { background?: boolean } = {}) =>
    api.get<HiringOk<HiringRequestList>>(`/hiring-links/${projectId}/requests`, {
      params: {
        ...(q.status ? { status: q.status } : {}),
        ...(q.subject ? { subject: q.subject } : {}),
        ...(q.storeId ? { storeId: q.storeId } : {}),
        ...(q.limit ? { limit: q.limit } : {}),
        ...(q.cursor ? { cursor: q.cursor } : {}),
      },
      ...(opts.background ? hiringProbeConfig : {}),
    }),
  /** The person who asked takes their waiting request back. */
  withdrawRequest: (projectId: number, requestId: string) =>
    api.post<HiringOk<{ request: HiringRequest }>>(`/hiring-links/${projectId}/requests/${requestId}/withdraw`, {}),

  // ── Hiring requests. What a person who may MANAGE does. ──
  /**
   * A fresh review of a waiting request, in the confirmer's own 1Office sign-in, with the
   * recruitment team THEY picked (the asker never picks one; without a team the server
   * answers TEAM_INCOMPLETE). Sends nothing.
   */
  reviewRequest: (projectId: number, requestId: string, team?: HiringRaiseTeam) =>
    api.post<HiringOk<HiringRequestReview>>(`/hiring-links/${projectId}/requests/${requestId}/review`, team ? { team } : {}, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /**
   * "Yes, raise hiring", for a hiring request: beside `raise`, THE call behind which
   * Recruitment creates an opening, in the confirmer's name. Only the confirmer's own key,
   * the hash of the review they were just shown and the team they reviewed are sent: the
   * form is the one the server kept. Another team than the reviewed one is PREVIEW_STALE. A repeat with the same key is answered from the first and creates nothing
   * more. It waits as long as a raise does.
   */
  confirmRequest: (projectId: number, requestId: string, body: { requestKey: string; previewHash: string; team?: HiringRaiseTeam }) =>
    api.post<HiringOk<HiringRequestConfirmed>>(`/hiring-links/${projectId}/requests/${requestId}/confirm`, body, { timeout: HIRING_RAISE_TIMEOUT_MS }),
  /** A person who may raise hiring declines a waiting request, with the reason the person who asked will read. */
  declineRequest: (projectId: number, requestId: string, reason: string) =>
    api.post<HiringOk<{ request: HiringRequest }>>(`/hiring-links/${projectId}/requests/${requestId}/decline`, { reason }),

  // ── Messages from Recruitment. Neither of these two sends anything to Recruitment. ──
  /**
   * A person answers a question a message from Recruitment opened on a link: still this
   * store, keep following, close (or close to raise again). Refused with 409 when the
   * opening changed again while the person was reading (`ATTENTION_CHANGED`).
   */
  answerAttention: (projectId: number, linkId: string, body: HiringAttentionAnswer) =>
    api.post<HiringOk<HiringAttentionResult>>(`/hiring-links/${projectId}/links/${linkId}/attention`, body),
  /** Whether Recruitment's messages are being accepted on this server, and when the last one arrived (super admins). */
  recruitmentMessages: (projectId: number) =>
    api.get<HiringOk<HiringMessagesStatus>>(`/hiring-links/${projectId}/recruitment-messages`, hiringProbeConfig),
};

// ── Home screen counts ───────────────────────────────────────────────────────
// The numbers the home screen (pages/home) puts beside a list it has not loaded: one
// small count per call, worked out by the server with that list's own rules, for the
// person asking.
//
// Every call sets `skipSessionExpiry`. Nobody opened these lists: the home screen asks
// on the person's behalf, and several of them refuse someone who is signed in (Start
// Exit and Onboarding need a grant the menu does not give; Probation Confirmation needs
// an identity 1Office can confirm). A refusal here means "no number for that row",
// never "signed out". None is wrapped in product-load tracking either: opening the home
// screen is not a visit to the list.
//
// Envelope: { success, data }. The raw AxiosResponse comes back, so callers read
// `res.data.data`. A count the server could not work out is null or absent, never 0.
//
// Two kinds of count live here. The first six are worked out when they are asked for.
// The last three are KEPT: the server reads the facts in the background, about every ten
// minutes for a project somebody asked about in the last half hour, and a call only
// narrows what is kept to the person asking. A kept count therefore always says when its
// facts were read (`asOf`, an ISO time), and answers a null number with a null `asOf`
// when nothing is kept yet. That call is also what asks the server to start reading.
type HomeCount<T> = { success: boolean; data: T };
type HomeCountConfig = NonNullable<Parameters<typeof api.get>[1]> & { skipSessionExpiry: true };
const homeCount = (params?: Record<string, unknown>): HomeCountConfig => ({ params, skipSessionExpiry: true });

/**
 * How long the home screen waits for the Hiring card's count (homeCountsAPI.noticeNotRaised).
 * Long enough for a read the server remembers (a minute) or a small project's; past it the
 * card has no number ("Open") and the "Needs attention" tab's count stops waiting for it.
 */
export const HOME_NOTICE_WAIT_MS = 15_000;

export const homeCountsAPI = {
  // Improvement plans that ended without improvement and wait for Extend or Terminate,
  // among the people this person may see.
  pipDecisions: (projectId: number) =>
    api.get<HomeCount<{ count: number | null }>>('/pip/decisions/count', homeCount({ projectId })),
  // People on Start Exit's "Pending review" list. Worked out when asked, then held by
  // the server for two minutes, so the answer says when (`asOf`, an ISO time).
  exitReviews: (projectId: number) =>
    api.get<HomeCount<{ pending: number | null; asOf: string | null }>>(`/start-exit/${projectId}/pending-count`, homeCount()),
  // The person's own probation confirmations, across every project: no project is sent.
  probation: () =>
    api.get<HomeCount<{ waiting: number | null; overdue: number | null }>>('/approvals/probation/count', homeCount()),
  // The Training dashboard's "Not marked" figure for a month ('YYYY-MM') and its Role
  // filter. `roleId` is the comma-separated ids; leave it out for no Role filter.
  trainingNotMarked: (p: { projectId: number; month: string; roleId?: string }) =>
    api.get<HomeCount<{ notMarked: number | null }>>('/apple-scorecard/trainings/dashboard/not-marked', homeCount(p)),
  // New joiners on the Onboarding dashboard's "Waiting for a buddy" list: plan running,
  // no buddy, no store yet.
  noBuddy: (projectId: number) =>
    api.get<HomeCount<{ noBuddy: number | null }>>(`/onboarding/${projectId}/joiners/no-buddy-count`, homeCount()),
  // Stores with something to fix on the Data Hygiene dashboard: the number on its Stores
  // switch. Null for someone who does not see the whole project: that dashboard has
  // nothing to show them.
  dataHygiene: (projectId: number) =>
    api.get<HomeCount<{ stores: number | null }>>(`/data-hygiene/${projectId}/count`, homeCount()),
  // People who left in a month ('YYYY-MM'): the "Exits" of that month on the Attrition
  // dashboard, opened with this Role filter (`roleId` as for trainingNotMarked) and no
  // other. Null where the dashboard draws no figure for this person.
  attritionExits: (p: { projectId: number; month: string; roleId?: string }) =>
    api.get<HomeCount<{ exits: number | null }>>(
      `/scorecard/${p.projectId}/dashboard/attrition/exits-count`, homeCount({ month: p.month, roleId: p.roleId })),

  // People serving notice at the stores this person looks after whose hiring is not raised
  // yet: the Hiring dashboard's Notice period tab's own read, answered as that one number
  // (no row: no name, code or store), so it is the tab's count. It reads 1Office in this
  // person's own name, so it cannot be kept in the background; it is given HOME_NOTICE_WAIT_MS,
  // not the tab's minute and a half, so a slow 1Office leaves the card without a number rather
  // than holding up the home screen. (The read goes on on the server and fills its memory.)
  noticeNotRaised: (projectId: number) =>
    api.get<HomeCount<HiringNoticeCount>>(`/hiring-links/${projectId}/notice/count`, {
      ...homeCount(),
      timeout: HOME_NOTICE_WAIT_MS,
    }),

  // ── Kept counts ──
  // What waits on My Approvals for this person: the leave requests, attendance
  // corrections and comp-off claims the page lists as Pending, and the whole days the
  // oldest has waited. Kept by the server in the background, so the answer says when its
  // facts were read (`asOf`), or carries no number yet.
  // `notCounted` comes with a time and no number when this person's people reach beyond the
  // project the server read ('outside_project'): a definite "not counted here", not asked again.
  approvalsPending: (projectId: number) =>
    api.get<HomeCount<{ pending: number | null; oldestWaitDays: number | null; asOf: string | null; notCounted?: string }>>(
      '/approvals/pending-count', homeCount({ projectId })),
  // The Leave & Attendance Requests dashboard's Pending list for a month ('YYYY-MM') and
  // its Role filter: requests raised in the month and still undecided, and the whole
  // days the oldest has waited. `roleId` as for trainingNotMarked.
  requestsPending: (p: { projectId: number; month: string; roleId?: string }) =>
    api.get<HomeCount<{ pending: number | null; oldestWaitDays: number | null; asOf: string | null }>>(
      '/approvals/dashboard/pending-count', homeCount(p)),
  // The Attendance dashboard's Absent Today list under its Role filter: shift start had
  // passed, no check-in, no leave. Always today's, so no month is sent.
  absentToday: (p: { projectId: number; roleId?: string }) =>
    api.get<HomeCount<{ absentToday: number | null; asOf: string | null }>>(
      '/attendance/dashboard/absentees/today-count', homeCount(p)),
  // The Deployment dashboard's Undeployed Stores list for the current month ('YYYY-MM'):
  // stores with nobody, or with fewer people than their category needs. That list is not
  // narrowed by role, so no roleId is sent.
  storesShort: (p: { projectId: number; month: string }) =>
    api.get<HomeCount<{ short: number | null; asOf: string | null }>>('/stores/short-count', homeCount(p)),
};

// ── Section front pages: the figures on the cards ────────────────────────────
// The line of one to three figures on each card of a section's front page (Master Data,
// Reports, Governance, Grow, Settings): "1,240 active stores · 86 short of staff". The
// server works each one out with the rules of the page the card opens — its guard, its
// viewer scope — for the person asking (apps/api/src/services/section-stats).
//
// `skipSessionExpiry` for the same reason as the home counts: nobody opened those pages,
// and a refusal here means "no number on that card", never "signed out".
//
// The front page asks twice at once: `fast` for the quick figures (and a null for every
// page that will have no number), `slow` for the ones read from 1Office or another slow
// source. A page in neither answer gets no number. A figure is never 0 for "unknown":
// unknown is null, and a 0 is drawn.

/** One figure on a card. Mirrors StatPart in apps/api/src/services/section-stats/types.ts. */
export interface StatPart {
  /** A count, or a short word where the page has no count ("Custom"). */
  value: number | string;
  /** Two or three lower-case words after the figure: "short of staff". */
  label: string;
  /** Something waits to be done: drawn in amber. Only ever set on a number above 0. */
  warn?: boolean;
}

/** A card's figures, in reading order. */
export interface CardStat {
  parts: StatPart[];
}

export type SectionStatsSpeed = 'fast' | 'slow';

/** The answer: each page address asked about → its figures, or null for no number. */
export interface SectionStatsAnswer {
  success: boolean;
  data: { stats: Record<string, CardStat | null> };
}

/** The most page addresses one call may carry (the server refuses more). */
export const SECTION_STATS_MAX_PATHS = 40;

export const sectionStatsAPI = {
  /** The figures for these page addresses of one front page; resolves to the envelope itself. */
  get: async (
    projectId: number,
    section: string,
    paths: string[],
    speed: SectionStatsSpeed,
  ): Promise<SectionStatsAnswer> => {
    const res = await api.get<SectionStatsAnswer>(
      `/section-stats/${projectId}`,
      homeCount({ section, paths: paths.join(','), speed }),
    );
    return res.data;
  },
};

// ── Hiring: a new joiner and the store, and people leaving a store ───────────
// Two things a store's hiring panel says about PEOPLE. Both are about employees: a person
// who has joined, and a person deployed at the store. Neither is about a candidate.
//
// "Who joined": when Recruitment's numbers show that someone joined on a store's opening,
// `joiners` asks who on the project's employee list that may be. It is a PROPOSAL: the
// employee list carries no opening number, so each person says how they were matched
// (`match`). A person who may manage hiring, and looks after the store, confirms one with
// `place`: the person is deployed at the store (as "Assign team" would) and the hiring is
// closed as filled, together. Nothing is sent to Recruitment by either.
//
// "People leaving": `leaving` lists the people deployed at a store who have a resignation
// on record whose exit has not been started, or who are on notice. Read-only.
//
// The three calls are behind Hiring's own switches and permissions, and no other: they
// exist wherever a project's Hiring is on. The shapes are held to the API by one shared
// file, apps/api/src/services/__fixtures__/hiring-people.contract.json (see
// components/hiring/hiringPeople.contract.test.tsx).
/** How a person on the employee list was matched to an opening. */
export type HiringJoinerMatch = 'named' | 'store_tag' | 'same_address';
/** A store a person is deployed at, as ChannelPlex names it. */
export interface HiringJoinerStore { storeId: string; storeName: string | null; storeCode: string | null }
/** One employee who may be the person who joined on an opening. */
export interface HiringJoiner {
  empCode: string;
  name: string;
  /** `YYYY-MM-DD`; null when the employee list has none. */
  dateOfJoining: string | null;
  match: HiringJoinerMatch;
  /** Deployed at this store already: nothing is left to place, only the hiring to close. */
  atThisStore: boolean;
  /** Where else in the project they are deployed today. */
  otherStores: HiringJoinerStore[];
  /** Their role keeps its other stores: a placing adds this store and moves nothing. */
  keepsOtherStores: boolean;
  /** Sent back with a placing. The server refuses a placing whose token is no longer this person's. */
  token: string;
}
export interface HiringJoiners {
  linkId: string;
  openingId: number | null;
  store: HiringJoinerStore;
  /** ChannelPlex holds a joining on this opening. */
  joined: boolean;
  /** How many Recruitment's numbers show joined; null when the joiner is only named, or nobody joined. */
  joinedCount: number | null;
  /** This person may place someone here: Hiring (manage), and the store is one of theirs. */
  canPlace: boolean;
  roster: 'read' | 'unavailable' | 'not_read';
  /** Who a message from Recruitment named as the joiner. */
  named: { empCode: string; on: string | null; onEmployeeList: boolean | null } | null;
  /** The server's sentence for what was found. */
  sentence: string;
  people: HiringJoiner[];
}
export interface HiringPlaced {
  /** `closed_only`: the person was already at the store, so nobody was deployed. */
  outcome: 'placed' | 'closed_only';
  message: string;
  placed: { empCode: string; storeId: string };
  /** The hiring link as it now stands: closed as filled. */
  link: HiringLink;
}
export type HiringLeavingState = 'exit_not_started' | 'on_notice' | 'filed_here';
export interface HiringLeavingPerson {
  empCode: string;
  /** The name ChannelPlex holds for the person at the store. */
  name: string | null;
  state: HiringLeavingState;
  /** The day ChannelPlex filed a resignation for them (Start Exit), when it did. */
  filedFromChannelPlexOn: string | null;
  resignedOn: string | null;
  lastWorkingDay: string | null;
}
export interface HiringStoreLeaving {
  storeId: string;
  /** How far 1Office was read: everything; not every budget; nothing (no 1Office account); not even the employee list. */
  oneOffice: 'read' | 'partial' | 'no_identity' | 'unavailable';
  /** How many of the store's people 1Office could not be asked about. */
  notChecked: number;
  readAt: string;
  people: HiringLeavingPerson[];
}

export const hiringPeopleAPI = {
  /** Who, on the employee list, may be the person who joined on this opening. Asked by the panel itself: a refusal must not end the session. */
  joiners: (projectId: number, linkId: string) =>
    api.get<HiringOk<HiringJoiners>>(`/hiring-links/${projectId}/links/${linkId}/joiners`, { ...hiringProbeConfig, timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /** The person is placed at the store, and the hiring is closed as filled. Only the code and its token are sent. */
  place: (projectId: number, linkId: string, body: { empCode: string; token: string }) =>
    api.post<HiringOk<HiringPlaced>>(`/hiring-links/${projectId}/links/${linkId}/place`, body, { timeout: HIRING_REVIEW_TIMEOUT_MS }),
  /** The people of a store who are leaving. Asked by the panel itself: a refusal must not end the session. */
  leaving: (projectId: number, storeId: string) =>
    api.get<HiringOk<HiringStoreLeaving>>(`/hiring-links/${projectId}/stores/${storeId}/leaving`, { ...hiringProbeConfig, timeout: HIRING_REVIEW_TIMEOUT_MS }),
};
